import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { absoluteApiFilePreviewUrl, api, canonicalStoredFileIdForApiPath } from '../lib/api'
import { useAuth } from '../auth/AuthContext'
import { Roles } from '../auth/rbac'
import { formatApiDateTimeDisplay } from '../lib/date'
import { useToast } from '../ui/ToastProvider'
import MaskableSensitiveInput from '../components/MaskableSensitiveInput'
import { PhoneDigitsSplitInput, ResidentDigitsSplitInput } from '../components/claimSplitInputs'
import ClaimAdditionalFieldInputs from '../components/claim/ClaimAdditionalFieldInputs'
import MenuGuideButton from '../components/MenuGuideButton'
import { Button } from '../components/ui/button'
import { cn } from '../lib/cn'
import { EmptyState, ErrorState } from '../components/ui/feedback-state'
import {
  birthDateYmdFromResidentDigits,
  formatPhoneDisplay,
  isValidKoreanClaimPhone,
  normalizeKoreanPhoneDigits,
  residentDigitsPrefixFromBirthDateYmd,
  todayYmdLocal,
} from '../lib/claimContactFormat'
import {
  accidentTypeFromApplication,
  resolveListedClaimFaxNumber,
} from '../lib/claimFaxRouting'
import { CLAIM_DOCUMENT_TYPE_OPTIONS } from '../lib/claimDocumentTypes'
import {
  CLAIM_MAX_DOCUMENT_PAGE_COUNT,
  claimDocumentPageCount,
  claimDocumentsPageCount,
  prepareClaimDocumentFiles,
  takeClaimDocumentsWithinPageLimit,
} from '../lib/claimDocumentPages'
import { CLAIM_DOCUMENT_SECTIONS_ALL } from '../data/claimRequiredDocumentsGuide'
import {
  CLAIM_ADDITIONAL_FIELD_COMMON_MODE,
  buildAdditionalExtraFieldsForInsurer,
  buildAdditionalFieldGroupsForInsurers,
  additionalFieldGroupModeKey,
  normalizeAdditionalFieldDefinitions,
  validateRequiredAdditionalFields,
} from '../lib/claimAdditionalFields'
import {
  customerRefIdFromManagedCustomer,
  fetchManagedCustomersForPicker,
  managedCustomerSearchErrorMessage,
  normalizeManagedCustomerItems,
} from '../lib/customerManagementApi'

/** 보험금 청구 STEP1: 보험사 다중 선택 */
const CLAIM_INSURER_MULTI_SELECT_ENABLED = true

const FLOW_CATEGORIES = [
  { key: 'NONLIFE', label: '손해보험', enabled: true },
  { key: 'LIFE', label: '생명보험', enabled: true },
  { key: 'PET', label: '펫보험', enabled: true },
  { key: 'LIABILITY', label: '배상책임', enabled: true },
]

const GROUPABLE_FLOW_CATEGORY_KEYS = ['NONLIFE', 'LIFE', 'PET', 'LIABILITY']
const CLAIM_FAX_DELAY_MINUTES = 1
const CLAIM_MAX_DOCUMENT_COUNT = CLAIM_MAX_DOCUMENT_PAGE_COUNT

const CLAIM_APPLICATION_STATUS_LABELS = {
  DRAFT: '임시저장',
  SUBMITTED: '접수',
  IN_REVIEW: '검토 중',
  APPROVED: '승인',
  REJECTED: '반려',
  COMPLETED: '완료',
  CANCELLED: '취소',
}

const FAX_RESERVATION_UI_LABELS = {
  SCHEDULED: '발송 대기 중',
  SENDING: '발송 처리 중',
  SENT: '발송 접수',
  FAILED: '발송 실패',
  CANCELLED: '발송 취소',
}

const FAX_TRANSMISSION_STATUS_LABELS = {
  PENDING: '팩스 전송 대기',
  CONSUMED: '전송 완료 · 차감 확정',
  RELEASED: '전송 종료 · 무차감',
  SETTLEMENT_FAILED: '전송·이용 처리 오류',
  NONE: '없음',
}

const CLAIM_CONSENT_DOCS = [
  { docCode: 'CLAIM_PRIVACY', fallbackLabel: '보험금 청구 지원을 위한 개인정보 수집·이용 동의' },
  { docCode: 'CLAIM_SENSITIVE_UNIQUE_ID', fallbackLabel: '민감정보 및 고유식별정보 처리 동의' },
  { docCode: 'CLAIM_THIRD_PARTY_PROVISION', fallbackLabel: '보험금 청구 지원 및 제3자 제공 동의' },
]

function buildClaimConsentBundleVersion(docs) {
  return (docs || [])
    .map((doc) => `${doc.docCode}:${doc.versionLabel || ''}`)
    .join('|')
}

const GROUP_CLAIM_AVAILABLE_INSURERS = new Set([
  'KB손해보험',
  'DB손해보험',
  'NH농협손해보험',
  '롯데손해보험',
  '메리츠화재',
  '삼성화재',
  '현대해상화재',
  '흥국화재',
])

/** API가 백엔드 호스트 기준 URL을 줄 때, 복사·열기용으로 현재 웹 오리진으로 맞춤 */
function rewritePublicClaimLinkToCurrentOrigin(url) {
  if (!url || typeof url !== 'string') return url
  try {
    const parsed = new URL(url, typeof window !== 'undefined' ? window.location.origin : 'http://localhost')
    const p = parsed.pathname || ''
    if (
      /\/share\/claim-intake\//.test(p) ||
      /\/claims\/intake\//.test(p) ||
      /\/share\/claim-signature\//.test(p) ||
      /\/sign\/claim-signature\//.test(p)
    ) {
      if (typeof window === 'undefined') return url
      return `${window.location.origin}${parsed.pathname}${parsed.search}${parsed.hash}`
    }
  } catch {
    /* ignore */
  }
  return url
}

/** STEP 2 공통 입력만 브라우저 로컬에 보관 (보험사별 청구서 API 임시저장과 별개) */
const CLAIMS_FLOW_LOCAL_DRAFT_KEY = 'sgez_claims_flow_local_draft_v1'
/** 「공통 입력 임시저장」으로 저장된 브라우저 초안이 있음을 표시(자동 복원하지 않음 — 사용자가 불러오기 시에만 적용) */
const CLAIMS_FLOW_DRAFT_EXPLICIT_KEY = 'sgez_claims_flow_draft_explicit_v1'
const CLAIMS_ATTACHMENT_PLACEHOLDER = '자료첨부'

const CLAIM_DOCUMENT_TYPE_LABEL_BY_VALUE = Object.fromEntries(
  CLAIM_DOCUMENT_TYPE_OPTIONS.map((option) => [option.value, option.label]),
)

const CLAIM_DOCUMENT_TYPES_BY_CLAIM_SECTION = {
  일반: ['ID_CARD', 'BANKBOOK', 'MEDICAL_RECEIPT', 'MEDICAL_DETAIL_STATEMENT'],
  실비보험: ['MEDICAL_RECEIPT', 'MEDICAL_DETAIL_STATEMENT', 'PRESCRIPTION', 'DIAGNOSIS_CERTIFICATE'],
  진단금: ['DIAGNOSIS_CERTIFICATE'],
  '골절, 수술, 깁스치료, 치아보험': ['DIAGNOSIS_CERTIFICATE', 'SURGERY_CONFIRMATION', 'MEDICAL_RECEIPT'],
  '상해사고 입증서류': ['DIAGNOSIS_CERTIFICATE', 'ETC'],
  태아보험: ['DIAGNOSIS_CERTIFICATE', 'MEDICAL_RECEIPT', 'MEDICAL_DETAIL_STATEMENT'],
  장해: ['DIAGNOSIS_CERTIFICATE', 'ETC'],
  사망: ['DIAGNOSIS_CERTIFICATE', 'ETC'],
  운전자보험: ['DIAGNOSIS_CERTIFICATE', 'ETC'],
  자동차보험: ['DIAGNOSIS_CERTIFICATE', 'MEDICAL_RECEIPT', 'ETC'],
  재물보험: ['ETC'],
  배상책임보험: ['MEDICAL_RECEIPT', 'DIAGNOSIS_CERTIFICATE', 'ETC'],
  '구직급여·실업급여 지원': ['ETC'],
}

function claimDocumentTypeLabel(value) {
  return CLAIM_DOCUMENT_TYPE_LABEL_BY_VALUE[value] || value || '미분류'
}

function filePreviewKind(doc) {
  const name = String(doc?.label || doc?.fileId || '').toLowerCase()
  if (/\.(png|jpe?g|webp|gif|bmp|svg)$/.test(name)) return 'image'
  if (/\.pdf$/.test(name)) return 'pdf'
  return 'file'
}

function normalizeClaimDocumentsForFlow(documents) {
  const rows = Array.isArray(documents) ? documents : []
  return takeClaimDocumentsWithinPageLimit(
    rows.map((doc, idx) => ({
      fileId: doc?.fileId,
      documentType: doc?.documentType || 'ETC',
      label: doc?.label || doc?.originalName || `첨부 ${idx + 1}`,
      sortOrder: doc?.sortOrder ?? idx,
      pageCount: claimDocumentPageCount(doc),
    })),
    CLAIM_MAX_DOCUMENT_COUNT,
  )
}

function readClaimsFlowLocalDraft() {
  try {
    const raw = localStorage.getItem(CLAIMS_FLOW_LOCAL_DRAFT_KEY)
    if (!raw) return null
    const o = JSON.parse(raw)
    return o && (o.version === 1 || o.version === 2) ? o : null
  } catch {
    return null
  }
}

/** 현재 선택된 STEP 하나만 표시할 때 사용 — 접기/펼치기 없음 */
function ClaimStepSection({ step, title, children, headerRight }) {
  return (
    <div className="rounded border border-slate-200 bg-white">
      <div className="flex flex-wrap items-start gap-3 border-b border-slate-200 bg-white px-4 py-2.5 sm:py-3">
        <span className="mt-1 h-8 w-1 shrink-0 rounded-full bg-brand-navy" aria-hidden />
        <div className="min-w-0 flex-1">
          <div className="text-xs font-semibold uppercase tracking-wide text-brand-navy">STEP {step}</div>
          <div className="text-lg font-bold text-slate-950">{title}</div>
        </div>
        {headerRight ? (
          <div className="ml-auto flex shrink-0 items-center gap-2">{headerRight}</div>
        ) : null}
      </div>
      <div className="space-y-4 p-4">{children}</div>
    </div>
  )
}

function getInsurerLogoSrc(org) {
  if (!org) return null
  if (org.claimBrandLogoFileId) return absoluteApiFilePreviewUrl(org.claimBrandLogoFileId)
  if (org.logoUrl) return org.logoUrl
  if (org.logoImageUrl) return org.logoImageUrl
  if (org.logoFileId) return absoluteApiFilePreviewUrl(org.logoFileId)
  if (org.fileId) return absoluteApiFilePreviewUrl(org.fileId)
  return null
}

function getInsurerInitial(orgName) {
  const name = String(orgName || '').trim()
  if (!name) return '?'
  const latin = name.match(/^[A-Za-z]+/)?.[0]
  if (latin) {
    const upper = latin.toUpperCase()
    return upper.length > 3 ? upper.slice(0, 3) : upper
  }
  return name.slice(0, 1)
}

function ClaimInsurerLogoMark({ org, variant = 'grid' }) {
  const logoSrc = getInsurerLogoSrc(org)
  if (variant === 'chip') {
    if (logoSrc) {
      return (
        <img
          src={logoSrc}
          alt={`${org?.name || ''} 로고`}
          className="h-6 w-auto max-w-[3.5rem] shrink-0 object-contain"
        />
      )
    }
    return (
      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded border border-slate-200 bg-white text-[10px] font-semibold text-slate-600">
        {getInsurerInitial(org?.name)}
      </span>
    )
  }

  const gridLogoShell = 'flex h-12 w-[4.5rem] shrink-0 items-center justify-start'
  if (logoSrc) {
    return (
      <span className={gridLogoShell}>
        <img
          src={logoSrc}
          alt={`${org?.name || ''} 로고`}
          className="max-h-12 max-w-full object-contain object-left"
        />
      </span>
    )
  }
  return (
    <span className={gridLogoShell}>
      <span className="flex h-12 w-12 items-center justify-center rounded-md border border-slate-200 bg-white text-sm font-semibold text-slate-600">
        {getInsurerInitial(org?.name)}
      </span>
    </span>
  )
}

function getVisibleStep2Sections(sharedFields, showContractHolderInputs, showBeneficiaryInputs) {
  const sections = ['insured']
  if (showContractHolderInputs && !isContractHolderSameAsInsured(sharedFields.contractHolderSameAsInsured)) {
    sections.push('contractHolder')
  }
  if (showBeneficiaryInputs) {
    sections.push('beneficiary')
  }
  sections.push('payout', 'details')
  return sections
}

function FieldSection({ title, open, onToggle, children, active, completed, onNext, nextLabel = '다음' }) {
  return (
    <div
      className={cn(
        'overflow-hidden rounded bg-white transition-shadow',
        active
          ? 'border-2 border-brand-navy shadow-sm ring-2 ring-brand-navy/10'
          : 'border border-slate-200',
        completed && !active && 'bg-slate-50/40',
      )}
    >
      <button
        type="button"
        className={cn(
          'flex w-full items-center justify-between border-l-4 px-4 py-3 text-left',
          active
            ? 'border-brand-navy bg-sky-50/80 hover:bg-sky-50'
            : 'border-brand-navy/70 bg-slate-50 hover:bg-sky-50/50',
        )}
        onClick={onToggle}
      >
        <span className="flex min-w-0 flex-wrap items-center gap-2">
          <span className="text-base font-bold tracking-tight text-slate-950">{title}</span>
          {active && open ? (
            <span className="rounded-full bg-sky-100 px-2 py-0.5 text-xs font-medium text-brand-navy">입력 중</span>
          ) : null}
          {completed && !open ? (
            <span className="rounded-full border border-slate-200 bg-white px-2 py-0.5 text-xs font-medium text-slate-500">입력됨</span>
          ) : null}
        </span>
        <span className="shrink-0 rounded-full border border-slate-200 bg-white px-2 py-0.5 text-xs font-medium text-slate-500">{open ? '접기' : '펼치기'}</span>
      </button>
      {open && (
        <div className="border-t bg-white p-4 [&_input:disabled]:border-slate-200 [&_input:disabled]:bg-slate-100 [&_input:disabled]:text-slate-500 [&_input:disabled]:placeholder:text-slate-400 [&_input[readonly]]:border-slate-200 [&_input[readonly]]:bg-slate-50 [&_input[readonly]]:text-slate-500">
          {children}
          {onNext ? (
            <div className="mt-4 flex justify-end border-t border-slate-100 pt-4">
              <Button type="button" variant="sgezOutline" size="sm" onClick={onNext}>
                {nextLabel}
              </Button>
            </div>
          ) : null}
        </div>
      )}
    </div>
  )
}

function ClaimRequiredDocumentsPanel({ section }) {
  if (!section?.panels?.length) return null
  return (
    <div className="space-y-3">
      <div>
        <div className="text-sm font-semibold text-slate-900">{section.sectionTitle}</div>
        <div className="mt-1 text-xs text-slate-500">
          {section.sectionTitle === '일반'
            ? '대부분의 청구에서 함께 확인되는 공통 서류입니다. 보험사·담보별로 추가 서류가 있을 수 있습니다.'
            : '해당 청구 유형에서 통상 요구되는 서류입니다. 보험사·약관에 따라 달라질 수 있습니다.'}
        </div>
      </div>
      <div className="space-y-2">
        {section.panels.map((panel) => (
          <details
            key={`${section.sectionTitle}-${panel.title}`}
            open={panel.defaultOpen}
            className="group rounded border border-slate-200 bg-white"
          >
            <summary className="cursor-pointer list-none px-3 py-2.5 text-sm font-bold text-slate-800 marker:hidden [&::-webkit-details-marker]:hidden">
              <span className="flex items-center justify-between gap-2">
                <span>{panel.title}</span>
                <span className="text-xs font-normal text-slate-400 group-open:hidden">펼치기</span>
                <span className="hidden text-xs font-normal text-slate-400 group-open:inline">접기</span>
              </span>
            </summary>
            <div className="space-y-2 border-t border-slate-100 px-3 py-2.5 text-sm leading-relaxed text-slate-700">
              {panel.blocks.map((block, bi) => (
                <div key={bi}>
                  {block.strong && <div className="font-semibold text-slate-900">{block.strong}</div>}
                  {block.lines?.filter(Boolean).map((line) => (
                    <div key={line} className="mt-1 text-xs leading-5 text-slate-600">{line}</div>
                  ))}
                </div>
              ))}
            </div>
          </details>
        ))}
      </div>
    </div>
  )
}

function normalizeDigits(value) {
  return String(value || '').replace(/\D/g, '')
}

function splitResidentNumber(value) {
  const digits = normalizeDigits(value)
  return [digits.slice(0, 6), digits.slice(6, 13)]
}

function splitPhoneNumber(value) {
  const digits = normalizeDigits(value)
  if (digits.length === 11) return [digits.slice(0, 3), digits.slice(3, 7), digits.slice(7, 11)]
  if (digits.length === 10) return [digits.slice(0, 3), digits.slice(3, 6), digits.slice(6, 10)]
  return [digits.slice(0, 3), digits.slice(3, 7), digits.slice(7, 11)]
}

/** 우편번호·도로명·상세 → PDF·extraFields용 한 줄 주소 */
function buildCustomerAddressLine(sf) {
  if (!sf || typeof sf !== 'object') return ''
  const z = String(sf.customerAddressZonecode || '').trim()
  const r = String(sf.customerAddressRoad || '').trim()
  const d = String(sf.customerAddressDetail || '').trim()
  if (z || r || d) {
    const head = z && r ? `(${z}) ${r}`.trim() : z ? `(${z})` : r
    return d ? `${head} ${d}`.trim() : head
  }
  return String(sf.customerAddress || '').trim()
}

function openDaumPostcode(onComplete) {
  if (!window?.daum?.Postcode) {
    throw new Error('daum_postcode_not_loaded')
  }
  new window.daum.Postcode({
    oncomplete(data) {
      onComplete?.(data)
    },
  }).open()
}

/** 계약자=피보험자 동일 여부 — UI·병합용 (boolean / Y·N 혼용) */
function isContractHolderSameAsInsured(v) {
  if (v === false || v === 'N') return false
  if (v === true || v === 'Y') return true
  return true
}

/** 수익자=피보험자 동일 (기본: 동일) — boolean·Y/N */
function isBeneficiarySameAsCustomerFlag(v) {
  if (v === false || v === 'N') return false
  if (v === true || v === 'Y') return true
  return true
}

/** 수익자=계약자 동일 — boolean·Y/N (기본: 비동일) */
function isBeneficiarySameAsContractHolderFlag(v) {
  return v === true || v === 'Y'
}

/** 로컬/서버 임시저장 병합 후 accountPayoutType·계약자 동일 여부 기본값 보정 */
function normalizeClaimsSharedFieldsAfterMerge(merged) {
  if (!merged || typeof merged !== 'object') return merged
  const out = { ...merged }
  if (!out.accountPayoutType) {
    if (out.bankAutoTransfer === 'Y') out.accountPayoutType = 'AUTO_TRANSFER'
    else out.accountPayoutType = 'GENERAL'
  }
  out.contractHolderSameAsInsured = isContractHolderSameAsInsured(out.contractHolderSameAsInsured)
  out.beneficiarySameAsCustomer = isBeneficiarySameAsCustomerFlag(out.beneficiarySameAsCustomer)
  out.beneficiarySameAsContractHolder = isBeneficiarySameAsContractHolderFlag(out.beneficiarySameAsContractHolder)
  if (!out.benefitNotificationRecipient) out.benefitNotificationRecipient = 'PLANNER'
  out.claimScope = '전체보험금'
  out.partialClaimReason = ''
  const road = String(out.customerAddressRoad || '').trim()
  const zone = String(out.customerAddressZonecode || '').trim()
  const det = String(out.customerAddressDetail || '').trim()
  const legacy = String(out.customerAddress || '').trim()
  if (!road && !zone && !det && legacy) {
    out.customerAddressRoad = legacy
    out.customerAddressDetail = ''
    out.customerAddressZonecode = ''
  }
  const aip = String(out.autoInsuranceProcessed ?? '').trim()
  out.autoInsuranceProcessed = aip === '예' || aip === '아니오' ? aip : '아니오'
  if (out.medicalBenefitRecipient !== 'Y' && out.medicalBenefitRecipient !== 'N') {
    out.medicalBenefitRecipient = 'N'
  }
  return out
}

/** 서버에 저장된 청구서 extraFields·memo → 공통 입력(sharedFields) 형태로 복원 */
function sharedFieldsPartialFromStoredClaimExtra(ex, memoCombined) {
  const base = createInitialSharedFields()
  if (!ex || typeof ex !== 'object') ex = {}
  const out = { ...base }
  out.accidentDetails = ''
  out.accidentLocation = ''
  out.accidentInfoAttached = false

  const assign = (k, v) => {
    if (v === undefined || v === null) return
    out[k] = v
  }

  assign('customerResidentNumber', ex.customerResidentNumber != null ? String(ex.customerResidentNumber) : undefined)
  assign('customerEmail', ex.customerEmail != null ? String(ex.customerEmail) : undefined)
  assign('customerWorkplace', ex.customerWorkplace != null ? String(ex.customerWorkplace) : undefined)
  assign('customerOccupation', ex.customerOccupation != null ? String(ex.customerOccupation) : undefined)

  if (ex.contractHolderSameAsInsured === 'Y' || ex.contractHolderSameAsInsured === 'N') {
    out.contractHolderSameAsInsured = ex.contractHolderSameAsInsured === 'Y'
  }
  assign('contractHolderName', ex.contractHolderName != null ? String(ex.contractHolderName) : undefined)
  assign('contractHolderResidentNumber', ex.contractHolderResidentNumber != null ? String(ex.contractHolderResidentNumber) : undefined)
  assign('contractHolderPhone', ex.contractHolderPhone != null ? String(ex.contractHolderPhone) : undefined)

  if (ex.medicalBenefitRecipient === 'Y' || ex.medicalBenefitRecipient === 'N') {
    out.medicalBenefitRecipient = ex.medicalBenefitRecipient
  }

  if (ex.beneficiarySameAsCustomer === 'Y' || ex.beneficiarySameAsCustomer === 'N') {
    out.beneficiarySameAsCustomer = ex.beneficiarySameAsCustomer === 'Y'
  }
  if (ex.beneficiarySameAsContractHolder === 'Y' || ex.beneficiarySameAsContractHolder === 'N') {
    out.beneficiarySameAsContractHolder = ex.beneficiarySameAsContractHolder === 'Y'
  }

  assign('beneficiaryName', ex.beneficiaryName != null ? String(ex.beneficiaryName) : undefined)
  assign('beneficiaryRelationToInsured', ex.beneficiaryRelationToInsured != null ? String(ex.beneficiaryRelationToInsured) : undefined)
  assign('beneficiaryResidentNumber', ex.beneficiaryResidentNumber != null ? String(ex.beneficiaryResidentNumber) : undefined)
  assign('beneficiaryPhone', ex.beneficiaryPhone != null ? String(ex.beneficiaryPhone) : undefined)
  assign('beneficiaryEmail', ex.beneficiaryEmail != null ? String(ex.beneficiaryEmail) : undefined)
  assign('beneficiaryAddress', ex.beneficiaryAddress != null ? String(ex.beneficiaryAddress) : undefined)
  assign('beneficiaryWorkplace', ex.beneficiaryWorkplace != null ? String(ex.beneficiaryWorkplace) : undefined)
  assign('beneficiaryOccupation', ex.beneficiaryOccupation != null ? String(ex.beneficiaryOccupation) : undefined)

  if (ex.accountPayoutType === 'PREPAID' || ex.accountPayoutType === 'GENERAL' || ex.accountPayoutType === 'AUTO_TRANSFER') {
    out.accountPayoutType = ex.accountPayoutType
  } else if (ex.bankAutoTransfer === 'Y') {
    out.accountPayoutType = 'AUTO_TRANSFER'
    out.bankAutoTransfer = 'Y'
  }

  assign('bankName', ex.bankName != null ? String(ex.bankName) : undefined)
  assign('accountNumber', ex.accountNumber != null ? String(ex.accountNumber) : undefined)
  assign('accountHolder', ex.accountHolder != null ? String(ex.accountHolder) : undefined)

  if (ex.benefitNotificationRecipient === 'PLANNER' || ex.benefitNotificationRecipient === 'CLAIMANT') {
    out.benefitNotificationRecipient = ex.benefitNotificationRecipient
  }

  const atRaw = String(ex.accidentType || '').trim()
  if (atRaw) {
    if (atRaw.startsWith('기타:')) {
      out.accidentType = '기타'
      out.accidentTypeEtc = atRaw.slice(3).trim()
    } else {
      out.accidentType = atRaw
    }
  }

  const ctRaw = String(ex.claimType || '').trim()
  if (ctRaw) {
    const parts = ctRaw.split(',').map((s) => s.trim()).filter(Boolean)
    const claimTypes = []
    let claimTypeEtc = ''
    for (const p of parts) {
      if (p.startsWith('기타:')) claimTypeEtc = p.slice(3).trim()
      else claimTypes.push(p)
    }
    out.claimTypes = claimTypes
    out.claimTypeEtc = claimTypeEtc
  }

  let claimContent = String(ex.claimContent || '').trim()
  let accidentDetails = String(ex.accidentDetails || '').trim()
  if (!claimContent && !accidentDetails && memoCombined) {
    const m = String(memoCombined).trim()
    const parts = m.split(/\n\n+/)
    claimContent = parts[0] || ''
    accidentDetails = parts.slice(1).join('\n\n').trim()
  }
  assign('claimContent', claimContent)
  assign('accidentDetails', accidentDetails)

  assign('accidentLocation', ex.accidentLocation != null ? String(ex.accidentLocation) : undefined)
  out.accidentInfoAttached =
    String(out.accidentLocation || '').trim() === CLAIMS_ATTACHMENT_PLACEHOLDER &&
    String(out.accidentDetails || '').trim() === CLAIMS_ATTACHMENT_PLACEHOLDER

  const aip = String(ex.autoInsuranceProcessed || '').trim()
  if (aip === '예' || aip === '아니오') {
    out.autoInsuranceProcessed = aip
  }
  assign('autoInsuranceCompany', ex.autoInsuranceCompany != null ? String(ex.autoInsuranceCompany) : undefined)
  assign('autoInsuranceContact', ex.autoInsuranceContact != null ? String(ex.autoInsuranceContact) : undefined)
  assign('vehicleNumber', ex.vehicleNumber != null ? String(ex.vehicleNumber) : undefined)

  if (ex.accidentReceiptType === 'NEW' || ex.accidentReceiptType === 'ADDITIONAL') {
    out.accidentReceiptType = ex.accidentReceiptType
  }

  const addrLine = String(ex.customerAddress || '').trim()
  if (addrLine) {
    out.customerAddress = addrLine
  }

  return out
}

/** 보험금 청구 플로우 — 공통 입력 필드 기본값(로컬 초안 초기화 시 동일하게 사용) */
function createInitialSharedFields() {
  return {
    customerResidentNumber: '',
    customerEmail: '',
    customerAddress: '',
    customerAddressZonecode: '',
    customerAddressRoad: '',
    customerAddressDetail: '',
    customerWorkplace: '',
    customerOccupation: '',
    medicalBenefitRecipient: 'N',
    beneficiarySameAsCustomer: true,
    beneficiaryName: '',
    beneficiaryRelationToInsured: '',
    beneficiaryResidentNumber: '',
    beneficiaryPhone: '',
    beneficiaryEmail: '',
    beneficiaryAddress: '',
    beneficiaryWorkplace: '',
    beneficiaryOccupation: '',
    accountPayoutType: 'AUTO_TRANSFER',
    bankName: '',
    accountNumber: '',
    accountHolder: '',
    accidentType: '',
    accidentTypeEtc: '',
    claimTypes: [],
    accidentReceiptType: 'NEW',
    claimTypeEtc: '',
    claimContent: '',
    accidentDetails: CLAIMS_ATTACHMENT_PLACEHOLDER,
    accidentLocation: CLAIMS_ATTACHMENT_PLACEHOLDER,
    accidentInfoAttached: true,
    autoInsuranceProcessed: '아니오',
    autoInsuranceCompany: '',
    autoInsuranceContact: '',
    vehicleNumber: '',
    claimScope: '전체보험금',
    partialClaimReason: '',
    contractHolderSameAsInsured: true,
    contractHolderName: '',
    contractHolderResidentNumber: '',
    contractHolderPhone: '',
    contractHolderSignatureImageBase64: '',
    benefitNotificationRecipient: 'PLANNER',
    beneficiarySameAsContractHolder: false,
  }
}

function createInitialExpandedSharedSections() {
  return {
    insured: true,
    contractHolder: false,
    benefitGuide: true,
    beneficiary: false,
    payout: false,
    details: false,
  }
}

function buildClaimFieldsFromCustomerSummary(data) {
  if (!data || typeof data !== 'object') {
    return { name: '', phone: '', birthDate: '', sharedPatch: {} }
  }
  const name = data.customerName != null ? String(data.customerName).trim() : ''
  const phone = data.customerPhone != null ? normalizeKoreanPhoneDigits(data.customerPhone) : ''
  const birthDate = data.birthDate != null ? String(data.birthDate).trim().slice(0, 10) : ''
  const sharedPatch = {}
  const email = data.customerEmail != null ? String(data.customerEmail).trim() : ''
  const workplace = data.affiliation != null ? String(data.affiliation).trim() : ''
  const occupation = data.occupation != null ? String(data.occupation).trim() : ''
  const homeAddr = data.homeAddress != null ? String(data.homeAddress).trim() : ''
  const companyAddr = data.companyAddress != null ? String(data.companyAddress).trim() : ''
  const addressLine = homeAddr || companyAddr
  if (birthDate) {
    const rrnPrefix = residentDigitsPrefixFromBirthDateYmd(birthDate)
    if (rrnPrefix) sharedPatch.customerResidentNumber = rrnPrefix
  }
  if (email) sharedPatch.customerEmail = email
  if (workplace) sharedPatch.customerWorkplace = workplace
  if (occupation) sharedPatch.customerOccupation = occupation
  if (addressLine) {
    sharedPatch.customerAddressRoad = addressLine
    sharedPatch.customerAddressDetail = ''
    sharedPatch.customerAddressZonecode = ''
    sharedPatch.customerAddress = addressLine
  }
  return { name, phone, birthDate, sharedPatch }
}

function claimApplicationExtraFields(application) {
  if (!application) return {}
  if (application.extraFields && typeof application.extraFields === 'object') return application.extraFields
  try {
    const raw = application.extraFieldsJson
    if (raw && typeof raw === 'string') return JSON.parse(raw)
  } catch {
    /* ignore */
  }
  return {}
}

/** extraFields 기준: 계약자가 피보험자와 다를 때만 계약자 서명까지 필요 */
function needsContractHolderSignatureFromApplication(application) {
  return claimApplicationExtraFields(application).contractHolderSameAsInsured === 'N'
}

/** extraFields 기준: 수익자가 피보험자/계약자와 모두 다를 때만 별도 수익자 서명이 필요 */
function needsBeneficiarySignatureFromApplication(application) {
  const extra = claimApplicationExtraFields(application)
  return extra.beneficiarySameAsCustomer === 'N' && extra.beneficiarySameAsContractHolder !== 'Y'
}

function beneficiarySignatureSameLabelFromApplication(application) {
  const extra = claimApplicationExtraFields(application)
  if (extra.beneficiarySameAsCustomer === 'Y') return '피보험자와 동일'
  if (extra.beneficiarySameAsContractHolder === 'Y') return '계약자와 동일'
  return ''
}

function signatureSlotFromSignerRole(signerRole) {
  if (signerRole === 'CONTRACT_HOLDER') return 'contractHolder'
  if (signerRole === 'BENEFICIARY') return 'beneficiary'
  return 'insured'
}

function claimSignatureCompleted(status) {
  return String(status || '').toUpperCase() === 'COMPLETED'
}

export default function ClaimsPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { hasAnyRole, user } = useAuth()
  const { show } = useToast()
  const isOperator = hasAnyRole ? hasAnyRole([Roles.OPERATOR]) : false
  
  /** 상단 탭: 보험금 청구 / 청구 이력 / 임시저장 이력 (이 페이지는 항상 보험금 청구 탭 활성) */
  const activeMenu = useMemo(() => {
    if (location.pathname === '/support/claims/applications') {
      const sp = new URLSearchParams(location.search)
      return sp.get('status') === 'DRAFT' ? 'DRAFT_HISTORY' : 'HISTORY'
    }
    return 'CLAIM'
  }, [location.pathname, location.search])

  /** 임시저장 이력 → 메인 보험금 청구 플로우로 이어쓰기 */
  const resumeApplicationId = useMemo(() => {
    if (location.pathname !== '/support/claims') return null
    const sp = new URLSearchParams(location.search)
    const raw = sp.get('resumeApplicationId')
    if (!raw) return null
    const n = Number(raw)
    return Number.isFinite(n) && n > 0 ? n : null
  }, [location.pathname, location.search])
  
  const [flowCategory, setFlowCategory] = useState('NONLIFE')
  const [flowCategoryKeys, setFlowCategoryKeys] = useState(['NONLIFE'])
  const [openStep, setOpenStep] = useState('1')
  const [selectedInsurerIds, setSelectedInsurerIds] = useState([])
  const [claimCustomerName, setClaimCustomerName] = useState('')
  const [claimCustomerRefId, setClaimCustomerRefId] = useState(null)
  const [claimCustomerBirthDate, setClaimCustomerBirthDate] = useState('')
  const [claimCustomerPhone, setClaimCustomerPhone] = useState('')
  const [crmPickerOpen, setCrmPickerOpen] = useState(false)
  const [crmPickerLoading, setCrmPickerLoading] = useState(false)
  const [crmPickerList, setCrmPickerList] = useState([])
  const [crmPickerErr, setCrmPickerErr] = useState('')
  const [crmPickerQuery, setCrmPickerQuery] = useState('')
  const crmPrefillFromUrlRef = useRef(false)
  const [claimDate, setClaimDate] = useState(() => todayYmdLocal())
  const [accidentDate, setAccidentDate] = useState('')
  /** 청구 일자 필드 비표시 — 사고 일보다 이른 경우 자동 보정 */
  useEffect(() => {
    if (!accidentDate || !claimDate) return
    if (claimDate < accidentDate) setClaimDate(accidentDate)
  }, [accidentDate, claimDate])
  const [sharedFields, setSharedFields] = useState(() => createInitialSharedFields())
  const [sharedDocuments, setSharedDocuments] = useState([])
  const [sharedDocumentPreviewUrls, setSharedDocumentPreviewUrls] = useState({})
  const [sharedDocumentsUploading, setSharedDocumentsUploading] = useState(false)
  const [sharedDocumentsError, setSharedDocumentsError] = useState('')
  const [sharedDocumentPreviewIndex, setSharedDocumentPreviewIndex] = useState(null)
  const sharedDocumentPageCount = useMemo(() => claimDocumentsPageCount(sharedDocuments), [sharedDocuments])
  const [expandedSharedSections, setExpandedSharedSections] = useState(() => createInitialExpandedSharedSections())
  const [activeStep2Section, setActiveStep2Section] = useState('insured')
  const [step2MaxReachedIndex, setStep2MaxReachedIndex] = useState(0)
  const [claimAdditionalFieldDefsByInsurerId, setClaimAdditionalFieldDefsByInsurerId] = useState({})
  const [claimAdditionalFieldValues, setClaimAdditionalFieldValues] = useState({})
  const [claimAdditionalFieldModeByGroupKey, setClaimAdditionalFieldModeByGroupKey] = useState({})
  const [claimAdditionalFieldsLoading, setClaimAdditionalFieldsLoading] = useState(false)
  const [claimAdditionalFieldsError, setClaimAdditionalFieldsError] = useState('')
  const [activeModal, setActiveModal] = useState(null) // GUIDE | FORMS | SIGN | INTAKE
  const [claimDocumentSectionTitle, setClaimDocumentSectionTitle] = useState(
    () => CLAIM_DOCUMENT_SECTIONS_ALL[0]?.sectionTitle || ''
  )
  /** 보험사별 청구서 자동 생성·동기화 중 오류 */
  const [insurerAppSyncError, setInsurerAppSyncError] = useState('')
  const [insurerAppSyncWorking, setInsurerAppSyncWorking] = useState(false)
  const [createdClaimApplications, setCreatedClaimApplications] = useState({})
  const [faxScheduleModal, setFaxScheduleModal] = useState({ open: false, application: null, insurer: null })
  const [faxScheduleForm, setFaxScheduleForm] = useState({ receiveNum: '', receiveName: '', title: '' })
  const [faxScheduling, setFaxScheduling] = useState(false)
  /** @type {Record<string, { claimFaxNumber: string, claimFaxRoutingJson?: string|null, virtualFaxRequired: boolean }>} */
  const [insurerFaxByOrgId, setInsurerFaxByOrgId] = useState({})
  /** 보험사별 사용자 청구 가능 여부 — `/claims/form-templates/statuses`의 hasTemplate && claimEnabled */
  const [claimTemplateByOrgId, setClaimTemplateByOrgId] = useState({})
  const [claimTemplateStatusByOrgId, setClaimTemplateStatusByOrgId] = useState({})
  const [claimTemplateStatusLoading, setClaimTemplateStatusLoading] = useState(false)

  // guides
  const [categoryRootOrgId, setCategoryRootOrgId] = useState(null)
  const [guidesLoading, setGuidesLoading] = useState(false)
  const [guidesError, setGuidesError] = useState(null)
  const [guides, setGuides] = useState({ common: [], specific: [] })
  const [selectedGuide, setSelectedGuide] = useState(null)
  const [guideDetailLoading, setGuideDetailLoading] = useState(false)

  // signature request
  const [sigCustomerName, setSigCustomerName] = useState('')
  const [sigCustomerPhone, setSigCustomerPhone] = useState('')
  const [sigChannel, setSigChannel] = useState('KAKAO') // KAKAO | SMS
  const [sigSending, setSigSending] = useState(false)
  const [sigResult, setSigResult] = useState(null)
  const [sigError, setSigError] = useState(null)

  // customer intake link
  const [orgOptions, setOrgOptions] = useState([])
  const [insurerOrgs, setInsurerOrgs] = useState([])
  const [intakeInsurerOrgIds, setIntakeInsurerOrgIds] = useState([])
  const [intakeCustomerName, setIntakeCustomerName] = useState('')
  const [intakeCustomerPhone, setIntakeCustomerPhone] = useState('')
  const [intakeChannel, setIntakeChannel] = useState('KAKAO')
  const [intakeSending, setIntakeSending] = useState(false)
  const [intakeResult, setIntakeResult] = useState(null)
  const [intakeError, setIntakeError] = useState(null)

  const [localCommonDraftReady, setLocalCommonDraftReady] = useState(false)
  /** 브라우저에 불러오기 가능한 공통 입력 초안 존재 여부(자동 복원 금지 — 버튼으로만 적용) */
  const [browserDraftAvailable, setBrowserDraftAvailable] = useState(false)
  /** 운영자: 서버 flow-draft 존재 여부(메타만 — 자동 병합 안 함) */
  const [serverFlowDraftHint, setServerFlowDraftHint] = useState(null)
  /** 전체 청구하기 — 남은 팩스 설정 대기열 (성공 시 다음 모달로 이어감) */
  const bulkClaimRemainderRef = useRef(null)

  const [claimConsentDocs, setClaimConsentDocs] = useState([])
  const [claimConsentLoading, setClaimConsentLoading] = useState(false)
  const [claimConsentError, setClaimConsentError] = useState('')
  const [claimConsentChecks, setClaimConsentChecks] = useState({})
  const [claimAgentConfirmed, setClaimAgentConfirmed] = useState(false)
  const claimConsentReady = claimConsentDocs.length > 0
    && claimConsentDocs.every((doc) => claimConsentChecks?.[doc.docCode])
    && claimAgentConfirmed
  const claimConsentAllChecked = claimConsentDocs.length > 0
    && claimConsentDocs.every((doc) => claimConsentChecks?.[doc.docCode])
    && claimAgentConfirmed
  const claimConsentSatisfiedForApplication = (application) =>
    !!application?.claimConsentRecorded || claimConsentReady
  const [inPersonSigModal, setInPersonSigModal] = useState({
    open: false,
    application: null,
    insurer: null,
    applications: [],
    insurers: [],
    signerRole: 'INSURED',
  })
  /** 링크 발송 후 연결: { applicationId?, applicationIds?, insurer?, insurers?, signerRole: 'INSURED'|'CONTRACT_HOLDER'|'BENEFICIARY' } */
  const [sigLinkTarget, setSigLinkTarget] = useState(null)
  /** appId 문자열 → { insured?: id, contractHolder?: id, beneficiary?: id } */
  const [pendingRemoteSigsByAppId, setPendingRemoteSigsByAppId] = useState({})
  /** signatureId 문자열 → { status, signatureFileId, previewUrl } */
  const [signaturePreviewById, setSignaturePreviewById] = useState({})
  const [inPersonSigSubmitting, setInPersonSigSubmitting] = useState(false)
  const signaturePreviewObjectUrlsRef = useRef(new Set())
  const inPersonCanvasRef = useRef(null)
  const inPersonDrawingRef = useRef(false)
  const contractHolderCanvasRef = useRef(null)
  const contractHolderDrawingRef = useRef(false)
  const insuredAddressDetailRef = useRef(null)
  const [insuredPostcodeError, setInsuredPostcodeError] = useState('')

  useEffect(() => {
    let ignore = false
    setClaimConsentLoading(true)
    setClaimConsentError('')
    Promise.all(
      CLAIM_CONSENT_DOCS.map(async (meta) => {
        const res = await api.get(`/legal-policies/public/${encodeURIComponent(meta.docCode)}`)
        const doc = res?.data?.data ?? res?.data ?? {}
        return {
          ...meta,
          title: doc.title || meta.fallbackLabel,
          versionLabel: doc.versionLabel || '',
          effectiveDate: doc.effectiveDate || '',
          bodyHtml: doc.bodyHtml || '',
        }
      }),
    )
      .then((docs) => {
        if (ignore) return
        setClaimConsentDocs(docs)
        setClaimConsentChecks(Object.fromEntries(docs.map((doc) => [doc.docCode, false])))
      })
      .catch(() => {
        if (ignore) return
        setClaimConsentDocs([])
        setClaimConsentError('보험금청구 동의 문서를 불러오지 못했습니다.')
      })
      .finally(() => {
        if (!ignore) setClaimConsentLoading(false)
      })
    return () => { ignore = true }
  }, [])

  useEffect(() => {
    let ignore = false
    ;(async () => {
      try {
        const scope = isOperator ? 'all' : undefined
        const res = await api.get('/orgs/options', { params: scope ? { scope } : undefined })
        const payload = Array.isArray(res?.data?.data) ? res.data.data : (Array.isArray(res?.data) ? res.data : [])
        const list = Array.isArray(payload) ? payload : []
        if (!ignore) {
          setOrgOptions(list)
        }
      } catch {
        if (!ignore) {
          setOrgOptions([])
          setInsurerOrgs([])
        }
      }
    })()
    return () => { ignore = true }
  }, [isOperator])

  const lifeRootId = useMemo(() => {
    const root = (orgOptions || []).find(o => o?.type === 'INSURER' && !o?.parentOrgId && String(o?.name || '').includes('생명'))
    return root?.id != null ? Number(root.id) : null
  }, [orgOptions])

  const nonlifeRootId = useMemo(() => {
    const root = (orgOptions || []).find(o =>
      o?.type === 'INSURER'
      && !o?.parentOrgId
      && (String(o?.name || '').includes('손해') || String(o?.name || '').includes('화재') || String(o?.name || '').includes('해상'))
    )
    return root?.id != null ? Number(root.id) : null
  }, [orgOptions])

  const petRootId = useMemo(() => {
    const root = (orgOptions || []).find(o => o?.type === 'INSURER' && !o?.parentOrgId && String(o?.name || '').includes('펫'))
    return root?.id != null ? Number(root.id) : null
  }, [orgOptions])

  const liabilityRootId = useMemo(() => {
    const root = (orgOptions || []).find(o => o?.type === 'INSURER' && !o?.parentOrgId && String(o?.name || '').includes('배상'))
    return root?.id != null ? Number(root.id) : null
  }, [orgOptions])

  useEffect(() => {
    const companyLevel = (orgOptions || [])
      .filter((o) => o?.type === 'INSURER' && o?.parentOrgId && (
        (lifeRootId != null && Number(o.parentOrgId) === lifeRootId)
        || (nonlifeRootId != null && Number(o.parentOrgId) === nonlifeRootId)
        || (petRootId != null && Number(o.parentOrgId) === petRootId)
        || (liabilityRootId != null && Number(o.parentOrgId) === liabilityRootId)
      ))
      .sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || ''), 'ko'))
    setInsurerOrgs(companyLevel)
  }, [orgOptions, lifeRootId, nonlifeRootId, petRootId, liabilityRootId])

  useEffect(() => {
    const nextCategoryRootOrgId = flowCategory === 'LIFE'
      ? lifeRootId
      : flowCategory === 'NONLIFE'
        ? nonlifeRootId
        : flowCategory === 'PET'
          ? petRootId
          : flowCategory === 'LIABILITY'
            ? liabilityRootId
            : null
    setCategoryRootOrgId(nextCategoryRootOrgId)
  }, [flowCategory, lifeRootId, nonlifeRootId, petRootId, liabilityRootId])

  useEffect(() => {
    if (selectedInsurerIds.length === 0) {
      setOpenStep('1')
    }
  }, [selectedInsurerIds])

  useEffect(() => {
    setIntakeInsurerOrgIds(selectedInsurerIds.map((id) => String(id)))
  }, [selectedInsurerIds])

  useEffect(() => {
    setSigCustomerName(claimCustomerName)
    setSigCustomerPhone(claimCustomerPhone)
    setIntakeCustomerName(claimCustomerName)
    setIntakeCustomerPhone(claimCustomerPhone)
  }, [claimCustomerName, claimCustomerPhone])

  useEffect(() => {
    let ignore = false
    ;(async () => {
      if (!categoryRootOrgId) {
        if (!ignore) {
          setGuides({ common: [], specific: [] })
          setSelectedGuide(null)
          setGuidesLoading(false)
          setGuidesError(null)
        }
        return
      }
      setGuidesLoading(true)
      setGuidesError(null)
      setSelectedGuide(null)
      try {
        const res = await api.get('/claims/guides', { params: { categoryRootOrgId } })
        const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
        if (!ignore) {
          setGuides({
            common: Array.isArray(payload?.common) ? payload.common : [],
            specific: Array.isArray(payload?.specific) ? payload.specific : [],
          })
        }
      } catch {
        if (!ignore) setGuidesError('가이드 정보를 불러오지 못했습니다.')
      } finally {
        if (!ignore) setGuidesLoading(false)
      }
    })()
    return () => { ignore = true }
  }, [categoryRootOrgId])

  const mergedGuides = useMemo(() => {
    // 우선은 common만 사용. (추후 insurerOrgId 선택 UI 붙이면 specific merge 가능)
    return Array.isArray(guides?.common) ? guides.common : []
  }, [guides])

  const selectedClaimDocumentSection = useMemo(
    () => CLAIM_DOCUMENT_SECTIONS_ALL.find((s) => s.sectionTitle === claimDocumentSectionTitle) || CLAIM_DOCUMENT_SECTIONS_ALL[0],
    [claimDocumentSectionTitle]
  )

  const sharedClaimExtraFields = useMemo(() => {
    const chSameUi = isContractHolderSameAsInsured(sharedFields.contractHolderSameAsInsured)
    const benAsInsured = isBeneficiarySameAsCustomerFlag(sharedFields.beneficiarySameAsCustomer)
    const benAsContract = !chSameUi && isBeneficiarySameAsContractHolderFlag(sharedFields.beneficiarySameAsContractHolder)

    let beneficiaryName = ''
    let beneficiaryPhone = ''
    let beneficiaryResidentNumber = ''
    let beneficiaryAddress = ''
    let beneficiaryRelationToInsuredResolved = ''
    let beneficiaryEmailResolved = ''
    let beneficiaryWorkplaceResolved = ''
    let beneficiaryOccupationResolved = ''

    if (benAsInsured) {
      beneficiaryName = claimCustomerName
      beneficiaryPhone = claimCustomerPhone
      beneficiaryResidentNumber = sharedFields.customerResidentNumber
      beneficiaryAddress = buildCustomerAddressLine(sharedFields)
      beneficiaryRelationToInsuredResolved = '본인'
      beneficiaryEmailResolved = sharedFields.customerEmail || ''
      beneficiaryWorkplaceResolved = sharedFields.customerWorkplace || ''
      beneficiaryOccupationResolved = (sharedFields.customerOccupation || '').trim()
    } else if (benAsContract) {
      beneficiaryName = (sharedFields.contractHolderName || '').trim()
      beneficiaryPhone = normalizeDigits(sharedFields.contractHolderPhone)
      beneficiaryResidentNumber = sharedFields.contractHolderResidentNumber || ''
      beneficiaryRelationToInsuredResolved = (sharedFields.beneficiaryRelationToInsured || '').trim()
      beneficiaryEmailResolved = ''
      beneficiaryWorkplaceResolved = ''
      beneficiaryOccupationResolved = ''
      beneficiaryAddress = ''
    } else {
      beneficiaryName = sharedFields.beneficiaryName || ''
      beneficiaryPhone = sharedFields.beneficiaryPhone || ''
      beneficiaryResidentNumber = sharedFields.beneficiaryResidentNumber || ''
      beneficiaryAddress = sharedFields.beneficiaryAddress || ''
      beneficiaryRelationToInsuredResolved = (sharedFields.beneficiaryRelationToInsured || '').trim()
      beneficiaryEmailResolved = sharedFields.beneficiaryEmail || ''
      beneficiaryWorkplaceResolved = sharedFields.beneficiaryWorkplace || ''
      beneficiaryOccupationResolved = (sharedFields.beneficiaryOccupation || '').trim()
    }
    const payout =
      sharedFields.accountPayoutType === 'PREPAID' || sharedFields.accountPayoutType === 'GENERAL' || sharedFields.accountPayoutType === 'AUTO_TRANSFER'
        ? sharedFields.accountPayoutType
        : sharedFields.bankAutoTransfer === 'Y'
          ? 'AUTO_TRANSFER'
          : 'GENERAL'
    const useManualBank = payout === 'GENERAL'
    const chSame = isContractHolderSameAsInsured(sharedFields.contractHolderSameAsInsured)
    const chName = chSame ? claimCustomerName : (sharedFields.contractHolderName || '').trim()
    const chRrn = chSame ? sharedFields.customerResidentNumber : sharedFields.contractHolderResidentNumber
    const chPhoneDigits = chSame ? claimCustomerPhone : normalizeDigits(sharedFields.contractHolderPhone)
    const [customerResidentNumber1, customerResidentNumber2] = splitResidentNumber(sharedFields.customerResidentNumber)
    const [beneficiaryResidentNumber1, beneficiaryResidentNumber2] = splitResidentNumber(beneficiaryResidentNumber)
    const [contractHolderResidentNumber1, contractHolderResidentNumber2] = splitResidentNumber(chRrn)
    const [customerPhone1, customerPhone2, customerPhone3] = splitPhoneNumber(claimCustomerPhone)
    const [beneficiaryPhone1, beneficiaryPhone2, beneficiaryPhone3] = splitPhoneNumber(beneficiaryPhone)
    const [contractHolderPhone1, contractHolderPhone2, contractHolderPhone3] = splitPhoneNumber(chPhoneDigits)
    const accidentTypeValue = sharedFields.accidentType === '기타'
      ? `기타:${sharedFields.accidentTypeEtc || ''}`.trim()
      : sharedFields.accidentType
    const claimTypeValue = [
      ...sharedFields.claimTypes,
      ...(sharedFields.claimTypeEtc ? [`기타:${sharedFields.claimTypeEtc}`] : []),
    ].filter(Boolean).join(', ')

    const bnRec = sharedFields.benefitNotificationRecipient
    const benefitMethodKo =
      bnRec === 'PLANNER' ? '보험설계사' : bnRec === 'CLAIMANT' ? '보험청구인' : ''

    const autoYes = String(sharedFields.autoInsuranceProcessed || '').trim() === '예'

    return {
      customerResidentNumber: sharedFields.customerResidentNumber || '',
      customerResidentNumber_1: customerResidentNumber1,
      customerResidentNumber_2: customerResidentNumber2,
      customerPhone_1: customerPhone1,
      customerPhone_2: customerPhone2,
      customerPhone_3: customerPhone3,
      customerEmail: sharedFields.customerEmail || '',
      customerAddress: buildCustomerAddressLine(sharedFields),
      customerWorkplace: sharedFields.customerWorkplace || '',
      customerOccupation: (sharedFields.customerOccupation || '').trim(),
      contractHolderSameAsInsured: chSame ? 'Y' : 'N',
      contractHolderName: chName || '',
      contractHolderResidentNumber: normalizeDigits(chRrn).length >= 6 ? String(chRrn || '').trim() : '',
      contractHolderResidentNumber_1: contractHolderResidentNumber1,
      contractHolderResidentNumber_2: contractHolderResidentNumber2,
      contractHolderPhone: chPhoneDigits || '',
      contractHolderPhone_1: contractHolderPhone1,
      contractHolderPhone_2: contractHolderPhone2,
      contractHolderPhone_3: contractHolderPhone3,
      contractHolderSignatureImageBase64: !chSame && sharedFields.contractHolderSignatureImageBase64
        ? sharedFields.contractHolderSignatureImageBase64
        : '',
      medicalBenefitRecipient: sharedFields.medicalBenefitRecipient === 'Y' ? 'Y' : 'N',
      beneficiaryName: beneficiaryName || '',
      beneficiaryRelationToInsured: beneficiaryRelationToInsuredResolved || '',
      beneficiaryResidentNumber: beneficiaryResidentNumber || '',
      beneficiaryResidentNo: beneficiaryResidentNumber || '',
      beneficiaryResidentNumber_1: beneficiaryResidentNumber1,
      beneficiaryResidentNumber_2: beneficiaryResidentNumber2,
      beneficiaryPhone: beneficiaryPhone || '',
      beneficiaryPhone_1: beneficiaryPhone1,
      beneficiaryPhone_2: beneficiaryPhone2,
      beneficiaryPhone_3: beneficiaryPhone3,
      beneficiaryEmail: beneficiaryEmailResolved || '',
      beneficiaryAddress: beneficiaryAddress || '',
      beneficiaryWorkplace: beneficiaryWorkplaceResolved || '',
      beneficiaryOccupation: beneficiaryOccupationResolved || '',
      accountPayoutType: payout,
      bankAutoTransfer: payout === 'AUTO_TRANSFER' ? 'Y' : 'N',
      bankPrepaidAccount: payout === 'PREPAID' ? 'Y' : 'N',
      bankName: useManualBank ? (sharedFields.bankName || '') : '',
      accountNumber: useManualBank ? (sharedFields.accountNumber || '') : '',
      accountHolder: useManualBank ? (sharedFields.accountHolder || '') : '',
      benefitNotificationRecipient: bnRec || '',
      benefitNotificationMethod: benefitMethodKo,
      benefitNotificationMethodOption: benefitMethodKo,
      /** 보상안내 대상이 설계사일 때 PDF·extraFields용(주민번호는 계정에 없어 빈 값) */
      ...(bnRec === 'PLANNER'
        ? (() => {
            const pname = String(user?.name || '').trim()
            const pemail = String(user?.email || '').trim()
            return {
              benefitGuidePlannerName: pname,
              benefitGuidePlannerEmail: pemail,
              benefitGuidePlannerResidentNumber: '',
              benefitGuidePlannerResidentNumber_1: '',
              benefitGuidePlannerResidentNumber_2: '',
            }
          })()
        : {}),
      accidentType: accidentTypeValue || '',
      claimType: claimTypeValue,
      claimContent: (sharedFields.claimContent || '').trim(),
      accidentDetails: sharedFields.accidentDetails || '',
      accidentDateHour: '',
      accidentDateMinute: '',
      accidentLocation: (sharedFields.accidentLocation || '').trim(),
      autoInsuranceProcessed: (sharedFields.autoInsuranceProcessed || '').trim(),
      autoInsuranceCompany: autoYes ? (sharedFields.autoInsuranceCompany || '').trim() : '',
      autoInsuranceContact: autoYes ? (sharedFields.autoInsuranceContact || '').trim() : '',
      vehicleNumber: autoYes ? (sharedFields.vehicleNumber || '').trim() : '',
      workersCompInsuranceProcessed: '',
      claimScope: '전체보험금',
      partialClaimReason: '',
      beneficiarySameAsCustomer: benAsInsured ? 'Y' : 'N',
      beneficiarySameAsContractHolder: benAsContract ? 'Y' : 'N',
      accidentReceiptType:
        sharedFields.accidentReceiptType === 'ADDITIONAL' || sharedFields.accidentReceiptType === 'NEW'
          ? sharedFields.accidentReceiptType
          : 'NEW',
    }
  }, [claimCustomerName, claimCustomerPhone, sharedFields, user?.name, user?.email])

  const manualBeneficiaryInputsVisible = useMemo(() => {
    const chSame = isContractHolderSameAsInsured(sharedFields.contractHolderSameAsInsured)
    const sameInsured = isBeneficiarySameAsCustomerFlag(sharedFields.beneficiarySameAsCustomer)
    const sameContract = isBeneficiarySameAsContractHolderFlag(sharedFields.beneficiarySameAsContractHolder)
    if (chSame) return !sameInsured
    return !sameInsured && !sameContract
  }, [
    sharedFields.contractHolderSameAsInsured,
    sharedFields.beneficiarySameAsCustomer,
    sharedFields.beneficiarySameAsContractHolder,
  ])

  const payoutType = sharedFields.accountPayoutType === 'PREPAID' || sharedFields.accountPayoutType === 'GENERAL' || sharedFields.accountPayoutType === 'AUTO_TRANSFER'
    ? sharedFields.accountPayoutType
    : sharedFields.bankAutoTransfer === 'Y'
      ? 'AUTO_TRANSFER'
      : 'GENERAL'

  const supportedFlowCategory = GROUPABLE_FLOW_CATEGORY_KEYS.includes(flowCategory)

  const filteredInsurers = useMemo(() => {
    if (!supportedFlowCategory) return []
    return insurerOrgs.filter((o) => {
      const parentId = o?.parentOrgId != null ? Number(o.parentOrgId) : null
      return (flowCategory === 'LIFE' && lifeRootId != null && parentId === lifeRootId)
        || (flowCategory === 'NONLIFE' && nonlifeRootId != null && parentId === nonlifeRootId)
        || (flowCategory === 'PET' && petRootId != null && parentId === petRootId)
        || (flowCategory === 'LIABILITY' && liabilityRootId != null && parentId === liabilityRootId)
    })
  }, [flowCategory, insurerOrgs, lifeRootId, nonlifeRootId, petRootId, liabilityRootId, supportedFlowCategory])

  const allSelectableInsurers = useMemo(() => {
    return [...insurerOrgs].sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || ''), 'ko'))
  }, [insurerOrgs])

  const selectedInsurers = useMemo(() => {
    const selected = new Set(selectedInsurerIds.map(String))
    return allSelectableInsurers.filter(o => selected.has(String(o.id)))
  }, [allSelectableInsurers, selectedInsurerIds])

  const selectedInsurerIdsKey = useMemo(
    () => [...selectedInsurerIds].map(String).sort().join(','),
    [selectedInsurerIds],
  )

  useEffect(() => {
    const ids = [...new Set(selectedInsurerIds.map(String).filter(Boolean))]
    if (ids.length === 0) {
      setClaimAdditionalFieldDefsByInsurerId({})
      setClaimAdditionalFieldsLoading(false)
      setClaimAdditionalFieldsError('')
      return undefined
    }

    let cancelled = false
    setClaimAdditionalFieldsLoading(true)
    setClaimAdditionalFieldsError('')
    ;(async () => {
      const next = {}
      let failed = 0
      await Promise.all(ids.map(async (id) => {
        try {
          const res = await api.get('/claims/additional-fields', {
            params: { insurerOrgId: id },
          })
          const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
          next[id] = normalizeAdditionalFieldDefinitions(payload)
        } catch {
          failed += 1
          next[id] = []
        }
      }))
      if (cancelled) return
      setClaimAdditionalFieldDefsByInsurerId(next)
      setClaimAdditionalFieldsError(failed > 0 ? '일부 보험사의 추가 입력 항목을 불러오지 못했습니다.' : '')
      setClaimAdditionalFieldsLoading(false)
    })()

    return () => { cancelled = true }
  }, [selectedInsurerIdsKey, selectedInsurerIds])

  const claimAdditionalFieldGroups = useMemo(
    () => buildAdditionalFieldGroupsForInsurers(claimAdditionalFieldDefsByInsurerId, selectedInsurers),
    [claimAdditionalFieldDefsByInsurerId, selectedInsurers],
  )

  useEffect(() => {
    const allowedKeys = new Set(claimAdditionalFieldGroups.map((group) => additionalFieldGroupModeKey(group)).filter(Boolean))
    setClaimAdditionalFieldModeByGroupKey((prev) => {
      const next = {}
      Object.entries(prev || {}).forEach(([key, mode]) => {
        if (allowedKeys.has(key)) next[key] = mode
      })
      return next
    })
  }, [claimAdditionalFieldGroups])

  const setClaimAdditionalFieldGroupMode = useCallback((groupKey, mode) => {
    const key = String(groupKey || '').trim()
    if (!key) return
    setClaimAdditionalFieldModeByGroupKey((prev) => {
      const next = { ...(prev || {}) }
      if (!mode || mode === CLAIM_ADDITIONAL_FIELD_COMMON_MODE) {
        delete next[key]
      } else {
        next[key] = mode
      }
      return next
    })
  }, [])

  const claimAdditionalFieldRequiredError = useMemo(
    () => validateRequiredAdditionalFields(
      claimAdditionalFieldGroups,
      claimAdditionalFieldValues,
      claimAdditionalFieldModeByGroupKey,
    ),
    [claimAdditionalFieldGroups, claimAdditionalFieldModeByGroupKey, claimAdditionalFieldValues],
  )

  const selectedIncludesNonlife = useMemo(() => {
    if (selectedInsurers.length === 0) return flowCategory === 'NONLIFE'
    return selectedInsurers.some((insurer) => {
      const parentId = insurer?.parentOrgId != null ? Number(insurer.parentOrgId) : null
      return nonlifeRootId != null && parentId === nonlifeRootId
    })
  }, [flowCategory, nonlifeRootId, selectedInsurers])

  const selectedIncludesLife = useMemo(() => {
    if (selectedInsurers.length === 0) return flowCategory === 'LIFE'
    return selectedInsurers.some((insurer) => {
      const parentId = insurer?.parentOrgId != null ? Number(insurer.parentOrgId) : null
      return lifeRootId != null && parentId === lifeRootId
    })
  }, [flowCategory, lifeRootId, selectedInsurers])

  const showContractHolderInputs = selectedIncludesNonlife
  const showBeneficiaryRoleInputs = selectedIncludesLife
  const showBeneficiaryDetailsInputs = showBeneficiaryRoleInputs && manualBeneficiaryInputsVisible
  const showBeneficiaryRelationInput =
    showBeneficiaryRoleInputs && !isBeneficiarySameAsCustomerFlag(sharedFields.beneficiarySameAsCustomer)
  const showBeneficiaryInputs = showBeneficiaryDetailsInputs || showBeneficiaryRelationInput
  const beneficiaryRelationRequiredError = useMemo(() => {
    if (!showBeneficiaryRelationInput) return ''
    return String(sharedFields.beneficiaryRelationToInsured || '').trim()
      ? ''
      : '수익자의 피보험자와의 관계를 입력해 주세요.'
  }, [showBeneficiaryRelationInput, sharedFields.beneficiaryRelationToInsured])

  useEffect(() => {
    if (showContractHolderInputs) return
    if (
      isContractHolderSameAsInsured(sharedFields.contractHolderSameAsInsured)
      && !isBeneficiarySameAsContractHolderFlag(sharedFields.beneficiarySameAsContractHolder)
    ) {
      return
    }
    setSharedFields((prev) => ({
      ...prev,
      contractHolderSameAsInsured: true,
      contractHolderName: '',
      contractHolderResidentNumber: '',
      contractHolderPhone: '',
      contractHolderSignatureImageBase64: '',
      beneficiarySameAsContractHolder: false,
    }))
  }, [
    showContractHolderInputs,
    sharedFields.contractHolderSameAsInsured,
    sharedFields.beneficiarySameAsContractHolder,
  ])

  useEffect(() => {
    if (showBeneficiaryRoleInputs) return
    if (
      isBeneficiarySameAsCustomerFlag(sharedFields.beneficiarySameAsCustomer)
      && !isBeneficiarySameAsContractHolderFlag(sharedFields.beneficiarySameAsContractHolder)
    ) {
      return
    }
    setSharedFields((prev) => ({
      ...prev,
      beneficiarySameAsCustomer: true,
      beneficiarySameAsContractHolder: false,
      beneficiaryName: '',
      beneficiaryRelationToInsured: '',
      beneficiaryResidentNumber: '',
      beneficiaryPhone: '',
      beneficiaryEmail: '',
      beneficiaryAddress: '',
      beneficiaryWorkplace: '',
      beneficiaryOccupation: '',
    }))
  }, [
    showBeneficiaryRoleInputs,
    sharedFields.beneficiarySameAsCustomer,
    sharedFields.beneficiarySameAsContractHolder,
  ])

  const visibleStep2Sections = useMemo(
    () => getVisibleStep2Sections(sharedFields, showContractHolderInputs, showBeneficiaryInputs),
    [sharedFields, showContractHolderInputs, showBeneficiaryInputs],
  )

  const getStep2SectionMeta = useCallback((key) => {
    const idx = visibleStep2Sections.indexOf(key)
    const activeIdx = activeStep2Section ? visibleStep2Sections.indexOf(activeStep2Section) : -1
    return {
      active: activeStep2Section === key,
      completed: idx >= 0 && (idx < step2MaxReachedIndex || (activeIdx >= 0 && idx < activeIdx)),
      isLast: idx === visibleStep2Sections.length - 1,
      index: idx,
    }
  }, [visibleStep2Sections, activeStep2Section, step2MaxReachedIndex])

  const reopenStep2DetailsForEdit = useCallback(() => {
    setActiveModal(null)
    setOpenStep('2')
    setActiveStep2Section('details')
    setExpandedSharedSections((prev) => ({ ...prev, details: true }))
  }, [])

  const proceedFromStep2Summary = useCallback(() => {
    if (beneficiaryRelationRequiredError) {
      setActiveModal(null)
      setOpenStep('2')
      setActiveStep2Section('beneficiary')
      setExpandedSharedSections((prev) => ({ ...prev, beneficiary: true }))
      show(beneficiaryRelationRequiredError, 'error')
      return
    }
    setActiveModal(null)
    setOpenStep('3')
  }, [beneficiaryRelationRequiredError, show])

  const goNextStep2Section = useCallback((currentKey) => {
    if (currentKey === 'beneficiary' && beneficiaryRelationRequiredError) {
      setExpandedSharedSections((prev) => ({ ...prev, beneficiary: true }))
      setActiveStep2Section('beneficiary')
      show(beneficiaryRelationRequiredError, 'error')
      return
    }
    const idx = visibleStep2Sections.indexOf(currentKey)
    if (idx < 0) return
    const nextKey = idx < visibleStep2Sections.length - 1 ? visibleStep2Sections[idx + 1] : null
    setStep2MaxReachedIndex((prev) => Math.max(prev, idx + 1))
    setExpandedSharedSections((prev) => {
      const next = { ...prev, [currentKey]: false }
      if (nextKey) next[nextKey] = true
      return next
    })
    setActiveStep2Section(nextKey || null)
    if (!nextKey) {
      setActiveModal('STEP2_SUMMARY')
    }
  }, [beneficiaryRelationRequiredError, show, visibleStep2Sections])

  useEffect(() => {
    if (openStep !== '2') return
    if (activeStep2Section && visibleStep2Sections.includes(activeStep2Section)) return
    if (
      activeStep2Section === null
      && step2MaxReachedIndex >= visibleStep2Sections.length
      && visibleStep2Sections.length > 0
    ) {
      return
    }
    const first = visibleStep2Sections[0]
    if (!first) return
    setActiveStep2Section(first)
    setExpandedSharedSections((prev) => {
      const next = { ...prev }
      visibleStep2Sections.forEach((key, i) => {
        next[key] = i === 0
      })
      return next
    })
  }, [openStep, visibleStep2Sections, activeStep2Section, step2MaxReachedIndex])

  useEffect(() => {
    if (selectedIncludesNonlife) return
    if (
      sharedFields.autoInsuranceProcessed === '아니오'
      && !sharedFields.autoInsuranceCompany
      && !sharedFields.autoInsuranceContact
      && !sharedFields.vehicleNumber
    ) {
      return
    }
    setSharedFields((prev) => ({
      ...prev,
      autoInsuranceProcessed: '아니오',
      autoInsuranceCompany: '',
      autoInsuranceContact: '',
      vehicleNumber: '',
    }))
  }, [
    selectedIncludesNonlife,
    sharedFields.autoInsuranceProcessed,
    sharedFields.autoInsuranceCompany,
    sharedFields.autoInsuranceContact,
    sharedFields.vehicleNumber,
  ])

  const step2SummaryRows = useMemo(() => {
    const value = (v) => {
      const s = String(v || '').trim()
      return s || '-'
    }
    const accidentTypeLabel = sharedFields.accidentType === '기타'
      ? value(sharedFields.accidentTypeEtc ? `기타: ${sharedFields.accidentTypeEtc}` : '기타')
      : value(sharedFields.accidentType)
    return [
      { label: '보험사', value: selectedInsurers.map((o) => o.name).filter(Boolean).join(', ') || '-' },
      { label: '피보험자', value: value(claimCustomerName) },
      { label: '연락처', value: value(formatPhoneDisplay(claimCustomerPhone)) },
      { label: '사고일자', value: value(accidentDate) },
      { label: '접수 구분', value: sharedFields.accidentReceiptType === 'ADDITIONAL' ? '동일사고 추가접수' : '최초청구' },
      { label: '사고 유형', value: accidentTypeLabel },
      { label: '사고 장소', value: value(sharedFields.accidentLocation) },
      { label: '사고 경위', value: value(sharedFields.accidentDetails) },
      { label: '보험금 수령', value: payoutType === 'GENERAL' ? `${value(sharedFields.bankName)} / ${value(sharedFields.accountHolder)}` : payoutType === 'PREPAID' ? '기지급계좌' : '자동이체계좌' },
      { label: '첨부 서류', value: sharedDocuments.length ? `${sharedDocuments.length}개 · ${sharedDocumentPageCount}장` : '-' },
    ]
  }, [
    accidentDate,
    claimCustomerName,
    claimCustomerPhone,
    payoutType,
    selectedInsurers,
    sharedDocumentPageCount,
    sharedDocuments.length,
    sharedFields.accidentDetails,
    sharedFields.accidentLocation,
    sharedFields.accidentReceiptType,
    sharedFields.accidentType,
    sharedFields.accidentTypeEtc,
    sharedFields.accountHolder,
    sharedFields.bankName,
  ])

  const createdClaimsForSelectedInsurers = useMemo(() => {
    return selectedInsurers
      .map((org) => ({
        insurer: org,
        application: createdClaimApplications[String(org.id)] || null,
      }))
  }, [selectedInsurers, createdClaimApplications])

  const signableClaimsForSelectedInsurers = useMemo(() => {
    return createdClaimsForSelectedInsurers.filter(({ application }) => application?.id)
  }, [createdClaimsForSelectedInsurers])

  const commonInsuredApplicationIds = useMemo(() => {
    return signableClaimsForSelectedInsurers.map(({ application }) => application.id)
  }, [signableClaimsForSelectedInsurers])

  const commonContractHolderApplicationIds = useMemo(() => {
    return signableClaimsForSelectedInsurers
      .filter(({ application }) => needsContractHolderSignatureFromApplication(application))
      .map(({ application }) => application.id)
  }, [signableClaimsForSelectedInsurers])

  const commonBeneficiaryApplicationIds = useMemo(() => {
    return signableClaimsForSelectedInsurers
      .filter(({ application }) => needsBeneficiarySignatureFromApplication(application))
      .map(({ application }) => application.id)
  }, [signableClaimsForSelectedInsurers])

  const commonContractHolderSignatureRequired = commonContractHolderApplicationIds.length > 0
  const commonBeneficiarySignatureRequired = commonBeneficiaryApplicationIds.length > 0

  const commonInsuredSignatureCompleted =
    signableClaimsForSelectedInsurers.length > 0 &&
    signableClaimsForSelectedInsurers.every(({ application }) => claimSignatureCompleted(application.signatureStatus))

  const commonContractHolderSignatureCompleted =
    !commonContractHolderSignatureRequired ||
    signableClaimsForSelectedInsurers
      .filter(({ application }) => needsContractHolderSignatureFromApplication(application))
      .every(({ application }) => claimSignatureCompleted(application.contractHolderSignatureStatus))

  const commonBeneficiarySignatureCompleted =
    !commonBeneficiarySignatureRequired ||
    signableClaimsForSelectedInsurers
      .filter(({ application }) => needsBeneficiarySignatureFromApplication(application))
      .every(({ application }) => claimSignatureCompleted(application.beneficiarySignatureStatus))

  const commonSignatureCompleted =
    signableClaimsForSelectedInsurers.length > 0 &&
    commonInsuredSignatureCompleted &&
    commonContractHolderSignatureCompleted &&
    commonBeneficiarySignatureCompleted

  const completedClaimSignatureIds = useMemo(() => {
    const ids = new Set()
    signableClaimsForSelectedInsurers.forEach(({ application }) => {
      if (claimSignatureCompleted(application?.signatureStatus) && application?.signatureId) {
        ids.add(String(application.signatureId))
      }
      if (claimSignatureCompleted(application?.contractHolderSignatureStatus) && application?.contractHolderSignatureId) {
        ids.add(String(application.contractHolderSignatureId))
      }
      if (claimSignatureCompleted(application?.beneficiarySignatureStatus) && application?.beneficiarySignatureId) {
        ids.add(String(application.beneficiarySignatureId))
      }
    })
    return Array.from(ids)
  }, [signableClaimsForSelectedInsurers])

  const needsVirtualFax = useMemo(() => {
    return selectedInsurers.some((o) => !!insurerFaxByOrgId[String(o.id)]?.virtualFaxRequired)
  }, [selectedInsurers, insurerFaxByOrgId])

  const hasGroupClaimAvailable = useMemo(() => {
    return selectedInsurers.some(o => GROUP_CLAIM_AVAILABLE_INSURERS.has(String(o?.name || '').trim()))
  }, [selectedInsurers])

  const faxScheduleModalCfg = useMemo(() => {
    if (!faxScheduleModal.open || !faxScheduleModal.insurer) return null
    return insurerFaxByOrgId[String(faxScheduleModal.insurer.id)] || null
  }, [faxScheduleModal.open, faxScheduleModal.insurer, insurerFaxByOrgId])

  const faxScheduleListedFaxDisplay = useMemo(() => {
    if (!faxScheduleModalCfg || !faxScheduleModal.application) return ''
    const at = accidentTypeFromApplication(faxScheduleModal.application)
    return resolveListedClaimFaxNumber(faxScheduleModalCfg, at)
  }, [faxScheduleModalCfg, faxScheduleModal.application])

  const selectedInsurerOptionsForActions = useMemo(() => {
    const base = selectedInsurers.length > 0 ? [...selectedInsurers] : [...filteredInsurers]
    return base.sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || ''), 'ko'))
  }, [selectedInsurers, filteredInsurers])

  const intakeSelectedInsurerOptions = useMemo(() => {
    const selectedKeys = new Set(intakeInsurerOrgIds.map(String))
    return selectedInsurerOptionsForActions.filter((o) => selectedKeys.has(String(o?.id)))
  }, [intakeInsurerOrgIds, selectedInsurerOptionsForActions])

  const faxSettingTargetInsurers = useMemo(() => {
    const byId = new Map()
    filteredInsurers.forEach((item) => {
      if (item?.id != null) byId.set(String(item.id), item)
    })
    selectedInsurers.forEach((item) => {
      if (item?.id != null) byId.set(String(item.id), item)
    })
    return Array.from(byId.values())
  }, [filteredInsurers, selectedInsurers])

  const buildInsurerSummary = (insurers) => {
    const names = (insurers || [])
      .map((item) => String(item?.name || '').trim())
      .filter(Boolean)
    if (names.length === 0) return ''
    if (names.length === 1) return names[0]
    return `${names[0]} 외 ${names.length - 1}곳`
  }

  const toggleInsurerSelection = (insurerId) => {
    const key = String(insurerId)
    setSelectedInsurerIds((prev) => {
      const exists = prev.some(v => String(v) === key)
      if (exists) return prev.filter(v => String(v) !== key)
      if (claimTemplateStatusLoading) return prev
      if (claimTemplateByOrgId[key] !== true) return prev
      if (!CLAIM_INSURER_MULTI_SELECT_ENABLED) return [insurerId]
      return [...prev, insurerId]
    })
  }

  const selectFlowCategory = (categoryKey) => {
    if (!categoryKey) return
    if (!GROUPABLE_FLOW_CATEGORY_KEYS.includes(categoryKey)) {
      setFlowCategory(categoryKey)
      setFlowCategoryKeys([categoryKey])
      setSelectedInsurerIds([])
      return
    }

    setFlowCategory(categoryKey)
    setFlowCategoryKeys([categoryKey])
  }

  const appsRef = useRef(createdClaimApplications)
  useEffect(() => {
    appsRef.current = createdClaimApplications
  }, [createdClaimApplications])

  const refreshApplicationSummaries = useCallback(async () => {
    const prev = appsRef.current
    const updates = {}
    for (const [key, row] of Object.entries(prev)) {
      if (!row?.id) continue
      try {
        const res = await api.get(`/claims/applications/${row.id}`)
        const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
        if (payload?.id) updates[key] = payload
      } catch { /* ignore */ }
    }
    if (Object.keys(updates).length > 0) {
      setCreatedClaimApplications((p) => ({ ...p, ...updates }))
    }
  }, [])

  useEffect(() => {
    if (openStep === '5') refreshApplicationSummaries()
  }, [openStep, refreshApplicationSummaries])

  const applicationHasCompletedSignature = (application) => {
    if (!application) return false
    const insuredOk = claimSignatureCompleted(application.signatureStatus)
    const chOk = !needsContractHolderSignatureFromApplication(application)
      || claimSignatureCompleted(application.contractHolderSignatureStatus)
    const beneficiaryOk = !needsBeneficiarySignatureFromApplication(application)
      || claimSignatureCompleted(application.beneficiarySignatureStatus)
    return insuredOk && chOk && beneficiaryOk
  }

  const applicationHasAttachedDocuments = (application) =>
    Array.isArray(application?.documents) && application.documents.some((doc) => String(doc?.fileId || '').trim())

  /** STEP 7 팩스 예약 버튼과 동일 조건 — STEP 완료·발송 대기/팩스확정대기 기준 */
  const getClaimApplicationFaxReadiness = (application) => {
    if (!application) {
      return { ready: false, message: 'STEP 2에서 공통 청구정보를 입력해 청구서를 먼저 생성해 주세요.' }
    }
    if (!applicationHasAttachedDocuments(application)) {
      return { ready: false, message: 'STEP 4에서 청구서류 첨부를 완료한 뒤 보험금 청구를 진행할 수 있습니다.' }
    }
    if (!applicationHasCompletedSignature(application)) {
      return { ready: false, message: 'STEP 5에서 고객 서명을 완료한 뒤 보험금 청구를 진행할 수 있습니다.' }
    }
    const hasPendingFax = application?.faxQuotaSettlementStatus === 'PENDING'
    const isScheduled = application?.faxReservationStatus === 'SCHEDULED'
      || application?.faxReservationStatus === 'SENDING'
    const faxCanReschedule =
      application?.faxReservationStatus === 'FAILED' || application?.faxReservationStatus === 'CANCELLED'
    const pendingBlocksSchedule = hasPendingFax && !faxCanReschedule
    if (pendingBlocksSchedule) {
      return { ready: false, message: '이전 팩스 발송 결과가 아직 확정되지 않아 새로 발송할 수 없습니다. 잠시 후 다시 시도해 주세요.' }
    }
    if (isScheduled) {
      return { ready: false, message: application?.faxReservationStatus === 'SENDING' ? '팩스 발송 처리 중입니다.' : '이미 발송 대기 중인 팩스가 있습니다.' }
    }
    return { ready: true, message: '' }
  }

  const claimApplicationReadyForFaxSchedule = (application) =>
    getClaimApplicationFaxReadiness(application).ready

  const bulkClaimEligibleRows = useMemo(
    () =>
      createdClaimsForSelectedInsurers.filter(({ application }) =>
        claimApplicationReadyForFaxSchedule(application),
      ),
    [createdClaimsForSelectedInsurers],
  )

  const openFaxScheduleModalInternal = (application, insurer) => {
    if (!application || !insurer) return
    const sid = String(insurer.id)
    const cfg = insurerFaxByOrgId[sid]
    const at = accidentTypeFromApplication(application)
    const defaultNum = resolveListedClaimFaxNumber(cfg, at) || cfg?.claimFaxNumber || ''
    const virt = !!cfg?.virtualFaxRequired
    setFaxScheduleForm({
      receiveNum: virt ? (application?.faxReceiveNum || '') : (defaultNum || application?.faxReceiveNum || ''),
      receiveName: application?.faxReceiveName || insurer?.name || '',
      title: application?.faxTitle || '보험금청구서',
    })
    setFaxScheduleModal({ open: true, application, insurer })
  }

  const openFaxDisclaimer = (application, insurer) => {
    const readiness = getClaimApplicationFaxReadiness(application)
    if (!readiness.ready) {
      show(readiness.message || '보험금 청구를 진행할 수 없습니다. 이전 STEP 완료 여부를 확인해 주세요.', 'error')
      return
    }
    if (claimAdditionalFieldRequiredError) {
      setOpenStep('3')
      show(claimAdditionalFieldRequiredError, 'error')
      return
    }
    if (claimConsentLoading || claimConsentError || !claimConsentSatisfiedForApplication(application)) {
      setOpenStep('6')
      show('STEP 6에서 보험금청구 필수 동의를 먼저 확인해 주세요.', 'error')
      return
    }
    if (application && insurer) openFaxScheduleModalInternal(application, insurer)
  }

  const openClaimPdfBlobInNewTab = (blob) => {
    const url = URL.createObjectURL(blob)
    window.open(url, '_blank', 'noopener,noreferrer')
    setTimeout(() => {
      try { URL.revokeObjectURL(url) } catch { /* ignore */ }
    }, 120000)
  }

  const previewClaimApplicationPdf = async (applicationId) => {
    if (!applicationId) return
    const openFormOnlyPdf = async () => {
      const res = await api.get(`/claims/applications/${applicationId}/pdf`, { responseType: 'blob' })
      openClaimPdfBlobInNewTab(res.data)
    }
    try {
      const res = await api.get(`/claims/applications/${applicationId}/fax-preview`, { responseType: 'blob' })
      openClaimPdfBlobInNewTab(res.data)
    } catch (e) {
      const status = e?.response?.status
      const blob = e?.response?.data
      let errText = ''
      if (blob && typeof blob.text === 'function') {
        try { errText = (await blob.text()).trim() } catch { /* ignore */ }
      }
      if (status === 409) {
        let detail = ''
        if (errText.startsWith('{')) {
          try {
            const j = JSON.parse(errText)
            if (j?.message) detail = j.message
          } catch { /* ignore */ }
        }
        if (detail.includes('청구서식 PDF 파일')) {
          show(detail, 'error')
          return
        }
        show(
          detail
            ? `${detail} 청구서 양식 PDF만 엽니다.`
            : '첨부를 팩스와 같은 한 장의 PDF로 묶을 수 없어 청구서 양식만 엽니다. (PDF·이미지 첨부만 합본 미리보기가 가능합니다.)',
          'info',
        )
        try {
          await openFormOnlyPdf()
        } catch {
          show('미리보기를 열 수 없습니다. 청구서 양식과 첨부파일 상태를 확인해 주세요.', 'error')
        }
        return
      }
      if (status >= 400 && status < 500) {
        let msg = '미리보기를 열 수 없습니다.'
        if (errText.startsWith('{')) {
          try {
            const j = JSON.parse(errText)
            if (j?.message) msg = j.message
          } catch { /* ignore */ }
        }
        show(msg, 'error')
        return
      }
      try {
        await openFormOnlyPdf()
        show('팩스 합본 미리보기를 불러오지 못해 청구서 양식만 열었습니다.', 'info')
      } catch {
        show('발송 서류 미리보기를 열 수 없습니다. 잠시 후 다시 시도하거나 청구서 양식 상태를 확인해 주세요.', 'error')
      }
    }
  }

  /** @param {'insured'|'contractHolder'|'beneficiary'} slot */
  const syncRemoteSignatureToApplication = async (applicationIdOrIds, signatureId, slot) => {
    const applicationIds = (Array.isArray(applicationIdOrIds) ? applicationIdOrIds : [applicationIdOrIds]).filter(Boolean)
    if (applicationIds.length === 0 || !signatureId || !slot) return
    try {
      const res = await api.get(`/claims/signatures/${signatureId}`)
      const s = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
      if (s?.status !== 'COMPLETED') {
        show('아직 서명이 완료되지 않았습니다. 서명 완료 후 다시 확인해 주세요.', 'info')
        return
      }
      const body = slot === 'contractHolder'
        ? { contractHolderSignatureId: signatureId, status: 'SUBMITTED' }
        : slot === 'beneficiary'
          ? { beneficiarySignatureId: signatureId, status: 'SUBMITTED' }
          : { signatureId, status: 'SUBMITTED' }
      await Promise.all(applicationIds.map((applicationId) => api.put(`/claims/applications/${applicationId}`, body)))
      await refreshApplicationSummaries()
      setPendingRemoteSigsByAppId((p) => {
        const next = { ...p }
        applicationIds.forEach((applicationId) => {
          const key = String(applicationId)
          const cur = { ...(next[key] || {}) }
          delete cur[slot]
          if (Object.keys(cur).length === 0) delete next[key]
          else next[key] = cur
        })
        return next
      })
      show(
        applicationIds.length > 1
          ? `공통 서명이 ${applicationIds.length}건의 청구서에 반영되었습니다.`
          : '서명이 청구서에 반영되었습니다. STEP 6 동의 확인 후 STEP 7에서 발송 전 서류를 다시 확인할 수 있습니다.',
        'success',
      )
    } catch {
      show('서명을 확인하거나 청구서에 반영하지 못했습니다. 서명 상태를 확인한 뒤 다시 시도해 주세요.', 'error')
    }
  }

  useEffect(() => {
    const missingIds = completedClaimSignatureIds.filter((id) => !signaturePreviewById[id])
    if (missingIds.length === 0) return
    let cancelled = false
    missingIds.forEach(async (signatureId) => {
      try {
        const res = await api.get(`/claims/signatures/${signatureId}`)
        const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
        const fileId = payload?.signatureFileId || ''
        let previewUrl = ''
        if (fileId) {
          const previewRes = await api.get(`/files/${encodeURIComponent(canonicalStoredFileIdForApiPath(fileId))}/preview`, { responseType: 'blob' })
          const contentType = String(previewRes.headers?.['content-type'] || '').toLowerCase()
          const blob = new Blob([previewRes.data], { type: contentType || 'image/png' })
          previewUrl = URL.createObjectURL(blob)
          signaturePreviewObjectUrlsRef.current.add(previewUrl)
        }
        if (cancelled) {
          if (previewUrl) {
            try { URL.revokeObjectURL(previewUrl) } catch { }
            signaturePreviewObjectUrlsRef.current.delete(previewUrl)
          }
          return
        }
        setSignaturePreviewById((prev) => ({
          ...prev,
          [signatureId]: {
            status: payload?.status || null,
            signatureFileId: fileId,
            previewUrl,
          },
        }))
      } catch {
        if (cancelled) return
        setSignaturePreviewById((prev) => ({
          ...prev,
          [signatureId]: { status: 'ERROR', signatureFileId: '', previewUrl: '' },
        }))
      }
    })
    return () => { cancelled = true }
  }, [completedClaimSignatureIds, signaturePreviewById])

  useEffect(() => () => {
    signaturePreviewObjectUrlsRef.current.forEach((url) => {
      try { URL.revokeObjectURL(url) } catch { }
    })
    signaturePreviewObjectUrlsRef.current.clear()
  }, [])

  const clearInPersonCanvas = () => {
    const c = inPersonCanvasRef.current
    if (!c) return
    const ctx = c.getContext('2d')
    if (!ctx) return
    ctx.fillStyle = '#fff'
    ctx.fillRect(0, 0, c.width, c.height)
  }

  const startInPersonDraw = (x, y) => {
    const c = inPersonCanvasRef.current
    if (!c) return
    const ctx = c.getContext('2d')
    if (!ctx) return
    inPersonDrawingRef.current = true
    ctx.beginPath()
    ctx.moveTo(x, y)
  }

  const moveInPersonDraw = (x, y) => {
    const c = inPersonCanvasRef.current
    if (!c || !inPersonDrawingRef.current) return
    const ctx = c.getContext('2d')
    if (!ctx) return
    ctx.strokeStyle = '#111'
    ctx.lineWidth = 2
    ctx.lineCap = 'round'
    ctx.lineTo(x, y)
    ctx.stroke()
  }

  const endInPersonDraw = () => {
    inPersonDrawingRef.current = false
  }

  const clearContractHolderCanvas = () => {
    const c = contractHolderCanvasRef.current
    if (!c) return
    const ctx = c.getContext('2d')
    if (!ctx) return
    ctx.fillStyle = '#fff'
    ctx.fillRect(0, 0, c.width, c.height)
  }

  const startContractHolderDraw = (x, y) => {
    const c = contractHolderCanvasRef.current
    if (!c) return
    const ctx = c.getContext('2d')
    if (!ctx) return
    contractHolderDrawingRef.current = true
    ctx.beginPath()
    ctx.moveTo(x, y)
  }

  const moveContractHolderDraw = (x, y) => {
    const c = contractHolderCanvasRef.current
    if (!c || !contractHolderDrawingRef.current) return
    const ctx = c.getContext('2d')
    if (!ctx) return
    ctx.strokeStyle = '#111'
    ctx.lineWidth = 2
    ctx.lineCap = 'round'
    ctx.lineTo(x, y)
    ctx.stroke()
  }

  const endContractHolderDraw = () => {
    contractHolderDrawingRef.current = false
  }

  const getContractHolderCanvasPoint = (e) => {
    const c = contractHolderCanvasRef.current
    if (!c) return null
    const r = c.getBoundingClientRect()
    const clientX = e.touches?.[0]?.clientX ?? e.clientX
    const clientY = e.touches?.[0]?.clientY ?? e.clientY
    if (clientX == null || clientY == null) return null
    const scaleX = c.width / r.width
    const scaleY = c.height / r.height
    return { x: (clientX - r.left) * scaleX, y: (clientY - r.top) * scaleY }
  }

  const captureContractHolderSignature = () => {
    const canvas = contractHolderCanvasRef.current
    if (!canvas) return
    try {
      const dataUrl = canvas.toDataURL('image/png')
      setSharedField('contractHolderSignatureImageBase64', dataUrl)
    } catch {
      /* ignore */
    }
  }

  useLayoutEffect(() => {
    if (isContractHolderSameAsInsured(sharedFields.contractHolderSameAsInsured)) return
    const id = requestAnimationFrame(() => clearContractHolderCanvas())
    return () => cancelAnimationFrame(id)
  }, [sharedFields.contractHolderSameAsInsured])

  const submitInPersonSignature = async () => {
    const canvas = inPersonCanvasRef.current
    const app = inPersonSigModal.application
    const targetApplications = (inPersonSigModal.applications?.length ? inPersonSigModal.applications : [app]).filter((a) => a?.id)
    if (!canvas || targetApplications.length === 0) return
    const role = inPersonSigModal.signerRole === 'CONTRACT_HOLDER'
      ? 'CONTRACT_HOLDER'
      : inPersonSigModal.signerRole === 'BENEFICIARY'
        ? 'BENEFICIARY'
        : 'INSURED'
    setInPersonSigSubmitting(true)
    try {
      const dataUrl = canvas.toDataURL('image/png')
      const createRes = await api.post('/claims/signatures', {
        mode: 'IN_PERSON',
        customerName: role === 'CONTRACT_HOLDER'
          ? (sharedFields.contractHolderName || null)
          : role === 'BENEFICIARY'
            ? (sharedFields.beneficiaryName || null)
            : (claimCustomerName || null),
        customerPhone: role === 'CONTRACT_HOLDER'
          ? (sharedFields.contractHolderPhone || null)
          : role === 'BENEFICIARY'
            ? (sharedFields.beneficiaryPhone || null)
            : (claimCustomerPhone || null),
        onCompleteActionJson: null,
        signerRole: role,
      })
      const created = (createRes?.data && createRes.data.data !== undefined) ? createRes.data.data : createRes.data
      const sigId = created?.id
      if (!sigId) throw new Error('NO_SIG')
      await api.post(`/claims/signatures/${sigId}/complete`, { dataUrl })
      const putBody = role === 'CONTRACT_HOLDER'
        ? { contractHolderSignatureId: sigId, status: 'SUBMITTED' }
        : role === 'BENEFICIARY'
          ? { beneficiarySignatureId: sigId, status: 'SUBMITTED' }
          : { signatureId: sigId, status: 'SUBMITTED' }
      await Promise.all(targetApplications.map((targetApp) => api.put(`/claims/applications/${targetApp.id}`, putBody)))
      await refreshApplicationSummaries()
      setInPersonSigModal({ open: false, application: null, insurer: null, applications: [], insurers: [], signerRole: 'INSURED' })
      clearInPersonCanvas()
      show(
        targetApplications.length > 1
          ? `공통 서명이 ${targetApplications.length}건의 청구서에 저장되었습니다.`
          : '서명이 저장되었습니다. 보험사 발송 전 STEP 6 동의 확인 후 STEP 7에서 서류를 다시 확인할 수 있습니다.',
        'success',
      )
    } catch {
      show('서명을 저장하지 못했습니다. 서명 입력 상태와 네트워크를 확인해 주세요.', 'error')
    } finally {
      setInPersonSigSubmitting(false)
    }
  }

  const getInPersonCanvasPoint = (e) => {
    const c = inPersonCanvasRef.current
    if (!c) return null
    const r = c.getBoundingClientRect()
    const clientX = e.touches?.[0]?.clientX ?? e.clientX
    const clientY = e.touches?.[0]?.clientY ?? e.clientY
    if (clientX == null || clientY == null) return null
    const scaleX = c.width / r.width
    const scaleY = c.height / r.height
    return { x: (clientX - r.left) * scaleX, y: (clientY - r.top) * scaleY }
  }

  useLayoutEffect(() => {
    if (!inPersonSigModal.open) return
    const id = requestAnimationFrame(() => clearInPersonCanvas())
    return () => cancelAnimationFrame(id)
  }, [inPersonSigModal.open])

  const closeSignModal = () => {
    setActiveModal(null)
    setSigLinkTarget(null)
  }

  const getCategoryRootOrgIdForInsurer = useCallback((insurer) => {
    if (!insurer) return null
    const parentId = insurer?.parentOrgId != null ? Number(insurer.parentOrgId) : null
    if (lifeRootId != null && parentId === lifeRootId) return lifeRootId
    if (nonlifeRootId != null && parentId === nonlifeRootId) return nonlifeRootId
    if (petRootId != null && parentId === petRootId) return petRootId
    if (liabilityRootId != null && parentId === liabilityRootId) return liabilityRootId
    return categoryRootOrgId
  }, [lifeRootId, nonlifeRootId, petRootId, liabilityRootId, categoryRootOrgId])

  const getFlowCategoryLabelForInsurer = (insurer) => {
    const categoryId = getCategoryRootOrgIdForInsurer(insurer)
    if (categoryId != null && lifeRootId != null && categoryId === lifeRootId) return '생명보험'
    if (categoryId != null && nonlifeRootId != null && categoryId === nonlifeRootId) return '손해보험'
    if (categoryId != null && petRootId != null && categoryId === petRootId) return '펫보험'
    if (categoryId != null && liabilityRootId != null && categoryId === liabilityRootId) return '배상책임'
    return FLOW_CATEGORIES.find(v => v.key === flowCategory)?.label || ''
  }

  const selectedInsurersRef = useRef(selectedInsurers)
  selectedInsurersRef.current = selectedInsurers

  const sharedClaimExtraFieldsJson = useMemo(
    () => JSON.stringify(sharedClaimExtraFields),
    [sharedClaimExtraFields],
  )

  const sharedDocumentsKey = useMemo(
    () => JSON.stringify((sharedDocuments || []).map((d) => ({
      fileId: d?.fileId,
      pageCount: claimDocumentPageCount(d),
      sortOrder: d?.sortOrder,
    }))),
    [sharedDocuments],
  )

  useEffect(() => {
    let cancelled = false
    const objectUrls = []
    ;(async () => {
      const next = {}
      for (const doc of sharedDocuments || []) {
        const fileId = doc?.fileId
        if (!fileId) continue
        try {
          const res = await api.get(`/files/${encodeURIComponent(canonicalStoredFileIdForApiPath(fileId))}/preview`, { responseType: 'blob' })
          if (cancelled) return
          const contentType = String(res.headers?.['content-type'] || '').toLowerCase()
          const blob = new Blob([res.data], { type: contentType || 'application/octet-stream' })
          const url = URL.createObjectURL(blob)
          objectUrls.push(url)
          next[String(fileId)] = {
            url,
            kind: contentType.includes('image/')
              ? 'image'
              : contentType.includes('pdf')
                ? 'pdf'
                : filePreviewKind(doc),
          }
        } catch {
          next[String(fileId)] = { url: '', kind: filePreviewKind(doc) }
        }
      }
      if (!cancelled) setSharedDocumentPreviewUrls(next)
    })()
    return () => {
      cancelled = true
      objectUrls.forEach((url) => {
        try { URL.revokeObjectURL(url) } catch { /* ignore */ }
      })
    }
  }, [sharedDocuments])

  const selectedClaimDocumentTypeRows = useMemo(() => {
    const values = CLAIM_DOCUMENT_TYPES_BY_CLAIM_SECTION[claimDocumentSectionTitle] || CLAIM_DOCUMENT_TYPES_BY_CLAIM_SECTION.일반
    return values.map((value) => ({
      value,
      label: claimDocumentTypeLabel(value),
      ok: (sharedDocuments || []).some((d) => String(d.documentType || '').trim() === value),
    }))
  }, [claimDocumentSectionTitle, sharedDocuments])

  const buildFlowClaimApplicationPayload = useCallback(
    (insurer) => {
      const cat = getCategoryRootOrgIdForInsurer(insurer)
      const memo = [sharedFields.claimContent, sharedFields.accidentDetails]
        .map((s) => String(s || '').trim())
        .filter(Boolean)
        .join('\n\n')
      const additionalExtraFields = buildAdditionalExtraFieldsForInsurer(
        claimAdditionalFieldGroups,
        claimAdditionalFieldValues,
        insurer?.id,
        claimAdditionalFieldModeByGroupKey,
      )
      return {
        customerName: claimCustomerName.trim(),
        customerBirthDate: claimCustomerBirthDate || null,
        customerPhone: claimCustomerPhone || null,
        insurerOrgId: Number(insurer.id),
        categoryRootOrgId: cat != null ? Number(cat) : null,
        claimSection: null,
        claimItem: null,
        claimAmount: null,
        claimDate: claimDate || null,
        accidentDate: accidentDate || null,
        memo: memo || null,
        status: 'DRAFT',
        extraFields: {
          ...sharedClaimExtraFields,
          ...additionalExtraFields,
        },
        documents: takeClaimDocumentsWithinPageLimit(sharedDocuments, CLAIM_MAX_DOCUMENT_COUNT),
      }
    },
    [
      getCategoryRootOrgIdForInsurer,
      claimCustomerName,
      claimCustomerBirthDate,
      claimCustomerPhone,
      claimDate,
      accidentDate,
      sharedFields.claimContent,
      sharedFields.accidentDetails,
      sharedClaimExtraFields,
      claimAdditionalFieldGroups,
      claimAdditionalFieldModeByGroupKey,
      claimAdditionalFieldValues,
      sharedDocuments,
    ],
  )

  useEffect(() => {
    if (!supportedFlowCategory) return
    const name = String(claimCustomerName || '').trim()
    if (!name) return
    const insurers = selectedInsurersRef.current || []
    if (insurers.length === 0) return
    if (claimDate && accidentDate && claimDate < accidentDate) {
      setInsurerAppSyncError('청구일자는 사고일자보다 빠를 수 없습니다. 날짜를 수정하면 다시 저장됩니다.')
      return
    }
    if (String(claimCustomerPhone || '').trim() && !isValidKoreanClaimPhone(claimCustomerPhone)) {
      setInsurerAppSyncError(
        '연락처 형식이 올바르지 않습니다. 휴대폰·유선 번호를 숫자 9~11자리로 입력해 주세요. (아직 입력 중이면 완성 후 저장되거나, 비우면 저장 시 연락처 검사를 건너뜁니다.)',
      )
      return
    }

    let cancelled = false
    const t = window.setTimeout(() => {
      ;(async () => {
        setInsurerAppSyncWorking(true)
        setInsurerAppSyncError('')
        try {
          for (const insurer of insurers) {
            if (cancelled) return
            const key = String(insurer.id)
            const existing = appsRef.current[key]
            const basePayload = buildFlowClaimApplicationPayload(insurer)
            const payload = {
              ...basePayload,
              status: existing?.status && existing.status !== 'DRAFT' ? existing.status : basePayload.status,
            }
            const res = existing?.id
              ? await api.put(`/claims/applications/${existing.id}/content`, payload)
              : await api.post('/claims/applications', payload)
            const created = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
            if (cancelled) return
            if (created?.id) {
              appsRef.current = { ...appsRef.current, [key]: created }
              setCreatedClaimApplications((prev) => ({
                ...prev,
                [key]: created,
              }))
            }
          }
        } catch (e) {
          const body = e?.response?.data
          const msg = body?.message || body?.error || e?.message
          if (!cancelled) {
            setInsurerAppSyncError(typeof msg === 'string' ? msg : '청구서 자동 저장에 실패했습니다.')
          }
        } finally {
          if (!cancelled) {
            setInsurerAppSyncWorking(false)
            setTimeout(() => { refreshApplicationSummaries() }, 0)
          }
        }
      })()
    }, 850)

    return () => {
      cancelled = true
      window.clearTimeout(t)
    }
  }, [
    supportedFlowCategory,
    claimCustomerName,
    selectedInsurerIdsKey,
    sharedClaimExtraFieldsJson,
    sharedDocumentsKey,
    claimDate,
    accidentDate,
    claimCustomerPhone,
    buildFlowClaimApplicationPayload,
    refreshApplicationSummaries,
  ])

  const setSharedField = (key, value) => {
    setSharedFields((prev) => ({ ...prev, [key]: value }))
  }

  /** 우편번호·기본·상세 변경 시 통합 customerAddress 문자열 동기화(임시저장·수익자 동일 시 복사용) */
  useEffect(() => {
    setSharedFields((prev) => {
      const c = buildCustomerAddressLine(prev)
      if (c === (prev.customerAddress || '')) return prev
      return { ...prev, customerAddress: c }
    })
  }, [
    sharedFields.customerAddressZonecode,
    sharedFields.customerAddressRoad,
    sharedFields.customerAddressDetail,
  ])

  /** 피보험자 주민번호(앞 6자리·가능하면 7번째 성별코드)로 생년월일 자동 매핑 — 임시저장 복원·서버 동기화 시에도 동일 */
  useEffect(() => {
    const bd = birthDateYmdFromResidentDigits(sharedFields.customerResidentNumber)
    if (bd) setClaimCustomerBirthDate(bd)
  }, [sharedFields.customerResidentNumber])

  const openInsuredPostcode = useCallback(() => {
    setInsuredPostcodeError('')
    try {
      openDaumPostcode((data) => {
        if (!data) return
        const zone = String(data.zonecode || '').trim()
        const base =
          data.userSelectedType === 'R'
            ? String(data.roadAddress || '').trim()
            : String(data.jibunAddress || data.autoJibunAddress || data.roadAddress || '').trim()
        setSharedFields((p) => ({
          ...p,
          customerAddressZonecode: zone,
          customerAddressRoad: base,
          customerAddressDetail: '',
        }))
        queueMicrotask(() => insuredAddressDetailRef.current?.focus())
      })
    } catch {
      setInsuredPostcodeError('주소검색(카카오 우편번호) 스크립트를 불러오지 못했습니다. 새로고침 후 다시 시도해 주세요.')
    }
  }, [])

  useEffect(() => {
    const pt =
      sharedFields.accountPayoutType === 'PREPAID' || sharedFields.accountPayoutType === 'GENERAL' || sharedFields.accountPayoutType === 'AUTO_TRANSFER'
        ? sharedFields.accountPayoutType
        : sharedFields.bankAutoTransfer === 'Y'
          ? 'AUTO_TRANSFER'
          : 'GENERAL'
    if (pt === 'GENERAL') return
    if (!sharedFields.bankName && !sharedFields.accountNumber && !sharedFields.accountHolder) return
    setSharedFields((prev) => ({
      ...prev,
      bankName: '',
      accountNumber: '',
      accountHolder: '',
    }))
  }, [sharedFields.accountPayoutType, sharedFields.bankAutoTransfer, sharedFields.bankName, sharedFields.accountNumber, sharedFields.accountHolder])

  const toggleSharedSection = (key) => {
    let willOpen = false
    setExpandedSharedSections((prev) => {
      willOpen = prev[key] === false
      return { ...prev, [key]: willOpen ? true : !prev[key] }
    })
    if (willOpen) setActiveStep2Section(key)
  }

  const goNextClaimStep = useCallback(() => {
    setOpenStep((s) => {
      if (String(s) === '2') {
        if (beneficiaryRelationRequiredError) {
          setActiveStep2Section('beneficiary')
          setExpandedSharedSections((prev) => ({ ...prev, beneficiary: true }))
          show(beneficiaryRelationRequiredError, 'error')
          return s
        }
        setStep2MaxReachedIndex((prev) => Math.max(prev, visibleStep2Sections.length))
        setActiveStep2Section(null)
        setActiveModal('STEP2_SUMMARY')
        return s
      }
      if (String(s) === '3') {
        if (claimAdditionalFieldsLoading) {
          show('보험사별 입력 항목 확인이 끝난 뒤 진행해 주세요.', 'info')
          return s
        }
        if (claimAdditionalFieldRequiredError) {
          show(claimAdditionalFieldRequiredError, 'error')
          return s
        }
      }
      const seq = ['1', '2', '3', '4', '5', '6', '7']
      const i = seq.indexOf(String(s))
      if (i < 0 || i >= seq.length - 1) return s
      return seq[i + 1]
    })
  }, [
    beneficiaryRelationRequiredError,
    claimAdditionalFieldRequiredError,
    claimAdditionalFieldsLoading,
    show,
    visibleStep2Sections.length,
  ])

  const claimFlowNextStepButton = useMemo(
    () => (
      <Button
        type="button"
        variant="sgez"
        size="sm"
        onClick={goNextClaimStep}
        disabled={openStep === '7' || (openStep === '6' && (claimConsentLoading || !!claimConsentError || !claimConsentReady))}
      >
        다음 단계
      </Button>
    ),
    [claimConsentError, claimConsentLoading, claimConsentReady, goNextClaimStep, openStep],
  )

  const claimsCommonLocalDraftJson = useMemo(() => JSON.stringify({
    version: 2,
    flowCategory,
    flowCategoryKeys,
    selectedInsurerIds,
    claimCustomerName,
    claimCustomerRefId,
    claimCustomerBirthDate,
    claimCustomerPhone,
    claimDate,
    accidentDate,
    sharedFields,
    claimAdditionalFieldValues,
    claimAdditionalFieldModeByGroupKey,
    sharedDocuments,
    expandedSharedSections,
  }), [
    flowCategory,
    flowCategoryKeys,
    selectedInsurerIds,
    claimCustomerName,
    claimCustomerRefId,
    claimCustomerBirthDate,
    claimCustomerPhone,
    claimDate,
    accidentDate,
    sharedFields,
    claimAdditionalFieldValues,
    claimAdditionalFieldModeByGroupKey,
    sharedDocuments,
    expandedSharedSections,
  ])

  const applyClaimsCommonDraftRecord = useCallback((d) => {
    if (!d || typeof d !== 'object') return
    if (d.flowCategory) setFlowCategory(d.flowCategory)
    if (d.flowCategory) {
      setFlowCategoryKeys([d.flowCategory])
    }
    if (Array.isArray(d.selectedInsurerIds)) {
      setSelectedInsurerIds(
        CLAIM_INSURER_MULTI_SELECT_ENABLED
          ? d.selectedInsurerIds
          : d.selectedInsurerIds.slice(0, 1),
      )
    }
    if (typeof d.claimCustomerName === 'string') setClaimCustomerName(d.claimCustomerName)
    if (d.claimCustomerRefId != null && d.claimCustomerRefId !== '') {
      const refId = Number(d.claimCustomerRefId)
      setClaimCustomerRefId(Number.isFinite(refId) && refId > 0 ? refId : null)
    } else if (d.claimCustomerRefId === null) {
      setClaimCustomerRefId(null)
    }
    if (typeof d.claimCustomerBirthDate === 'string') setClaimCustomerBirthDate(d.claimCustomerBirthDate)
    if (typeof d.claimCustomerPhone === 'string') setClaimCustomerPhone(d.claimCustomerPhone)
    if (typeof d.claimDate === 'string') setClaimDate(d.claimDate)
    if (typeof d.accidentDate === 'string') setAccidentDate(d.accidentDate)
    const legacyMemo = typeof d.claimMemo === 'string' ? d.claimMemo.trim() : ''
    if (d.sharedFields && typeof d.sharedFields === 'object') {
      setSharedFields((prev) => {
        const merged = normalizeClaimsSharedFieldsAfterMerge({ ...prev, ...d.sharedFields })
        if (legacyMemo && !(String(merged.accidentDetails || '').trim()) && !(String(merged.claimContent || '').trim())) {
          merged.accidentDetails = legacyMemo
        } else if (legacyMemo && !(String(merged.accidentDetails || '').trim())) {
          merged.accidentDetails = legacyMemo
        }
        return merged
      })
    } else if (legacyMemo) {
      setSharedFields((prev) => ({
        ...prev,
        accidentDetails: (String(prev.accidentDetails || '').trim() || legacyMemo),
      }))
    }
    if (d.claimAdditionalFieldValues && typeof d.claimAdditionalFieldValues === 'object') {
      setClaimAdditionalFieldValues(d.claimAdditionalFieldValues)
    }
    if (d.claimAdditionalFieldModeByGroupKey && typeof d.claimAdditionalFieldModeByGroupKey === 'object') {
      setClaimAdditionalFieldModeByGroupKey(d.claimAdditionalFieldModeByGroupKey)
    }
    if (Array.isArray(d.sharedDocuments)) setSharedDocuments(normalizeClaimDocumentsForFlow(d.sharedDocuments))
    if (d.expandedSharedSections && typeof d.expandedSharedSections === 'object') {
      setExpandedSharedSections((prev) => ({ ...prev, ...d.expandedSharedSections }))
    }
  }, [])

  const applyCustomerSummaryToClaimForm = useCallback((customerRefId, data) => {
    const id = Number(customerRefId)
    if (!Number.isFinite(id) || id <= 0) return
    const { name, phone, birthDate, sharedPatch } = buildClaimFieldsFromCustomerSummary(data)
    setClaimCustomerRefId(id)
    if (name) setClaimCustomerName(name)
    if (phone) setClaimCustomerPhone(phone)
    if (birthDate) setClaimCustomerBirthDate(birthDate)
    if (Object.keys(sharedPatch).length > 0) {
      setSharedFields((prev) => normalizeClaimsSharedFieldsAfterMerge({ ...prev, ...sharedPatch }))
    }
  }, [])

  const loadCrmCustomersForPicker = useCallback(async (query = crmPickerQuery) => {
    setCrmPickerLoading(true)
    setCrmPickerErr('')
    try {
      const q = String(query || '').trim()
      const res = await fetchManagedCustomersForPicker({ q, page: 0, size: 20 })
      setCrmPickerList(normalizeManagedCustomerItems(res))
    } catch (e) {
      setCrmPickerErr(managedCustomerSearchErrorMessage(e))
      setCrmPickerList([])
    } finally {
      setCrmPickerLoading(false)
    }
  }, [crmPickerQuery])

  const applyCrmCustomerRowToClaimForm = useCallback((row) => {
    const customerRefId = customerRefIdFromManagedCustomer(row)
    if (!customerRefId) {
      setCrmPickerErr('선택한 고객 정보를 확인할 수 없습니다. 고객관리에서 다시 확인해 주세요.')
      return
    }
    applyCustomerSummaryToClaimForm(customerRefId, row)
    setCrmPickerOpen(false)
    show('고객관리 정보를 피보험자 입력란에 반영했습니다.', 'success')
  }, [applyCustomerSummaryToClaimForm, show])

  const openCustomerPicker = useCallback(() => {
    const q = String(claimCustomerName || '').trim()
    setCrmPickerQuery(q)
    setCrmPickerOpen(true)
    void loadCrmCustomersForPicker(q)
  }, [claimCustomerName, loadCrmCustomersForPicker])

  useEffect(() => {
    if (crmPrefillFromUrlRef.current || resumeApplicationId) return
    const sp = new URLSearchParams(location.search)
    const raw = sp.get('customerRefId')
    if (!raw) return
    const id = Number(String(raw).trim())
    if (!Number.isFinite(id) || id <= 0) return
    crmPrefillFromUrlRef.current = true
    ;(async () => {
      try {
        const res = await api.get(`/customer-management/customers/${id}/summary`, { __silent: true })
        const data = res?.data?.data ?? res?.data
        if (!data) {
          crmPrefillFromUrlRef.current = false
          return
        }
        applyCustomerSummaryToClaimForm(id, data)
        setOpenStep('2')
        show('고객관리에서 피보험자 정보를 불러왔습니다.', 'success')
        const next = new URLSearchParams(location.search)
        next.delete('customerRefId')
        const nextSearch = next.toString()
        navigate(
          { pathname: location.pathname, search: nextSearch ? `?${nextSearch}` : '' },
          { replace: true },
        )
      } catch {
        crmPrefillFromUrlRef.current = false
      }
    })()
  }, [location.pathname, location.search, resumeApplicationId, applyCustomerSummaryToClaimForm, navigate, show])

  const refreshClaimsDraftHints = useCallback(async () => {
    let browser = false
    try {
      browser = localStorage.getItem(CLAIMS_FLOW_DRAFT_EXPLICIT_KEY) === '1' && !!readClaimsFlowLocalDraft()
    } catch {
      browser = false
    }
    setBrowserDraftAvailable(browser)

    if (!isOperator) {
      setServerFlowDraftHint(null)
      return
    }
    try {
      const res = await api.get('/claims/flow-draft')
      const wrap = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
      if (wrap?.exists && wrap.updatedAt) {
        setServerFlowDraftHint({
          updatedAt: wrap.updatedAt,
          retentionDays: wrap.retentionDays ?? 7,
        })
      } else {
        setServerFlowDraftHint(null)
      }
    } catch {
      setServerFlowDraftHint(null)
    }
  }, [isOperator])

  const applyStoredClaimsCommonDraft = useCallback(async () => {
    if (isOperator) {
      try {
        const res = await api.get('/claims/flow-draft')
        const wrap = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
        if (wrap?.exists && wrap.payload && typeof wrap.payload === 'object') {
          applyClaimsCommonDraftRecord(wrap.payload)
          show('서버에 저장된 공통 입력을 불러왔습니다.', 'success')
          await refreshClaimsDraftHints()
          return
        }
      } catch {
        /* 서버 조회 실패 시 브라우저에 저장된 초안만 시도 */
      }
    }
    const d = readClaimsFlowLocalDraft()
    if (d) {
      applyClaimsCommonDraftRecord(d)
      show('브라우저에 저장된 공통 입력을 불러왔습니다.', 'success')
      await refreshClaimsDraftHints()
    } else {
      show('불러올 저장된 공통 입력이 없습니다.', 'info')
    }
  }, [isOperator, applyClaimsCommonDraftRecord, refreshClaimsDraftHints, show])

  useEffect(() => {
    try {
      const sp = new URLSearchParams(window.location.search)
      if (sp.get('resumeApplicationId')) {
        setLocalCommonDraftReady(true)
        return
      }
    } catch {
      /* ignore */
    }
    try {
      const explicit = localStorage.getItem(CLAIMS_FLOW_DRAFT_EXPLICIT_KEY) === '1'
      setBrowserDraftAvailable(explicit && !!readClaimsFlowLocalDraft())
    } catch {
      setBrowserDraftAvailable(false)
    }
    setLocalCommonDraftReady(true)
  }, [])

  /** 운영자: 서버 공통 초안 존재 여부만 표시(자동 병합하지 않음) */
  useEffect(() => {
    if (!isOperator || !localCommonDraftReady) return
    try {
      const sp = new URLSearchParams(location.search)
      if (sp.get('resumeApplicationId')) return
    } catch {
      /* ignore */
    }
    let cancelled = false
    ;(async () => {
      try {
        const res = await api.get('/claims/flow-draft')
        const wrap = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
        if (cancelled) return
        if (wrap?.exists && wrap.updatedAt) {
          setServerFlowDraftHint({
            updatedAt: wrap.updatedAt,
            retentionDays: wrap.retentionDays ?? 7,
          })
        } else {
          setServerFlowDraftHint(null)
        }
      } catch {
        if (!cancelled) setServerFlowDraftHint(null)
      }
    })()
    return () => { cancelled = true }
  }, [isOperator, localCommonDraftReady])

  useEffect(() => {
    if (!resumeApplicationId || !localCommonDraftReady) return
    if (orgOptions.length === 0) return
    let cancelled = false
    ;(async () => {
      try {
        const res = await api.get(`/claims/applications/${resumeApplicationId}`)
        const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
        if (cancelled || !payload?.id) return
        const catId = payload.categoryRootOrgId != null ? Number(payload.categoryRootOrgId) : null
        let nextFlow = 'NONLIFE'
        if (lifeRootId != null && catId === lifeRootId) nextFlow = 'LIFE'
        else if (nonlifeRootId != null && catId === nonlifeRootId) nextFlow = 'NONLIFE'
        else if (petRootId != null && catId === petRootId) nextFlow = 'PET'
        else if (liabilityRootId != null && catId === liabilityRootId) nextFlow = 'LIABILITY'

        const ex = claimApplicationExtraFields(payload)
        const partial = sharedFieldsPartialFromStoredClaimExtra(ex, payload.memo)
        const mergedShared = normalizeClaimsSharedFieldsAfterMerge({ ...createInitialSharedFields(), ...partial })

        const oid = payload.insurerOrgId
        if (oid == null) {
          show('청구서에 보험사 정보가 없어 불러오지 못했습니다.', 'error')
          navigate('/support/claims', { replace: true })
          return
        }

        const docsRaw = Array.isArray(payload.documents) ? payload.documents : []
        const mappedDocs = docsRaw.map((doc, idx) => ({
          fileId: doc.fileId,
          documentType: doc.documentType || 'ETC',
          label: doc.label || `첨부 ${idx + 1}`,
          sortOrder: doc.sortOrder ?? idx,
          pageCount: claimDocumentPageCount(doc),
        }))

        setFlowCategory(nextFlow)
        setFlowCategoryKeys([nextFlow])
        setClaimCustomerName(payload.customerName || '')
        setClaimCustomerBirthDate(
          payload.customerBirthDate ? String(payload.customerBirthDate).slice(0, 10) : '',
        )
        setClaimCustomerPhone(payload.customerPhone || '')
        setClaimDate(
          payload.claimDate != null && String(payload.claimDate).trim() !== ''
            ? String(payload.claimDate).slice(0, 10)
            : todayYmdLocal(),
        )
        setAccidentDate(payload.accidentDate ? String(payload.accidentDate).slice(0, 10) : '')
        setSharedFields(mergedShared)
        setSharedDocuments(normalizeClaimDocumentsForFlow(mappedDocs))
        setCreatedClaimApplications({ [String(oid)]: payload })
        setSelectedInsurerIds([oid])
        setOpenStep('2')

        navigate('/support/claims', { replace: true })
        show('임시저장 청구를 불러왔습니다. 청구 정보·첨부·서명 단계를 이어서 진행할 수 있습니다.', 'success')
      } catch {
        if (!cancelled) {
          show('임시저장 청구를 불러오지 못했습니다.', 'error')
          navigate('/support/claims', { replace: true })
        }
      }
    })()
    return () => { cancelled = true }
  }, [
    resumeApplicationId,
    localCommonDraftReady,
    orgOptions.length,
    lifeRootId,
    nonlifeRootId,
    petRootId,
    liabilityRootId,
    navigate,
    show,
  ])

  const persistClaimsCommonDraftNow = useCallback(async () => {
    try {
      localStorage.setItem(CLAIMS_FLOW_LOCAL_DRAFT_KEY, claimsCommonLocalDraftJson)
      localStorage.setItem(CLAIMS_FLOW_DRAFT_EXPLICIT_KEY, '1')
    } catch {
      show('임시저장에 실패했습니다.', 'error')
      return
    }
    if (isOperator) {
      try {
        const payload = JSON.parse(claimsCommonLocalDraftJson)
        await api.put('/claims/flow-draft', { payload })
        show('공통 입력을 임시저장했습니다. 7일간 서버에 보관됩니다.', 'success')
      } catch {
        show('이 브라우저에는 저장되었으나 서버 동기화에 실패했습니다. 네트워크 확인 후 다시 시도해 주세요.', 'info')
      }
      void refreshClaimsDraftHints()
      return
    }
    show('공통 입력을 이 브라우저에 임시저장했습니다. 아직 보험사로 발송되지 않았습니다.', 'success')
    void refreshClaimsDraftHints()
  }, [claimsCommonLocalDraftJson, isOperator, show, refreshClaimsDraftHints])

  const resetClaimsFlowInput = useCallback(async () => {
    const ok = window.confirm(
      'STEP 1·2·3 공통 입력·첨부, 보험사 선택, 이 세션에서 연결된 청구서 목록을 모두 초기화합니다.\n'
      + '브라우저에 저장된 초안도 지웁니다.'
      + (isOperator ? '\n(운영자) 서버 공통 임시저장도 삭제합니다.' : ''),
    )
    if (!ok) return
    try {
      localStorage.removeItem(CLAIMS_FLOW_LOCAL_DRAFT_KEY)
      localStorage.removeItem(CLAIMS_FLOW_DRAFT_EXPLICIT_KEY)
    } catch {
      /* ignore */
    }

    if (isOperator) {
      try {
        await api.delete('/claims/flow-draft')
      } catch {
        show('서버 임시저장 삭제에 실패했습니다. 브라우저 초안은 지워졌으며, 새로고침 시 서버 초안이 다시 덮어쓸 수 있습니다.', 'info')
      }
    }

    setFlowCategory('NONLIFE')
    setFlowCategoryKeys(GROUPABLE_FLOW_CATEGORY_KEYS)
    setOpenStep('1')
    setSelectedInsurerIds([])
    setClaimCustomerName('')
    setClaimCustomerRefId(null)
    setClaimCustomerBirthDate('')
    setClaimCustomerPhone('')
    setClaimDate(todayYmdLocal())
    setAccidentDate('')
    setSharedFields(createInitialSharedFields())
    setSharedDocuments([])
    setSharedDocumentsError('')
    setExpandedSharedSections(createInitialExpandedSharedSections())
    setActiveStep2Section('insured')
    setStep2MaxReachedIndex(0)
    setCreatedClaimApplications({})
    appsRef.current = {}
    setInsurerAppSyncError('')
    setInsurerAppSyncWorking(false)

    setBrowserDraftAvailable(false)
    setServerFlowDraftHint(null)

    show('보험금 청구 입력을 초기화했습니다.', 'success')
  }, [isOperator, show])

  useEffect(() => {
    const selectedKeys = new Set(selectedInsurerIds.map(String))
    setCreatedClaimApplications((prev) => {
      const next = Object.fromEntries(Object.entries(prev).filter(([key]) => selectedKeys.has(key)))
      const sameLength = Object.keys(next).length === Object.keys(prev).length
      return sameLength ? prev : next
    })
  }, [selectedInsurerIds])

  useEffect(() => {
    if (!supportedFlowCategory || faxSettingTargetInsurers.length === 0) {
      setClaimTemplateByOrgId({})
      setClaimTemplateStatusByOrgId({})
      setClaimTemplateStatusLoading(false)
      return
    }
    let cancelled = false
    setClaimTemplateStatusLoading(true)
    const q = new URLSearchParams()
    faxSettingTargetInsurers.forEach((o) => q.append('insurerOrgIds', o.id))
    ;(async () => {
      try {
        const res = await api.get(`/claims/form-templates/statuses?${q.toString()}`)
        const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
        const rows = Array.isArray(payload) ? payload : []
        if (cancelled) return
        const next = {}
        const nextStatus = {}
        for (const row of rows) {
          const oid = row?.insurerOrgId
          if (oid == null) continue
          const key = String(oid)
          const hasTemplate = !!row.hasTemplate
          const claimEnabled = !!row.claimEnabled
          next[key] = hasTemplate && claimEnabled
          nextStatus[key] = {
            hasTemplate,
            claimEnabled,
            reason: row.reason || null,
          }
        }
        for (const o of faxSettingTargetInsurers) {
          const k = String(o.id)
          if (!(k in next)) next[k] = false
          if (!(k in nextStatus)) nextStatus[k] = { hasTemplate: false, claimEnabled: false, reason: 'TEMPLATE_NOT_FOUND' }
        }
        setClaimTemplateByOrgId(next)
        setClaimTemplateStatusByOrgId(nextStatus)
      } catch {
        if (!cancelled) {
          const fallback = {}
          const fallbackStatus = {}
          faxSettingTargetInsurers.forEach((o) => {
            fallback[String(o.id)] = false
            fallbackStatus[String(o.id)] = { hasTemplate: false, claimEnabled: false, reason: 'STATUS_LOAD_FAILED' }
          })
          setClaimTemplateByOrgId(fallback)
          setClaimTemplateStatusByOrgId(fallbackStatus)
          show('청구 서식 활성 여부를 확인하지 못했습니다. 잠시 후 새로고침하거나 네트워크를 확인해 주세요.', 'warning')
        }
      } finally {
        if (!cancelled) setClaimTemplateStatusLoading(false)
      }
    })()
    return () => { cancelled = true }
    // show는 토스트용으로만 사용 — 의존성에 넣으면 불필요한 재조회가 생길 수 있음
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 조회 대상 보험사·지원 카테고리만 반응
  }, [faxSettingTargetInsurers, supportedFlowCategory])

  useEffect(() => {
    if (claimTemplateStatusLoading) return
    setSelectedInsurerIds((prev) => {
      const next = prev.filter((id) => claimTemplateByOrgId[String(id)] !== false)
      return next.length === prev.length ? prev : next
    })
  }, [claimTemplateByOrgId, claimTemplateStatusLoading])

  /** STEP 1 보험사 그리드 뱃지·팩스 모달 — 현재 카테고리와 선택 유지된 보험사까지 조회 */
  useEffect(() => {
    if (!supportedFlowCategory || faxSettingTargetInsurers.length === 0) {
      setInsurerFaxByOrgId({})
      return
    }
    const q = new URLSearchParams()
    faxSettingTargetInsurers.forEach((o) => q.append('insurerOrgIds', o.id))
    let cancelled = false
    ;(async () => {
      try {
        const res = await api.get(`/claims/insurer-fax-settings?${q.toString()}`)
        const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
        const list = Array.isArray(payload) ? payload : []
        const next = {}
        for (const row of list) {
          const oid = row?.insurerOrgId
          if (oid == null) continue
          next[String(oid)] = {
            claimFaxNumber: row.claimFaxNumber || '',
            claimFaxRoutingJson: row.claimFaxRoutingJson ?? null,
            virtualFaxRequired: !!row.virtualFaxRequired,
          }
        }
        if (!cancelled) setInsurerFaxByOrgId(next)
      } catch {
        if (!cancelled) setInsurerFaxByOrgId({})
      }
    })()
    return () => { cancelled = true }
  }, [faxSettingTargetInsurers, supportedFlowCategory])

  const loadGuideDetail = async (id) => {
    if (!id) return
    setGuideDetailLoading(true)
    setSelectedGuide(null)
    try {
      const res = await api.get(`/claims/guides/${id}`)
      const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
      setSelectedGuide(payload)
    } catch {
      setSelectedGuide({ error: true })
    } finally {
      setGuideDetailLoading(false)
    }
  }

  useEffect(() => {
    if (activeModal !== 'GUIDE') return
    if (!supportedFlowCategory || guidesLoading || guideDetailLoading || selectedGuide) return
    const firstGuideId = mergedGuides[0]?.id
    if (firstGuideId) loadGuideDetail(firstGuideId)
  }, [activeModal, supportedFlowCategory, guidesLoading, guideDetailLoading, selectedGuide, mergedGuides])

  function parseFilenameFromDisposition(cd) {
    if (!cd) return null
    // attachment; filename="foo.pdf"
    const m = String(cd).match(/filename="([^"]+)"/i)
    return m && m[1] ? m[1] : null
  }

  const downloadFile = async (fileId) => {
    if (!fileId) return
    try {
      const res = await api.get(`/files/${encodeURIComponent(canonicalStoredFileIdForApiPath(fileId))}/download`, { responseType: 'blob' })
      const filename = parseFilenameFromDisposition(res?.headers?.['content-disposition']) || fileId
      const url = URL.createObjectURL(res.data)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      a.click()
      URL.revokeObjectURL(url)
    } catch {
      // ignore
    }
  }

  const openSharedDocumentPreview = (index) => {
    if (!sharedDocuments[index]) return
    setSharedDocumentPreviewIndex(index)
  }

  const moveSharedDocumentPreview = (direction) => {
    if (!sharedDocuments.length) return
    setSharedDocumentPreviewIndex((prev) => {
      const current = Number.isInteger(prev) ? prev : 0
      return (current + direction + sharedDocuments.length) % sharedDocuments.length
    })
  }

  const uploadSharedDocuments = async (fileList) => {
    const files = Array.from(fileList || []).filter((f) => f != null)
    if (files.length === 0) return
    const currentPageCount = claimDocumentsPageCount(sharedDocuments)
    const remaining = Math.max(0, CLAIM_MAX_DOCUMENT_COUNT - currentPageCount)
    if (remaining <= 0) {
      setSharedDocumentsError(`첨부 서류는 최대 ${CLAIM_MAX_DOCUMENT_COUNT}장까지 가능합니다.`)
      return
    }
    let prepared
    try {
      prepared = await prepareClaimDocumentFiles(files, {
        initialPageCount: currentPageCount,
        maxPageCount: CLAIM_MAX_DOCUMENT_COUNT,
      })
    } catch {
      setSharedDocumentsError('PDF 페이지 수를 확인할 수 없습니다.')
      return
    }
    const acceptedFiles = prepared.accepted.map((item, index) => ({ ...item, index }))
    const skippedCount = prepared.skippedByPageLimit
    if (acceptedFiles.length === 0) {
      setSharedDocumentsError(`첨부 서류는 최대 ${CLAIM_MAX_DOCUMENT_COUNT}장까지 가능합니다.`)
      return
    }
    if (skippedCount > 0) {
      show(`첨부 서류는 최대 ${CLAIM_MAX_DOCUMENT_COUNT}장까지 가능합니다. 초과 ${skippedCount}개는 제외했습니다.`, 'info')
    }
    setSharedDocumentsUploading(true)
    setSharedDocumentsError('')
    const uploaded = []
    let failed = 0
    const uploadOne = async (item) => {
      try {
        const { file, index, pageCount } = item
        const formData = new FormData()
        formData.append('file', file)
        const res = await api.post('/files/upload/claim-document', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
        const fileId = payload?.fileId || payload?.id
        if (!fileId) throw new Error('UPLOAD_FAILED')
        uploaded.push({
          index,
          fileId,
          documentType: 'ETC',
          label: file.name,
          pageCount: Number(payload?.pageCount) > 0 ? Number(payload.pageCount) : pageCount,
        })
      } catch {
        failed += 1
      }
    }
    const queue = [...acceptedFiles]
    const workers = Array.from({ length: Math.min(4, queue.length) }, async () => {
      while (queue.length > 0) {
        const item = queue.shift()
        if (item) await uploadOne(item)
      }
    })
    await Promise.all(workers)
    if (uploaded.length > 0) {
      setSharedDocuments((prev) => {
        const toAdd = []
        let pages = claimDocumentsPageCount(prev)
        for (const { index, ...doc } of uploaded.sort((a, b) => a.index - b.index)) {
          const pageCount = claimDocumentPageCount(doc)
          if (pages + pageCount > CLAIM_MAX_DOCUMENT_COUNT) break
          toAdd.push({
            ...doc,
            pageCount,
            sortOrder: prev.length + toAdd.length,
          })
          pages += pageCount
        }
        return [...prev, ...toAdd]
      })
    }
    if (failed > 0) {
      setSharedDocumentsError(
        failed === acceptedFiles.length
          ? '공통 첨부서류 업로드에 실패했습니다.'
          : `${failed}개 파일 업로드에 실패했습니다. (${acceptedFiles.length - failed}개 성공)`,
      )
    }
    setSharedDocumentsUploading(false)
  }

  const sendSignatureLink = async () => {
    setSigSending(true)
    setSigError(null)
    setSigResult(null)
    try {
      const signerRole = sigLinkTarget?.signerRole === 'CONTRACT_HOLDER'
        ? 'CONTRACT_HOLDER'
        : sigLinkTarget?.signerRole === 'BENEFICIARY'
          ? 'BENEFICIARY'
          : 'INSURED'
      const rawApplicationIds = sigLinkTarget?.applicationIds?.length
        ? sigLinkTarget.applicationIds
        : sigLinkTarget?.applicationId != null
          ? [sigLinkTarget.applicationId]
          : []
      const applicationIds = rawApplicationIds
        .map((applicationId) => Number(applicationId))
        .filter((applicationId) => Number.isFinite(applicationId) && applicationId > 0)
      const attachAction = applicationIds.length > 0
        ? JSON.stringify({
          type: 'CLAIM_APPLICATION_SIGNATURE_ATTACH',
          applicationIds,
          signerRole,
        })
        : null
      const createRes = await api.post('/claims/signatures', {
        mode: 'REMOTE',
        customerName: sigCustomerName || null,
        customerPhone: sigCustomerPhone || null,
        expiresHours: 72,
        onCompleteActionJson: attachAction,
        signerRole,
      })
      const created = (createRes?.data && createRes.data.data !== undefined) ? createRes.data.data : createRes.data
      const id = created?.id
      if (!id) throw new Error('CREATE_FAILED')

      const insurersForSummary = sigLinkTarget?.insurers?.length
        ? sigLinkTarget.insurers
        : sigLinkTarget?.insurer
        ? [sigLinkTarget.insurer]
        : selectedInsurers
      const reqRes = await api.post(`/claims/signatures/${id}/request`, {
        channel: sigChannel,
        insurerSummary: buildInsurerSummary(insurersForSummary),
      })
      const sent = (reqRes?.data && reqRes.data.data !== undefined) ? reqRes.data.data : reqRes.data
      const urlFixed = sent?.url != null ? rewritePublicClaimLinkToCurrentOrigin(sent.url) : sent?.url
      setSigResult({ signatureId: id, ...sent, ...(urlFixed !== undefined ? { url: urlFixed } : {}) })
      if (applicationIds.length > 0) {
        const slot = signatureSlotFromSignerRole(signerRole)
        setPendingRemoteSigsByAppId((p) => ({
          ...p,
          ...applicationIds.reduce((acc, applicationId) => {
            const appKey = String(applicationId)
            acc[appKey] = { ...(p[appKey] || {}), [slot]: id }
            return acc
          }, {}),
        }))
      }
    } catch (e) {
      setSigError('발송에 실패했습니다. (휴대폰 번호/템플릿/발송 설정을 확인해 주세요)')
    } finally {
      setSigSending(false)
    }
  }

  const sendIntakeLink = async () => {
    setIntakeSending(true)
    setIntakeError(null)
    setIntakeResult(null)
    try {
      const targets = intakeSelectedInsurerOptions
      if (targets.length === 0) throw new Error('NO_INSURER')
      const residentDigits = normalizeDigits(sharedFields.customerResidentNumber)
      const insuredResidentNo = residentDigits.length >= 13
        ? `${residentDigits.slice(0, 6)}-${residentDigits.slice(6, 13)}`
        : (String(sharedFields.customerResidentNumber || '').trim() || null)
      const plannerPrefill = {
        customerBirthDate: claimCustomerBirthDate || null,
        claimDate: claimDate || null,
        accidentDate: accidentDate || null,
        memo: [sharedFields.claimContent, sharedFields.accidentDetails].map((s) => String(s || '').trim()).filter(Boolean).join('\n\n') || null,
        insuredName: claimCustomerName || null,
        contractorName: claimCustomerName || null,
        insuredPhone: claimCustomerPhone || null,
        insuredResidentNo,
        extraFields: { ...sharedClaimExtraFields },
      }
      const results = []
      let failed = 0
      for (const insurer of targets) {
        try {
          const categoryRootOrgIdForInsurer = getCategoryRootOrgIdForInsurer(insurer)
          const createRes = await api.post('/claims/intake-sessions', {
            insurerOrgId: Number(insurer.id),
            categoryRootOrgId: categoryRootOrgIdForInsurer ? Number(categoryRootOrgIdForInsurer) : null,
            customerName: intakeCustomerName || null,
            customerPhone: intakeCustomerPhone || null,
            plannerPrefill,
          })
          const created = (createRes?.data && createRes.data.data !== undefined) ? createRes.data.data : createRes.data
          const id = created?.id
          if (!id) throw new Error('CREATE_FAILED')

          const reqRes = await api.post(`/claims/intake-sessions/${id}/request`, {
            channel: intakeChannel,
            insurerSummary: buildInsurerSummary([insurer]),
          })
          const sent = (reqRes?.data && reqRes.data.data !== undefined) ? reqRes.data.data : reqRes.data
          const urlFixed = sent?.url != null ? rewritePublicClaimLinkToCurrentOrigin(sent.url) : sent?.url
          results.push({
            sessionId: id,
            insurerOrgId: insurer.id,
            insurerName: insurer.name || '',
            ...sent,
            ...(urlFixed !== undefined ? { url: urlFixed } : {}),
          })
        } catch {
          failed += 1
        }
      }
      if (results.length === 0) throw new Error('CREATE_FAILED')
      if (failed > 0) {
        setIntakeError(`${failed}개 보험사 링크 발송에 실패했습니다.`)
      }
      setIntakeResult({
        results,
        sessionId: results[0]?.sessionId,
        channel: results.length === 1 ? results[0]?.channel : `${results.length}건`,
        url: results[0]?.url,
      })
    } catch {
      setIntakeError('청구 링크 발송에 실패했습니다. (보험사/연락처/알림 설정을 확인해 주세요)')
    } finally {
      setIntakeSending(false)
    }
  }

  const buildClaimConsentPayloadForApplication = (application) => {
    if (application?.claimConsentRecorded && !claimConsentReady) return null
    if (!claimConsentReady) return null
    return {
      bundleVersion: buildClaimConsentBundleVersion(claimConsentDocs),
      consentItems: claimConsentDocs.map((doc) => ({
        docCode: doc.docCode,
        versionLabel: doc.versionLabel,
        accepted: !!claimConsentChecks?.[doc.docCode],
      })),
      agentConfirmed: claimAgentConfirmed,
      consentChannel: 'WEB',
    }
  }

  const scheduleClaimFaxApplication = async ({ application, insurer, receiveNum, receiveName, title }) => {
    if (!application?.id) throw new Error('application id required')
    const res = await api.post(`/claims/applications/${application.id}/schedule-fax`, {
      receiveNum,
      receiveName,
      title: title || '보험금청구서',
      delayMinutes: CLAIM_FAX_DELAY_MINUTES,
      claimConsent: buildClaimConsentPayloadForApplication(application),
    })
    const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
    const insurerId = insurer?.id != null ? String(insurer.id) : null
    if (insurerId) {
      setCreatedClaimApplications((prev) => ({
        ...prev,
        [insurerId]: payload,
      }))
    }
    return payload
  }

  const handleScheduleClaimFax = async () => {
    if (!faxScheduleModal.application?.id) return
    if (!faxScheduleForm.receiveNum || !faxScheduleForm.receiveName) {
      show('팩스 수신번호와 수신자명을 입력해주세요.', 'error')
      return
    }
    const sid = faxScheduleModal.insurer?.id != null ? String(faxScheduleModal.insurer.id) : ''
    const cfg = sid ? insurerFaxByOrgId[sid] : null
    if (cfg?.virtualFaxRequired) {
      const a = normalizeDigits(faxScheduleForm.receiveNum)
      const at = accidentTypeFromApplication(faxScheduleModal.application)
      const b = normalizeDigits(resolveListedClaimFaxNumber(cfg, at))
      if (b && a === b) {
        show('이 보험사는 가상팩스번호가 필요합니다. 발급받은 번호를 입력해 주세요.', 'error')
        return
      }
    }
    setFaxScheduling(true)
    try {
      await scheduleClaimFaxApplication({
        application: faxScheduleModal.application,
        insurer: faxScheduleModal.insurer,
        receiveNum: faxScheduleForm.receiveNum,
        receiveName: faxScheduleForm.receiveName,
        title: faxScheduleForm.title || '보험금청구서',
      })
      setFaxScheduleModal({ open: false, application: null, insurer: null })
      setFaxScheduleForm({ receiveNum: '', receiveName: '', title: '' })
      setOpenStep('7')
      const q = bulkClaimRemainderRef.current
      if (q && q.length > 0) {
        const next = q.shift()
        show(
          `청구서류 발송이 예약되었습니다. 다음: ${next.insurer?.name || '보험사'} — 수신번호를 확인해 주세요.`,
          'success',
        )
        window.setTimeout(() => {
          openFaxScheduleModalInternal(next.application, next.insurer)
        }, 200)
      } else {
        bulkClaimRemainderRef.current = null
        show('청구서류 발송이 예약되었습니다. 약 1분 후 보험사 팩스로 전송되며, 전송 시작 전까지 취소할 수 있습니다.', 'success')
        navigate('/support/claims/applications')
      }
    } catch (e) {
      bulkClaimRemainderRef.current = null
      const message = e?.response?.data?.message || e?.response?.data?.error?.message || ''
      if (String(message).includes('FAX_RESULT_PENDING')) {
        show('이전 팩스 발송 결과가 아직 확정되지 않았습니다. 최종 결과를 확인한 뒤 다시 시도해 주세요.', 'error')
      } else {
        show(message || '보험사 팩스 발송 요청에 실패했습니다. 수신번호와 청구서류를 확인한 뒤 다시 시도해 주세요.', 'error')
      }
    } finally {
      setFaxScheduling(false)
    }
  }

  const openBulkClaimScheduling = async () => {
    const eligible = bulkClaimEligibleRows
    if (claimAdditionalFieldRequiredError) {
      setOpenStep('3')
      show(claimAdditionalFieldRequiredError, 'error')
      return
    }
    if (claimConsentLoading || claimConsentError || eligible.some(({ application }) => !claimConsentSatisfiedForApplication(application))) {
      setOpenStep('6')
      show('STEP 6에서 보험금청구 필수 동의를 먼저 확인해 주세요.', 'error')
      return
    }
    if (eligible.length === 0) {
      show(
        '전체 청구로 진행할 수 있는 보험사가 없습니다. 청구서가 저장되어 있고 서명이 완료됐으며, 이미 발송 대기 중이거나 이전 팩스 결과 확정 대기 중인 건은 제외됩니다.',
        'info',
      )
      return
    }
    const names = eligible.map(({ insurer }) => insurer.name).join('\n· ')
    const preparedRows = eligible.map((row) => {
      const cfg = insurerFaxByOrgId[String(row.insurer.id)]
      const at = accidentTypeFromApplication(row.application)
      const defaultNum = resolveListedClaimFaxNumber(cfg, at) || cfg?.claimFaxNumber || ''
      const receiveNum = cfg?.virtualFaxRequired
        ? (row.application?.faxReceiveNum || '')
        : (defaultNum || row.application?.faxReceiveNum || '')
      return {
        ...row,
        receiveNum,
        needsManualInput: !!cfg?.virtualFaxRequired || !String(receiveNum || '').trim(),
      }
    })
    const manualRows = preparedRows.filter((row) => row.needsManualInput)
    const autoRows = preparedRows.filter((row) => !row.needsManualInput)
    if (
      !window.confirm(
        `아래 ${eligible.length}곳의 청구를 진행합니다.\n\n· ${names}\n\n수신번호가 확인된 ${autoRows.length}곳은 바로 1분 발송 대기로 예약하고, 가상팩스번호 또는 수신번호 확인이 필요한 ${manualRows.length}곳만 입력 창이 열립니다.\n\n계속할까요?`,
      )
    ) {
      return
    }
    setFaxScheduling(true)
    try {
      let autoScheduledCount = 0
      for (const row of autoRows) {
        await scheduleClaimFaxApplication({
          application: row.application,
          insurer: row.insurer,
          receiveNum: row.receiveNum,
          receiveName: row.application?.faxReceiveName || row.insurer?.name || '',
          title: row.application?.faxTitle || '보험금청구서',
        })
        autoScheduledCount += 1
      }
      bulkClaimRemainderRef.current = manualRows.slice(1).map(({ application, insurer }) => ({ application, insurer }))
      const firstManual = manualRows[0]
      if (firstManual) {
        show(
          autoScheduledCount > 0
            ? `${autoScheduledCount}곳은 1분 발송 대기로 예약했습니다. 수신번호 확인이 필요한 보험사를 입력해 주세요.`
            : '수신번호 확인이 필요한 보험사를 입력해 주세요.',
          'success',
        )
        openFaxScheduleModalInternal(firstManual.application, firstManual.insurer)
      } else {
        bulkClaimRemainderRef.current = null
        show(`${autoScheduledCount}곳의 청구서류 발송이 예약되었습니다. 약 1분 후 보험사 팩스로 전송됩니다.`, 'success')
        navigate('/support/claims/applications')
      }
    } catch (e) {
      bulkClaimRemainderRef.current = null
      const message = e?.response?.data?.message || e?.response?.data?.error?.message || ''
      if (String(message).includes('FAX_RESULT_PENDING')) {
        show('이전 팩스 발송 결과가 아직 확정되지 않았습니다. 최종 결과를 확인한 뒤 다시 시도해 주세요.', 'error')
      } else {
        show(message || '전체 청구 예약 중 일부 보험사 발송 요청에 실패했습니다. 수신번호와 청구서류를 확인한 뒤 다시 시도해 주세요.', 'error')
      }
    } finally {
      setFaxScheduling(false)
    }
  }

  const renderSignaturePreview = (signatureId, altText, label = '서명') => {
    if (!signatureId) return null
    const preview = signaturePreviewById[String(signatureId)]
    const previewUrl = preview?.previewUrl || ''
    const previewFailed = preview?.status === 'ERROR'
    return (
      <div className="rounded border border-emerald-100 bg-white p-2">
        <div className="mb-1 flex items-center justify-between gap-2">
          <span className="text-[11px] font-semibold text-slate-700">{label}</span>
          <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700">완료</span>
        </div>
        <div className="flex h-20 items-center justify-center overflow-hidden rounded bg-slate-50/80 p-2">
          {previewUrl ? (
            <img
              src={previewUrl}
              alt={altText}
              className="mx-auto max-h-16 w-full object-contain object-center"
            />
          ) : previewFailed ? (
            <div className="text-center text-[11px] text-rose-600">
              서명 이미지를 불러오지 못했습니다.
            </div>
          ) : (
            <div className="text-center text-[11px] text-slate-500">
              서명 이미지를 불러오는 중입니다.
            </div>
          )}
        </div>
      </div>
    )
  }

  const openCommonSignatureModal = (signerRole, mode) => {
    const isContract = signerRole === 'CONTRACT_HOLDER'
    const isBeneficiary = signerRole === 'BENEFICIARY'
    const rows = isContract
      ? signableClaimsForSelectedInsurers.filter(({ application }) => needsContractHolderSignatureFromApplication(application))
      : isBeneficiary
        ? signableClaimsForSelectedInsurers.filter(({ application }) => needsBeneficiarySignatureFromApplication(application))
        : signableClaimsForSelectedInsurers
    if (rows.length === 0) {
      show('서명을 반영할 청구서가 아직 없습니다. STEP 2에서 청구서를 먼저 생성해 주세요.', 'info')
      return
    }
    const applications = rows.map(({ application }) => application)
    const insurers = rows.map(({ insurer }) => insurer)
    if (mode === 'IN_PERSON') {
      setInPersonSigModal({
        open: true,
        application: applications[0],
        insurer: insurers[0],
        applications,
        insurers,
        signerRole,
      })
      return
    }
    setSigLinkTarget({
      applicationIds: applications.map((application) => application.id),
      insurers,
      signerRole,
    })
    if (isContract) {
      setSigCustomerName(sharedFields.contractHolderName || '')
      setSigCustomerPhone(sharedFields.contractHolderPhone || '')
    } else if (isBeneficiary) {
      setSigCustomerName(sharedFields.beneficiaryName || '')
      setSigCustomerPhone(sharedFields.beneficiaryPhone || '')
    } else {
      setSigCustomerName(claimCustomerName)
      setSigCustomerPhone(claimCustomerPhone)
    }
    setActiveModal('SIGN')
  }

  const openIndividualSignatureModal = (application, insurer, signerRole, mode) => {
    if (!application?.id) return
    if (mode === 'IN_PERSON') {
      setInPersonSigModal({ open: true, application, insurer, applications: [application], insurers: [insurer], signerRole })
      return
    }
    setSigLinkTarget({ applicationId: application.id, insurer, signerRole })
    if (signerRole === 'CONTRACT_HOLDER') {
      setSigCustomerName(sharedFields.contractHolderName || '')
      setSigCustomerPhone(sharedFields.contractHolderPhone || '')
    } else if (signerRole === 'BENEFICIARY') {
      setSigCustomerName(sharedFields.beneficiaryName || '')
      setSigCustomerPhone(sharedFields.beneficiaryPhone || '')
    } else {
      setSigCustomerName(claimCustomerName)
      setSigCustomerPhone(claimCustomerPhone)
    }
    setActiveModal('SIGN')
  }

  const commonInsuredSignatureId = signableClaimsForSelectedInsurers
    .find(({ application }) => claimSignatureCompleted(application?.signatureStatus) && application?.signatureId)
    ?.application?.signatureId
  const commonContractHolderSignatureId = signableClaimsForSelectedInsurers
    .find(({ application }) => claimSignatureCompleted(application?.contractHolderSignatureStatus) && application?.contractHolderSignatureId)
    ?.application?.contractHolderSignatureId
  const commonBeneficiarySignatureId = signableClaimsForSelectedInsurers
    .find(({ application }) => claimSignatureCompleted(application?.beneficiarySignatureStatus) && application?.beneficiarySignatureId)
    ?.application?.beneficiarySignatureId
  const pendingCommonInsuredSignatureId = commonInsuredApplicationIds
    .map((applicationId) => pendingRemoteSigsByAppId[String(applicationId)]?.insured)
    .find(Boolean)
  const pendingCommonContractHolderSignatureId = commonContractHolderApplicationIds
    .map((applicationId) => pendingRemoteSigsByAppId[String(applicationId)]?.contractHolder)
    .find(Boolean)
  const pendingCommonBeneficiarySignatureId = commonBeneficiaryApplicationIds
    .map((applicationId) => pendingRemoteSigsByAppId[String(applicationId)]?.beneficiary)
    .find(Boolean)

  const commonInsuredMissingApplicationIds = useMemo(() => {
    return signableClaimsForSelectedInsurers
      .filter(({ application }) =>
        application?.id && (!claimSignatureCompleted(application.signatureStatus) || !application.signatureId))
      .map(({ application }) => application.id)
  }, [signableClaimsForSelectedInsurers])

  const commonContractHolderMissingApplicationIds = useMemo(() => {
    return signableClaimsForSelectedInsurers
      .filter(({ application }) =>
        application?.id &&
        needsContractHolderSignatureFromApplication(application) &&
        (!claimSignatureCompleted(application.contractHolderSignatureStatus) || !application.contractHolderSignatureId))
      .map(({ application }) => application.id)
  }, [signableClaimsForSelectedInsurers])

  const commonBeneficiaryMissingApplicationIds = useMemo(() => {
    return signableClaimsForSelectedInsurers
      .filter(({ application }) =>
        application?.id &&
        needsBeneficiarySignatureFromApplication(application) &&
        (!claimSignatureCompleted(application.beneficiarySignatureStatus) || !application.beneficiarySignatureId))
      .map(({ application }) => application.id)
  }, [signableClaimsForSelectedInsurers])

  const applyCommonSignatureToUnsignedApplications = async (signerRole) => {
    const isContract = signerRole === 'CONTRACT_HOLDER'
    const isBeneficiary = signerRole === 'BENEFICIARY'
    const signatureId = isContract
      ? commonContractHolderSignatureId
      : isBeneficiary
        ? commonBeneficiarySignatureId
        : commonInsuredSignatureId
    const applicationIds = isContract
      ? commonContractHolderMissingApplicationIds
      : isBeneficiary
        ? commonBeneficiaryMissingApplicationIds
        : commonInsuredMissingApplicationIds
    if (!signatureId) {
      show('적용할 완료된 공통서명이 없습니다.', 'info')
      return
    }
    if (applicationIds.length === 0) {
      show('공통서명을 적용할 청구서가 없습니다.', 'info')
      return
    }
    await syncRemoteSignatureToApplication(applicationIds, signatureId, signatureSlotFromSignerRole(signerRole))
  }

  return (
    <div className="space-y-4">
      {crmPickerOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
          <div className="flex max-h-[min(520px,80vh)] w-full max-w-lg flex-col rounded-lg bg-white shadow-lg">
            <div className="flex items-center justify-between border-b px-4 py-3">
              <h2 className="text-base font-semibold text-slate-900">고객관리에서 불러오기</h2>
              <button
                type="button"
                className="text-sm text-slate-500 hover:text-slate-800"
                onClick={() => setCrmPickerOpen(false)}
              >
                닫기
              </button>
            </div>
            <div className="border-b px-4 py-3">
              <div className="flex gap-2">
                <input
                  type="search"
                  className="min-w-0 flex-1 rounded border border-slate-200 px-3 py-2 text-sm"
                  value={crmPickerQuery}
                  onChange={(e) => setCrmPickerQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      void loadCrmCustomersForPicker(crmPickerQuery)
                    }
                  }}
                  placeholder="이름 또는 연락처"
                />
                <button
                  type="button"
                  className="shrink-0 rounded border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                  onClick={() => void loadCrmCustomersForPicker(crmPickerQuery)}
                  disabled={crmPickerLoading}
                >
                  조회
                </button>
              </div>
            </div>
            {crmPickerErr ? (
              <div className="mx-4 mt-2 rounded border border-rose-200 bg-rose-50 px-2 py-1 text-xs text-rose-800">
                {crmPickerErr}
              </div>
            ) : null}
            <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-4">
              {crmPickerLoading ? (
                <p className="px-2 py-6 text-center text-sm text-slate-500">불러오는 중…</p>
              ) : crmPickerList.length === 0 ? (
                <p className="px-2 py-6 text-center text-sm text-slate-500">검색 결과가 없습니다.</p>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {crmPickerList.map((row, idx) => (
                    <li key={customerRefIdFromManagedCustomer(row) || `${row.customerName || 'customer'}-${idx}`}>
                      <button
                        type="button"
                        className="flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left text-sm hover:bg-slate-50"
                        onClick={() => applyCrmCustomerRowToClaimForm(row)}
                      >
                        <span className="font-medium text-slate-900">{row.customerName || '(이름 없음)'}</span>
                        <span className="text-xs text-slate-500">
                          {row.customerPhone ? formatPhoneDisplay(row.customerPhone) : '연락처 없음'}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="flex justify-end gap-2 border-t px-4 py-2">
              <Link
                to="/support/customers"
                className="mr-auto self-center text-xs font-medium text-brand-navy hover:underline"
              >
                고객관리 화면으로
              </Link>
              <button
                type="button"
                className="rounded border px-3 py-1 text-xs hover:bg-slate-50"
                onClick={() => void loadCrmCustomersForPicker(crmPickerQuery)}
                disabled={crmPickerLoading}
              >
                새로고침
              </button>
            </div>
          </div>
        </div>
      ) : null}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex min-w-0 flex-wrap items-center gap-2 sm:gap-3">
          <h1 className="text-2xl font-bold shrink-0">보험금청구</h1>
          <div className="inline-flex shrink-0 flex-wrap items-center gap-1 rounded-lg border border-sgez-border bg-white p-0.5 text-sm">
            <Button
              type="button"
              variant={activeMenu === 'CLAIM' ? 'sgezSelected' : 'sgezGhost'}
              size="xs"
              onClick={() => navigate('/support/claims')}
            >
              보험금 청구
            </Button>
            <Button
              type="button"
              variant={activeMenu === 'HISTORY' ? 'sgezSelected' : 'sgezGhost'}
              size="xs"
              onClick={() => navigate('/support/claims/applications')}
            >
              청구 이력
            </Button>
            <Button
              type="button"
              variant={activeMenu === 'DRAFT_HISTORY' ? 'sgezSelected' : 'sgezGhost'}
              size="xs"
              onClick={() => navigate('/support/claims/applications?status=DRAFT')}
            >
              임시저장 이력
            </Button>
          </div>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <MenuGuideButton slug="claims" title="보험금청구 가이드" anchor="claims-create" />
          <Button
            type="button"
            variant="sgezOutline"
            size="xs"
            className="shrink-0"
            onClick={() => setActiveModal('FORMS')}
          >
            청구서식
          </Button>
        </div>
      </div>

      {activeMenu === 'CLAIM' && (
        <>
          <div className="sticky top-0 z-20 rounded border border-slate-200 bg-white p-3 shadow-sm sm:p-4">
            {/* {(browserDraftAvailable || serverFlowDraftHint) && (
              <div className="mb-3 rounded border border-sky-200 bg-sky-50/90 px-3 py-2.5 text-xs text-slate-800">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <div className="space-y-1">
                    <div className="font-semibold text-slate-900">저장된 공통 입력이 있습니다</div>
                    <p className="text-slate-600">
                      페이지에 처음 들어오면 빈 양식으로 시작합니다. 이전에 「공통 입력 임시저장」한 내용은 아래 버튼으로만 불러올 수 있습니다.
                    </p>
                    {serverFlowDraftHint && (
                      <p className="text-slate-600">
                        서버 초안: 마지막 저장 {formatApiDateTimeDisplay(serverFlowDraftHint.updatedAtDisplay, serverFlowDraftHint.updatedAt)}
                        {typeof serverFlowDraftHint.retentionDays === 'number'
                          ? ` · 약 ${serverFlowDraftHint.retentionDays}일 보관`
                          : ''}
                      </p>
                    )}
                    {browserDraftAvailable && (
                      <p className="text-slate-600">이 브라우저에도 공통 입력 초안이 저장되어 있습니다.</p>
                    )}
                  </div>
                  <Button
                    type="button"
                    variant="sgezOutline"
                    size="sm"
                    className="shrink-0"
                    onClick={applyStoredClaimsCommonDraft}
                  >
                    저장된 공통 입력 불러오기
                  </Button>
                </div>
              </div>
            )} */}
            <div className="flex flex-col gap-3 lg:flex-row lg:items-stretch lg:justify-between lg:gap-4">
              <div className="grid min-w-0 flex-1 grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-7">
                {[
                  ['1', '보험사 선택'],
                  ['2', '공통 청구정보'],
                  ['3', '보험사별 정보'],
                  ['4', '청구서류 첨부'],
                  ['5', '고객서명'],
                  ['6', '동의 확인'],
                  ['7', '보험금 청구'],
                ].map(([step, title]) => (
                  <button
                    key={step}
                    type="button"
                    className={`rounded border px-3 py-2 text-left sm:px-3.5 sm:py-2.5 ${
                      openStep === step ? 'border-brand-navy bg-sky-50 text-brand-navy shadow-sm ring-1 ring-brand-navy/10' : 'border-slate-200 bg-white hover:bg-slate-50'
                    }`}
                    onClick={() => setOpenStep(step)}
                  >
                    <div className="text-xs font-semibold text-slate-500">STEP {step}</div>
                    <div className="mt-1 text-sm font-semibold text-slate-900 sm:text-base">{title}</div>
                  </button>
                ))}
              </div>
              <div className="flex shrink-0 flex-col gap-2 lg:w-56 lg:max-w-full">
                <Button
                  type="button"
                  variant="sgezOutline"
                  size="sm"
                  className="w-full"
                  onClick={persistClaimsCommonDraftNow}
                  // title="운영자는 서버(약 7일 보관)와 이 브라우저에 동시에 임시저장됩니다. 오프라인일 때는 브라우저에만 저장되며, 연결되면 자동으로 서버에 맞춥니다."
                >
                  공통 입력 임시저장
                </Button>
                <Button
                  type="button"
                  variant="sgezOutline"
                  size="sm"
                  className="w-full border-rose-200 text-rose-800 hover:bg-rose-50"
                  onClick={resetClaimsFlowInput}
                  // title={
                  //   isOperator
                  //     ? '브라우저·서버에 저장된 공통 입력 초안을 지우고 이 화면을 처음 상태로 되돌립니다. 이미 서버에 만들어진 청구서(DRAFT 등)는 삭제되지 않습니다.'
                  //     : '브라우저에 저장된 공통 입력 초안을 지우고 이 화면을 처음 상태로 되돌립니다. 이미 서버에 만들어진 청구서는 삭제되지 않습니다.'
                  // }
                >
                  입력 초기화
                </Button>
              </div>
            </div>
            {/* <p className="mt-2 text-xs text-slate-500">
              {isOperator
                ? '공통 입력은 서버에 암호화 임시저장되고 약 7일 뒤 자동 삭제됩니다. 저장만으로 고객에게 알림이 가거나 보험사로 발송되지는 않습니다.'
                : '공통 입력 임시저장은 이 기기 브라우저에만 보관됩니다. 저장만으로 고객에게 알림이 가거나 보험사로 발송되지는 않습니다.'}
            </p> */}
          </div>

          {openStep === '1' && (
          <ClaimStepSection step="1" title="보험사 선택" headerRight={claimFlowNextStepButton}>
            <div className="flex items-center justify-between gap-3">
              <div className="text-base text-slate-600">선택 {selectedInsurerIds.length}건</div>
            </div>

            <div className="flex flex-wrap gap-3">
              {FLOW_CATEGORIES.map((item) => {
                const active = flowCategory === item.key
                return (
                  <button
                    key={item.key}
                    type="button"
                    className={`rounded-lg border px-5 py-3 text-base font-medium ${
                      active
                        ? 'border-brand-navy bg-sky-50 text-brand-navy shadow-sm ring-1 ring-brand-navy/10'
                        : 'bg-white hover:bg-slate-50'
                    }`}
                    onClick={() => selectFlowCategory(item.key)}
                  >
                    {item.label}
                    {!item.enabled && <span className="ml-2 text-sm opacity-80">준비중</span>}
                  </button>
                )
              })}
            </div>

            {!supportedFlowCategory && (
              <div className="rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                선택한 청구 유형은 아직 상세 입력을 제공하지 않습니다.
              </div>
            )}

            {supportedFlowCategory && (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
                  {filteredInsurers.map((org) => {
                    const selected = selectedInsurerIds.some(v => String(v) === String(org.id))
                    const oid = String(org.id)
                    const tplStatus = claimTemplateStatusByOrgId[oid] || {}
                    const tplOk = claimTemplateByOrgId[oid] === true
                    const tplNo = !claimTemplateStatusLoading && tplStatus.hasTemplate === false
                    const tplInactive = !claimTemplateStatusLoading && tplStatus.hasTemplate === true && tplStatus.claimEnabled !== true
                    const tplBlocked = tplNo || tplInactive
                    const tplPending = claimTemplateStatusLoading
                    const canToggle = tplPending ? selected : (selected || tplOk)
                    const requiresVirtualFax = !!insurerFaxByOrgId[String(org.id)]?.virtualFaxRequired
                    const supportsGroupClaim = GROUP_CLAIM_AVAILABLE_INSURERS.has(String(org.name || '').trim())
                    return (
                      <button
                        key={org.id}
                        type="button"
                        disabled={!canToggle}
                        title={
                          tplNo
                            ? '등록된 청구 서식이 없어 선택할 수 없습니다. 운영 > 서식관리 > 청구서식에서 등록해 주세요.'
                            : tplInactive
                              ? '청구 준비 중인 보험사입니다.'
                            : tplPending && !selected
                              ? '청구 서식 여부 확인 중…'
                              : undefined
                        }
                        className={`relative min-h-[4.5rem] rounded-lg border py-3.5 pl-5 pr-4 ${
                          selected ? 'border-brand-navy bg-slate-50' : 'hover:bg-slate-50'
                        } ${!canToggle ? 'cursor-not-allowed opacity-60' : ''} ${tplBlocked ? 'border-slate-200 bg-slate-50' : ''}`}
                        onClick={() => toggleInsurerSelection(org.id)}
                      >
                        <span
                          className={`absolute right-3 top-3 inline-flex h-7 w-7 items-center justify-center rounded-full border text-sm ${
                            selected ? 'border-brand-navy bg-sky-50 text-brand-navy ring-1 ring-brand-navy/10' : 'border-slate-300 text-slate-400'
                          }`}
                          aria-hidden
                        >
                          {selected ? '✓' : ''}
                        </span>
                        <div className="flex items-center gap-2.5 pr-9">
                            <ClaimInsurerLogoMark org={org} variant="grid" />
                            <div className="flex min-w-0 flex-col items-start gap-1">
                              <span className="text-base font-semibold leading-snug text-slate-900">
                                {org.name}
                              </span>
                              {(supportsGroupClaim || requiresVirtualFax || (tplPending && !selected) || tplNo || tplInactive) ? (
                                <div className="flex flex-wrap items-center justify-start gap-1">
                                  {supportsGroupClaim && (
                                    <span className="inline-flex rounded bg-emerald-100 px-1.5 py-0 text-[10px] font-medium leading-4 text-emerald-700">
                                      단체
                                    </span>
                                  )}
                                  {requiresVirtualFax && (
                                    <span className="inline-flex rounded bg-amber-100 px-1.5 py-0 text-[10px] font-medium leading-4 text-amber-800">
                                      가상
                                    </span>
                                  )}
                                  {tplPending && !selected && (
                                    <span className="inline-flex rounded bg-slate-200 px-1.5 py-0 text-[10px] font-medium leading-4 text-slate-600">
                                      확인 중
                                    </span>
                                  )}
                                  {tplNo && (
                                    <span className="inline-flex rounded bg-rose-100 px-1.5 py-0 text-[10px] font-medium leading-4 text-rose-800">
                                      준비 중
                                    </span>
                                  )}
                                  {tplInactive && (
                                    <span className="inline-flex rounded bg-rose-100 px-1.5 py-0 text-[10px] font-medium leading-4 text-rose-800">
                                      준비 중
                                    </span>
                                  )}
                                </div>
                              ) : null}
                            </div>
                        </div>
                      </button>
                    )
                  })}
                </div>

                {filteredInsurers.length === 0 && (
                  <div className="text-sm text-slate-600">선택 가능한 보험사가 없습니다.</div>
                )}

                <div className="rounded border bg-slate-50 p-3 space-y-2.5">
                  <div className="text-sm font-semibold text-slate-900">선택된 보험사</div>
                  {selectedInsurers.length === 0 ? (
                    <div className="text-sm text-slate-600">보험사를 하나 이상 선택해 주세요.</div>
                  ) : (
                    <div className="flex flex-wrap gap-2.5">
                      {selectedInsurers.map((org) => (
                        <span
                          key={org.id}
                          className="inline-flex max-w-full items-center gap-1.5 rounded border bg-white py-1 pl-2 pr-0.5 text-sm"
                        >
                          <ClaimInsurerLogoMark org={org} variant="chip" />
                          <span className="min-w-0 truncate text-slate-900">{org.name}</span>
                          <button
                            type="button"
                            className="shrink-0 rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                            aria-label={`${org.name} 선택 해제`}
                            title="선택 해제"
                            onClick={() => toggleInsurerSelection(org.id)}
                          >
                            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                              <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" />
                            </svg>
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  {needsVirtualFax && (
                    <div className="rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                      선택한 보험사 중 가상팩스번호가 필요한 보험사가 있습니다. STEP 7 보험금 청구 시 발급받은 가상팩스번호를 수신번호로 입력해 주세요. (공용 청구번호와 동일한 번호는 사용할 수 없습니다.)
                    </div>
                  )}
                  {hasGroupClaimAvailable && (
                    <div className="text-sm text-emerald-700">
                      선택한 보험사 중 단체실손 청구 가능 보험사가 포함되어 있습니다.
                    </div>
                  )}
                </div>
              </>
            )}
          </ClaimStepSection>
          )}

          {openStep === '2' && (
          <ClaimStepSection step="2" title="공통 청구정보 입력" headerRight={claimFlowNextStepButton}>
            <div className="space-y-3">
              {!claimCustomerRefId ? (
                <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-900">
                  고객관리에 등록되지 않은 고객은 검색으로 불러올 수 없습니다. 아래 항목을 직접 입력해 주세요.
                </div>
              ) : null}
              <FieldSection
                title="피보험자"
                open={expandedSharedSections.insured !== false}
                onToggle={() => toggleSharedSection('insured')}
                active={getStep2SectionMeta('insured').active}
                completed={getStep2SectionMeta('insured').completed}
                onNext={() => goNextStep2Section('insured')}
                nextLabel={getStep2SectionMeta('insured').isLast ? '입력 완료' : '다음'}
              >
                <div className="space-y-3">
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                    <div>
                      <div className="text-sm font-medium text-slate-700 mb-1">성명</div>
                      <div className="flex gap-2">
                        <input
                          className="min-w-0 flex-1 rounded border px-3 py-2 text-sm"
                          value={claimCustomerName}
                          onChange={(e) => {
                            setClaimCustomerName(e.target.value)
                            if (claimCustomerRefId) setClaimCustomerRefId(null)
                          }}
                          placeholder="홍길동"
                        />
                        <button
                          type="button"
                          onClick={openCustomerPicker}
                          className="shrink-0 rounded border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                        >
                          검색
                        </button>
                      </div>
                      {claimCustomerRefId ? (
                        <p className="mt-1 text-xs text-emerald-700">고객관리에 등록된 고객과 연결됨</p>
                      ) : null}
                    </div>
                    <div>
                      <div className="text-sm font-medium text-slate-700 mb-1">주민등록번호</div>
                      <ResidentDigitsSplitInput
                        idPrefix="insured-rrn"
                        value={sharedFields.customerResidentNumber}
                        onChange={(v) => setSharedField('customerResidentNumber', v)}
                      />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-slate-700 mb-1">휴대폰</div>
                      <PhoneDigitsSplitInput
                        idPrefix="insured-ph"
                        value={claimCustomerPhone}
                        onChange={(v) => setClaimCustomerPhone(v)}
                      />
                      {String(claimCustomerPhone || '').replace(/\D/g, '').length > 0
                        && !isValidKoreanClaimPhone(claimCustomerPhone) && (
                        <p className="mt-1 text-xs text-rose-600">
                          숫자 9~11자리(예: 휴대폰 010·011·016…, 유선·인터넷전화 02·070 등)로 입력해 주세요.
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                    <div>
                      <div className="text-sm font-medium text-slate-700 mb-1">이메일</div>
                      <input
                        className="w-full rounded border px-3 py-2 text-sm"
                        value={sharedFields.customerEmail}
                        onChange={(e) => setSharedField('customerEmail', e.target.value)}
                        placeholder="name@example.com"
                      />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-slate-700 mb-1">직장명</div>
                      <input
                        className="w-full rounded border px-3 py-2 text-sm"
                        value={sharedFields.customerWorkplace}
                        onChange={(e) => setSharedField('customerWorkplace', e.target.value)}
                        placeholder="직장(회사)명"
                      />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-slate-700 mb-1">하시는 일</div>
                      <input
                        className="w-full rounded border px-3 py-2 text-sm"
                        value={sharedFields.customerOccupation}
                        onChange={(e) => setSharedField('customerOccupation', e.target.value)}
                        placeholder="직업·담당 업무 등"
                      />
                    </div>
                  </div>
                  <div>
                    <div className="mb-1 flex items-center gap-2">
                      <div className="text-sm font-medium text-slate-700">주소</div>
                    </div>
                    {/* <p className="mb-2 text-xs text-slate-500">
                      카카오(다음) 우편번호 검색으로 기본주소를 선택한 뒤, 상세주소(동·호수 등)만 입력합니다.
                    </p> */}
                    <div className="flex flex-col gap-2 md:flex-row md:items-stretch md:gap-2">
                      <div className="flex shrink-0 flex-wrap items-stretch gap-2">
                        <input
                          type="text"
                          readOnly
                          value={sharedFields.customerAddressZonecode}
                          placeholder="우편번호"
                          className="w-[6.5rem] shrink-0 rounded border border-slate-200 bg-slate-50 px-2 py-2 text-center text-sm text-slate-600"
                          aria-label="우편번호"
                        />
                        <button
                          type="button"
                          onClick={openInsuredPostcode}
                          className="shrink-0 rounded border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                        >
                          주소 검색
                        </button>
                      </div>
                      <input
                        type="text"
                        readOnly
                        value={sharedFields.customerAddressRoad}
                        placeholder="기본 주소 (검색 후 자동 입력)"
                        className="min-w-0 w-full flex-1 rounded border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 md:min-w-[8rem]"
                        aria-label="기본 주소"
                      />
                      <input
                        ref={insuredAddressDetailRef}
                        type="text"
                        value={sharedFields.customerAddressDetail}
                        onChange={(e) => setSharedField('customerAddressDetail', e.target.value)}
                        placeholder="상세 주소 (동·호수 등)"
                        className="min-w-0 w-full flex-1 rounded border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-sky-400 focus:ring-1 focus:ring-sky-100 md:min-w-[8rem]"
                        autoComplete="street-address"
                      />
                    </div>
                    {insuredPostcodeError ? (
                      <p className="mt-1 text-xs text-rose-600">{insuredPostcodeError}</p>
                    ) : null}
                  </div>

                  {(showContractHolderInputs || showBeneficiaryRoleInputs) && (
                    <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
                      <div className="flex flex-col gap-3 lg:flex-row lg:flex-wrap lg:items-center lg:gap-x-6 lg:gap-y-2">
                        {showContractHolderInputs && (
                          <div className="flex min-w-0 flex-wrap items-center gap-2">
                            <label className="inline-flex shrink-0 items-center gap-2">
                              <input
                                type="checkbox"
                                checked={isContractHolderSameAsInsured(sharedFields.contractHolderSameAsInsured)}
                                onChange={(e) => {
                                  const on = e.target.checked
                                  if (on) {
                                    setSharedFields((p) => ({
                                      ...p,
                                      contractHolderSameAsInsured: true,
                                      beneficiarySameAsContractHolder: false,
                                      contractHolderName: '',
                                      contractHolderResidentNumber: '',
                                      contractHolderPhone: '',
                                      contractHolderSignatureImageBase64: '',
                                    }))
                                  } else {
                                    setSharedField('contractHolderSameAsInsured', false)
                                  }
                                }}
                              />
                              계약자가 피보험자와 동일합니다
                            </label>
                          </div>
                        )}

                        {showBeneficiaryRoleInputs && (
                          <div
                            className={cn(
                              'flex min-w-0 flex-wrap items-center gap-x-4 gap-y-2 pt-2',
                              showContractHolderInputs
                                ? 'border-t border-slate-200 lg:border-l lg:border-t-0 lg:pl-6 lg:pt-0'
                                : 'pt-0',
                            )}
                            role="group"
                            aria-label="수익자 동일 여부"
                          >
                            <span className="w-full text-xs font-semibold text-slate-600 lg:hidden">수익자</span>
                            {isContractHolderSameAsInsured(sharedFields.contractHolderSameAsInsured) ? (
                              <label className="inline-flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  checked={isBeneficiarySameAsCustomerFlag(sharedFields.beneficiarySameAsCustomer)}
                                  onChange={(e) => setSharedField('beneficiarySameAsCustomer', e.target.checked)}
                                />
                                피보험자와 수익자 동일
                              </label>
                            ) : (
                              <>
                                <label className="inline-flex items-center gap-2">
                                  <input
                                    type="checkbox"
                                    checked={isBeneficiarySameAsCustomerFlag(sharedFields.beneficiarySameAsCustomer)}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setSharedFields((p) => ({
                                          ...p,
                                          beneficiarySameAsCustomer: true,
                                          beneficiarySameAsContractHolder: false,
                                        }))
                                      } else {
                                        setSharedField('beneficiarySameAsCustomer', false)
                                      }
                                    }}
                                  />
                                  수익자 — 피보험자와 동일
                                </label>
                                <label className="inline-flex items-center gap-2">
                                  <input
                                    type="checkbox"
                                    checked={isBeneficiarySameAsContractHolderFlag(sharedFields.beneficiarySameAsContractHolder)}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setSharedFields((p) => ({
                                          ...p,
                                          beneficiarySameAsContractHolder: true,
                                          beneficiarySameAsCustomer: false,
                                        }))
                                      } else {
                                        setSharedField('beneficiarySameAsContractHolder', false)
                                      }
                                    }}
                                  />
                                  수익자 — 계약자와 동일
                                </label>
                                <p className="mt-1 w-full basis-full text-xs leading-snug text-slate-600">
                                  피보험자·계약자와 모두 다른 수익자(제3자)인 경우, 위 두 항목을 모두 해제하면 아래 수익자 상세 입력란이 열립니다.
                                </p>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  <div>
                    <div className="mb-2 text-sm font-medium text-slate-700">보상 안내 받으실 분</div>
                    <div className="flex flex-wrap gap-4 rounded border border-slate-200 px-3 py-2 text-sm">
                      <label className="inline-flex items-center gap-2">
                        <input
                          type="radio"
                          name="benefitNotificationRecipient"
                          checked={sharedFields.benefitNotificationRecipient === 'PLANNER'}
                          onChange={() => setSharedField('benefitNotificationRecipient', 'PLANNER')}
                        />
                        보험설계사
                      </label>
                      <label className="inline-flex items-center gap-2">
                        <input
                          type="radio"
                          name="benefitNotificationRecipient"
                          checked={sharedFields.benefitNotificationRecipient === 'CLAIMANT'}
                          onChange={() => setSharedField('benefitNotificationRecipient', 'CLAIMANT')}
                        />
                        보험청구인
                      </label>
                    </div>
                  </div>
                </div>
              </FieldSection>

              {showContractHolderInputs && !isContractHolderSameAsInsured(sharedFields.contractHolderSameAsInsured) && (
              <FieldSection
                title="계약자"
                open={expandedSharedSections.contractHolder !== false}
                onToggle={() => toggleSharedSection('contractHolder')}
                active={getStep2SectionMeta('contractHolder').active}
                completed={getStep2SectionMeta('contractHolder').completed}
                onNext={() => goNextStep2Section('contractHolder')}
                nextLabel={getStep2SectionMeta('contractHolder').isLast ? '입력 완료' : '다음'}
              >
                <div className="space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                      <div>
                        <div className="text-sm font-medium text-slate-700 mb-1">계약자 성명</div>
                        <input
                          className="w-full rounded border px-3 py-2 text-sm"
                          value={sharedFields.contractHolderName}
                          onChange={(e) => setSharedField('contractHolderName', e.target.value)}
                          placeholder="계약자 이름"
                        />
                      </div>
                      <div>
                        <div className="text-sm font-medium text-slate-700 mb-1">계약자 주민등록번호</div>
                        <ResidentDigitsSplitInput
                          idPrefix="ch-rrn"
                          value={sharedFields.contractHolderResidentNumber}
                          onChange={(v) => setSharedField('contractHolderResidentNumber', v)}
                        />
                      </div>
                      <div>
                        <div className="text-sm font-medium text-slate-700 mb-1">계약자 연락처</div>
                        <PhoneDigitsSplitInput
                          idPrefix="ch-ph"
                          value={sharedFields.contractHolderPhone}
                          onChange={(v) => setSharedField('contractHolderPhone', v)}
                        />
                      </div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-slate-700 mb-1">계약자 서명</div>
                      <div className="rounded border bg-slate-50 p-2">
                        <canvas
                          ref={contractHolderCanvasRef}
                          width={480}
                          height={160}
                          className="w-full max-w-lg touch-none cursor-crosshair rounded border bg-white"
                          onMouseDown={(e) => {
                            const p = getContractHolderCanvasPoint(e)
                            if (p) startContractHolderDraw(p.x, p.y)
                          }}
                          onMouseMove={(e) => {
                            const p = getContractHolderCanvasPoint(e)
                            if (p) moveContractHolderDraw(p.x, p.y)
                          }}
                          onMouseUp={endContractHolderDraw}
                          onMouseLeave={endContractHolderDraw}
                          onTouchStart={(e) => {
                            e.preventDefault()
                            const p = getContractHolderCanvasPoint(e)
                            if (p) startContractHolderDraw(p.x, p.y)
                          }}
                          onTouchMove={(e) => {
                            e.preventDefault()
                            const p = getContractHolderCanvasPoint(e)
                            if (p) moveContractHolderDraw(p.x, p.y)
                          }}
                          onTouchEnd={(e) => {
                            e.preventDefault()
                            endContractHolderDraw()
                          }}
                        />
                      </div>
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <button
                          type="button"
                          className="rounded border px-3 py-1.5 text-xs font-semibold hover:bg-slate-50"
                          onClick={clearContractHolderCanvas}
                        >
                          지우기
                        </button>
                        <button
                          type="button"
                          className="rounded bg-brand-navy px-3 py-1.5 text-xs font-semibold text-white hover:opacity-90"
                          onClick={captureContractHolderSignature}
                        >
                          서명 캡처
                        </button>
                        {sharedFields.contractHolderSignatureImageBase64 ? (
                          <span className="text-xs font-medium text-emerald-700">캡처됨 · PDF extraFields에 포함됩니다</span>
                        ) : (
                          <span className="text-xs text-slate-500">서명 후 &quot;서명 캡처&quot;를 눌러 주세요</span>
                        )}
                      </div>
                    </div>
                </div>
              </FieldSection>
              )}

              {showBeneficiaryInputs && (
              <FieldSection
                title={showBeneficiaryDetailsInputs ? '수익자 상세' : '수익자'}
                open={expandedSharedSections.beneficiary !== false}
                onToggle={() => toggleSharedSection('beneficiary')}
                active={getStep2SectionMeta('beneficiary').active}
                completed={getStep2SectionMeta('beneficiary').completed}
                onNext={() => goNextStep2Section('beneficiary')}
                nextLabel={getStep2SectionMeta('beneficiary').isLast ? '입력 완료' : '다음'}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                  {showBeneficiaryDetailsInputs && (
                  <div>
                    <div className="text-sm font-medium text-slate-700 mb-1">성명</div>
                    <input
                      className="w-full rounded border px-3 py-2 text-sm"
                      value={sharedFields.beneficiaryName}
                      onChange={(e) => setSharedField('beneficiaryName', e.target.value)}
                    />
                  </div>
                  )}
                  <div>
                    <div className="text-sm font-medium text-slate-700 mb-1">
                      피보험자와의 관계 <span className="text-rose-500">*</span>
                    </div>
                    <input
                      className="w-full rounded border px-3 py-2 text-sm"
                      value={sharedFields.beneficiaryRelationToInsured}
                      onChange={(e) => setSharedField('beneficiaryRelationToInsured', e.target.value)}
                      placeholder="예: 배우자, 자녀, 부모"
                      required
                    />
                  </div>
                  {showBeneficiaryDetailsInputs && (
                  <>
                  <div>
                    <div className="text-sm font-medium text-slate-700 mb-1">주민등록번호</div>
                    <ResidentDigitsSplitInput
                      idPrefix="ben-rrn"
                      value={sharedFields.beneficiaryResidentNumber}
                      onChange={(v) => setSharedField('beneficiaryResidentNumber', v)}
                    />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-slate-700 mb-1">휴대폰</div>
                    <PhoneDigitsSplitInput
                      idPrefix="ben-ph"
                      value={sharedFields.beneficiaryPhone}
                      onChange={(v) => setSharedField('beneficiaryPhone', v)}
                    />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-slate-700 mb-1">이메일</div>
                    <input
                      className="w-full rounded border px-3 py-2 text-sm"
                      value={sharedFields.beneficiaryEmail}
                      onChange={(e) => setSharedField('beneficiaryEmail', e.target.value)}
                    />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-slate-700 mb-1">직장명</div>
                    <input
                      className="w-full rounded border px-3 py-2 text-sm"
                      value={sharedFields.beneficiaryWorkplace}
                      onChange={(e) => setSharedField('beneficiaryWorkplace', e.target.value)}
                    />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-slate-700 mb-1">하시는 일</div>
                    <input
                      className="w-full rounded border px-3 py-2 text-sm"
                      value={sharedFields.beneficiaryOccupation}
                      onChange={(e) => setSharedField('beneficiaryOccupation', e.target.value)}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <div className="text-sm font-medium text-slate-700 mb-1">주소</div>
                    <input
                      className="w-full rounded border px-3 py-2 text-sm"
                      value={sharedFields.beneficiaryAddress}
                      onChange={(e) => setSharedField('beneficiaryAddress', e.target.value)}
                    />
                  </div>
                  </>
                  )}
                </div>
              </FieldSection>
              )}

              <FieldSection
                title="보험금수령 관련"
                open={expandedSharedSections.payout !== false}
                onToggle={() => toggleSharedSection('payout')}
                active={getStep2SectionMeta('payout').active}
                completed={getStep2SectionMeta('payout').completed}
                onNext={() => goNextStep2Section('payout')}
                nextLabel={getStep2SectionMeta('payout').isLast ? '입력 완료' : '다음'}
              >
                <div className="flex flex-col gap-3">
                  {payoutType === 'GENERAL' ? (
                    <div className="grid min-w-0 grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4 lg:items-end">
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-slate-700 mb-1">계좌 유형</div>
                        <select
                          className="w-full rounded border px-3 py-2 text-sm"
                          value={payoutType}
                          onChange={(e) => setSharedField('accountPayoutType', e.target.value)}
                        >
                          <option value="AUTO_TRANSFER">자동이체계좌</option>
                          <option value="PREPAID">기지급계좌</option>
                          <option value="GENERAL">일반(은행·계좌 직접 입력)</option>
                        </select>
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-slate-700 mb-1">은행</div>
                        <input
                          className="w-full rounded border px-3 py-2 text-sm"
                          value={sharedFields.bankName}
                          onChange={(e) => setSharedField('bankName', e.target.value)}
                        />
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-slate-700 mb-1">예금주</div>
                        <MaskableSensitiveInput
                          maskKind="text"
                          value={sharedFields.accountHolder}
                          onChange={(v) => setSharedField('accountHolder', v)}
                          className="w-full rounded border px-3 py-2 text-sm"
                        />
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-slate-700 mb-1">계좌번호</div>
                        <MaskableSensitiveInput
                          maskKind="account"
                          value={sharedFields.accountNumber}
                          onChange={(v) => setSharedField('accountNumber', v)}
                          className="w-full rounded border px-3 py-2 text-sm"
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="max-w-xs">
                      <div className="text-sm font-medium text-slate-700 mb-1">계좌 유형</div>
                      <select
                        className="w-full rounded border px-3 py-2 text-sm"
                        value={payoutType}
                        onChange={(e) => setSharedField('accountPayoutType', e.target.value)}
                      >
                        <option value="AUTO_TRANSFER">자동이체계좌</option>
                        <option value="PREPAID">기지급계좌</option>
                        <option value="GENERAL">일반(은행·계좌 직접 입력)</option>
                      </select>
                    </div>
                  )}
                </div>
              </FieldSection>

              <FieldSection
                title="청구 세부내용"
                open={expandedSharedSections.details !== false}
                onToggle={() => toggleSharedSection('details')}
                active={getStep2SectionMeta('details').active}
                completed={getStep2SectionMeta('details').completed}
                onNext={() => goNextStep2Section('details')}
                nextLabel={getStep2SectionMeta('details').isLast ? '입력 완료' : '다음'}
              >
                <div className="space-y-8">
                    <div className="space-y-3">
                    <div className="flex flex-col gap-2 pb-1">
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                        <div className="flex min-w-0 items-center gap-2 shrink-0">
                          <span className="h-4 w-1 shrink-0 rounded-full bg-slate-400" aria-hidden />
                          <div className="text-sm font-bold text-slate-950">접수·청구</div>
                        </div>
                        <div
                          className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-1 text-sm"
                          role="group"
                          aria-label="접수 구분"
                        >
                          <label className="inline-flex items-center gap-2">
                            <input
                              type="radio"
                              name="claimAccidentReceiptType"
                              checked={sharedFields.accidentReceiptType !== 'ADDITIONAL'}
                              onChange={() => setSharedField('accidentReceiptType', 'NEW')}
                            />
                            최초청구
                          </label>
                          <label className="inline-flex items-center gap-2">
                            <input
                              type="radio"
                              name="claimAccidentReceiptType"
                              checked={sharedFields.accidentReceiptType === 'ADDITIONAL'}
                              onChange={() => setSharedField('accidentReceiptType', 'ADDITIONAL')}
                            />
                            동일사고 추가접수
                          </label>
                        </div>
                        {selectedIncludesNonlife && (
                          <div className="flex min-w-0 flex-wrap items-center gap-2 text-sm">
                            <span className="text-xs font-medium text-slate-600 whitespace-nowrap">자동차보험 처리</span>
                            <select
                              className="min-w-[7.5rem] rounded border border-slate-200 bg-white px-2 py-1.5 text-sm"
                              value={sharedFields.autoInsuranceProcessed}
                              onChange={(e) => {
                                const v = e.target.value
                                setSharedFields((p) => ({
                                  ...p,
                                  autoInsuranceProcessed: v,
                                  ...(v !== '예'
                                    ? { autoInsuranceCompany: '', autoInsuranceContact: '', vehicleNumber: '' }
                                    : {}),
                                }))
                              }}
                            >
                              <option value="아니오">아니오</option>
                              <option value="예">예</option>
                            </select>
                          </div>
                        )}
                      </div>
                    </div>
                    {selectedIncludesNonlife && sharedFields.autoInsuranceProcessed === '예' && (
                      <div className="grid grid-cols-1 gap-3 rounded border border-sky-100 bg-sky-50/40 p-3 md:grid-cols-2 xl:grid-cols-3">
                        <div className="md:col-span-2 xl:col-span-3 text-xs font-semibold text-sky-900">자동차보험 처리(예) — 추가 입력</div>
                        <div>
                          <div className="text-sm font-medium text-slate-700 mb-1">담당 보험사</div>
                          <input
                            className="w-full rounded border px-3 py-2 text-sm"
                            value={sharedFields.autoInsuranceCompany}
                            onChange={(e) => setSharedField('autoInsuranceCompany', e.target.value)}
                            placeholder="자동차보험 보험사명"
                          />
                        </div>
                        <div>
                          <div className="text-sm font-medium text-slate-700 mb-1">담당자 연락처</div>
                          <input
                            className="w-full rounded border px-3 py-2 text-sm"
                            value={sharedFields.autoInsuranceContact}
                            onChange={(e) => setSharedField('autoInsuranceContact', e.target.value)}
                            placeholder="연락처"
                          />
                        </div>
                        <div>
                          <div className="text-sm font-medium text-slate-700 mb-1">본인 차량번호</div>
                          <input
                            className="w-full rounded border px-3 py-2 text-sm"
                            value={sharedFields.vehicleNumber}
                            onChange={(e) => setSharedField('vehicleNumber', e.target.value)}
                            placeholder="예: 12가3456"
                          />
                        </div>
                      </div>
                    )}
{/*
                    <div>
                      <div className="text-sm font-medium text-slate-700 mb-2">청구유형(중복 가능)</div>
                      <div className="flex flex-col gap-2 md:flex-row md:items-center md:gap-3">
                        <div className="flex min-w-0 flex-1 flex-wrap gap-4 rounded border px-3 py-2 text-sm">
                          {['사망', '장해', '진단', '치과치료', '수술', '입원', '통원', '실손', '치료'].map((item) => (
                            <label key={item} className="inline-flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={sharedFields.claimTypes.includes(item)}
                                onChange={(e) => {
                                  setSharedField(
                                    'claimTypes',
                                    e.target.checked
                                      ? [...sharedFields.claimTypes, item]
                                      : sharedFields.claimTypes.filter((v) => v !== item)
                                  )
                                }}
                              />
                              {item}
                            </label>
                          ))}
                        </div>
                        <input
                          className="w-full shrink-0 rounded border px-3 py-2 text-sm md:max-w-xs md:flex-initial"
                          value={sharedFields.claimTypeEtc}
                          onChange={(e) => setSharedField('claimTypeEtc', e.target.value)}
                          placeholder="기타 청구유형 입력"
                        />
                      </div>
                    </div> */}
                  </div>

                  <div className="space-y-4 border-t border-slate-200 pt-6">
                    <div className="flex flex-wrap items-center gap-3 border-b border-slate-200 pb-2">
                      <div className="flex items-center gap-2">
                        <span className="h-4 w-1 rounded-full bg-slate-400" aria-hidden />
                        <div className="text-sm font-bold text-slate-950">사고 정보</div>
                      </div>
                      <label className="inline-flex items-center gap-2 rounded border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-xs text-slate-700">
                        <input
                          type="checkbox"
                          checked={!!sharedFields.accidentInfoAttached}
                          onChange={(e) => {
                            const checked = e.target.checked
                            setSharedFields((prev) => ({
                              ...prev,
                              accidentInfoAttached: checked,
                              accidentLocation: checked
                                ? CLAIMS_ATTACHMENT_PLACEHOLDER
                                : String(prev.accidentLocation || '').trim() === CLAIMS_ATTACHMENT_PLACEHOLDER
                                  ? ''
                                  : prev.accidentLocation,
                              accidentDetails: checked
                                ? CLAIMS_ATTACHMENT_PLACEHOLDER
                                : String(prev.accidentDetails || '').trim() === CLAIMS_ATTACHMENT_PLACEHOLDER
                                  ? ''
                                  : prev.accidentDetails,
                            }))
                          }}
                        />
                        <span className="font-medium">사고장소·경위 자료첨부</span>
                      </label>
                    </div>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:items-start">
                      <div>
                        <div className="text-sm font-medium text-slate-700 mb-1">사고 일자</div>
                        <input
                          type="date"
                          className="w-full rounded border px-3 py-2 text-sm"
                          max={todayYmdLocal()}
                          value={accidentDate}
                          onChange={(e) => {
                            const v = e.target.value
                            const m = todayYmdLocal()
                            if (v && v > m) return
                            setAccidentDate(v)
                          }}
                        />
                      </div>
                      <div>
                        <div className="text-sm font-medium text-slate-700 mb-2">사고 유형</div>
                        <div className="flex flex-wrap gap-4 rounded border px-3 py-2 text-sm">
                          {['질병', '재해', '상해', '교통사고', '자살', '기타'].map((item) => (
                            <label key={item} className="inline-flex items-center gap-2">
                              <input
                                type="radio"
                                name="accidentType"
                                checked={sharedFields.accidentType === item}
                                onChange={() => setSharedField('accidentType', item)}
                              />
                              {item}
                            </label>
                          ))}
                        </div>
                        {sharedFields.accidentType === '기타' && (
                          <input
                            className="mt-2 w-full rounded border px-3 py-2 text-sm"
                            value={sharedFields.accidentTypeEtc}
                            onChange={(e) => setSharedField('accidentTypeEtc', e.target.value)}
                            placeholder="기타 유형 입력"
                          />
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:items-start">
                      <div>
                        <div className="mb-1 flex items-center gap-2">
                          <div className="text-sm font-medium text-slate-700">사고 장소</div>
                          {sharedFields.accidentInfoAttached ? (
                            <span className="rounded-full border border-slate-200 bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-500">자료첨부</span>
                          ) : null}
                        </div>
                        <input
                          className="w-full rounded border px-3 py-2 text-sm disabled:border-slate-200 disabled:bg-slate-100 disabled:text-slate-500"
                          value={sharedFields.accidentLocation}
                          onChange={(e) => {
                            setSharedFields((prev) => ({
                              ...prev,
                              accidentInfoAttached: false,
                              accidentLocation: e.target.value,
                            }))
                          }}
                          placeholder="사고 발생 장소를 입력하세요"
                          disabled={!!sharedFields.accidentInfoAttached}
                        />
                      </div>
                      <div>
                        <div className="mb-1 flex items-center gap-2">
                          <div className="text-sm font-medium text-slate-700">사고 경위</div>
                          {sharedFields.accidentInfoAttached ? (
                            <span className="rounded-full border border-slate-200 bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-500">자료첨부</span>
                          ) : null}
                        </div>
                        <textarea
                          className="w-full min-h-[2.5rem] resize-y rounded border px-3 py-2 text-sm leading-snug disabled:border-slate-200 disabled:bg-slate-100 disabled:text-slate-500"
                          rows={1}
                          value={sharedFields.accidentDetails}
                          onChange={(e) => {
                            setSharedFields((prev) => ({
                              ...prev,
                              accidentInfoAttached: false,
                              accidentDetails: e.target.value,
                            }))
                          }}
                          placeholder="사고 발생 경위·상황 등 사고와 직접 관련된 내용을 입력하세요."
                          disabled={!!sharedFields.accidentInfoAttached}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </FieldSection>
            </div>

            {/* <div className="rounded border bg-slate-50 p-3 text-sm text-slate-700">
              공통 입력(피보험자·수익자·보험금수령·청구 세부내용 등)을 채우면 선택한 보험사마다 청구서가 자동으로 생성·갱신됩니다. 별도의 보험사별 입력 팝업은 없습니다.
            </div> */}

            {!supportedFlowCategory ? (
              <div className="text-sm text-slate-600">선택한 청구 유형은 상세 입력 단계를 아직 제공하지 않습니다.</div>
            ) : selectedInsurers.length === 0 ? (
              <div className="text-sm text-slate-600">STEP 1에서 보험사를 먼저 선택해 주세요.</div>
            ) : (
              <div className="space-y-2">
                {insurerAppSyncError && (
                  <div className="rounded border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
                    {insurerAppSyncError}
                  </div>
                )}
                <div className="flex flex-wrap gap-2">
                  {selectedInsurers.map((org) => {
                    const app = createdClaimApplications[String(org.id)]
                    return (
                      <div
                        key={org.id}
                        className="rounded border border-emerald-100 bg-emerald-50/50 px-3 py-2 text-sm text-slate-800"
                      >
                        <span className="font-medium text-slate-900">{org.name}</span>
                        {' '}
                        입력 완료
                        {insurerAppSyncWorking && !app?.id && (
                          <span className="ml-2 text-xs text-amber-700">청구서 생성 중…</span>
                        )}
                        {app?.id && (
                          <span className="ml-2 text-xs text-slate-500">#{app.id}</span>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
            )}

            {false && (
            <div className="rounded border border-sky-200 bg-sky-50/60 p-4 space-y-3">
              <div className="font-semibold text-slate-900">고객 청구링크 발송</div>
              <button
                type="button"
                className="rounded border border-slate-300 bg-white px-3 py-2 text-sm font-medium hover:bg-slate-50 disabled:opacity-50"
                disabled={!supportedFlowCategory || selectedInsurerIds.length === 0}
                onClick={() => {
                  setIntakeCustomerName(claimCustomerName)
                  setIntakeCustomerPhone(claimCustomerPhone)
                  setActiveModal('INTAKE')
                }}
              >
                고객 청구링크 발송
              </button>
            </div>
            )}
          </ClaimStepSection>
          )}

          {openStep === '3' && (
          <ClaimStepSection step="3" title="보험사별 추가/예외 정보" headerRight={claimFlowNextStepButton}>
            <div className="space-y-3">
              {selectedInsurers.length === 0 ? (
                <div className="rounded border border-dashed p-4 text-sm text-slate-500">
                  먼저 STEP 1에서 보험사를 선택해주세요.
                </div>
              ) : (
                <>
                  {claimAdditionalFieldsLoading ? (
                    <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
                      보험사별 입력 항목을 확인하는 중입니다.
                    </div>
                  ) : null}
                  {claimAdditionalFieldsError ? (
                    <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                      {claimAdditionalFieldsError}
                    </div>
                  ) : null}
                  {claimAdditionalFieldRequiredError ? (
                    <div className="rounded border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
                      {claimAdditionalFieldRequiredError}
                    </div>
                  ) : null}
                  {claimAdditionalFieldGroups.length > 0 ? (
                    <ClaimAdditionalFieldInputs
                      fields={claimAdditionalFieldGroups}
                      values={claimAdditionalFieldValues}
                      onChange={setClaimAdditionalFieldValues}
                      title=""
                      modeByGroupKey={claimAdditionalFieldModeByGroupKey}
                      onModeChange={setClaimAdditionalFieldGroupMode}
                    />
                  ) : !claimAdditionalFieldsLoading ? (
                    <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
                      선택한 보험사에 등록된 보험사별 추가 입력 항목이 없습니다.
                    </div>
                  ) : null}
                </>
              )}
            </div>
          </ClaimStepSection>
          )}

          {openStep === '4' && (
          <ClaimStepSection step="4" title="청구서류 첨부" headerRight={claimFlowNextStepButton}>
            <div className="space-y-4">
              <div className="flex flex-col gap-3 rounded border border-slate-200 bg-slate-50 p-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <div className="text-sm font-semibold text-slate-900">청구서류 업로드</div>
                  <div className="mt-1 text-xs text-slate-500">여러 파일을 한 번에 선택할 수 있습니다.</div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`rounded border px-3 py-1.5 text-sm font-semibold ${
                    sharedDocumentPageCount >= CLAIM_MAX_DOCUMENT_COUNT
                      ? 'border-amber-200 bg-amber-50 text-amber-800'
                      : 'border-slate-200 bg-white text-slate-700'
                  }`}>
                    {sharedDocumentPageCount} / {CLAIM_MAX_DOCUMENT_COUNT}장
                  </span>
                  <label className={`inline-flex cursor-pointer items-center justify-center rounded bg-teal-600 px-4 py-2 text-sm font-semibold text-white hover:bg-teal-700 ${
                    sharedDocumentsUploading || sharedDocumentPageCount >= CLAIM_MAX_DOCUMENT_COUNT
                      ? 'pointer-events-none opacity-60'
                      : ''
                  }`}>
                    파일 추가
                    <input
                      type="file"
                      multiple
                      accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                      className="hidden"
                      onChange={(e) => {
                        const list = e.target.files
                        if (list?.length) uploadSharedDocuments(list)
                        e.target.value = ''
                      }}
                      disabled={sharedDocumentsUploading || sharedDocumentPageCount >= CLAIM_MAX_DOCUMENT_COUNT}
                    />
                  </label>
                  {sharedDocumentsUploading && (
                    <div className="text-xs text-slate-500">업로드 중...</div>
                  )}
                </div>
                {sharedDocumentsError && (
                  <div className="text-xs text-rose-700">{sharedDocumentsError}</div>
                )}
              </div>
              <div className="rounded border border-slate-200 bg-white p-3">
                <div className="grid gap-3 lg:grid-cols-[minmax(220px,0.35fr)_1fr] lg:items-start">
                  <div>
                    <label htmlFor="step4-claim-document-section" className="block text-xs font-semibold text-slate-700">청구 유형</label>
                    <select
                      id="step4-claim-document-section"
                      className="mt-1 w-full rounded border border-slate-300 bg-white px-3 py-2 text-sm"
                      value={claimDocumentSectionTitle}
                      onChange={(e) => setClaimDocumentSectionTitle(e.target.value)}
                    >
                      {CLAIM_DOCUMENT_SECTIONS_ALL.map((section) => (
                        <option key={section.sectionTitle} value={section.sectionTitle}>{section.sectionTitle}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-slate-800">서류 유형 점검</div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {selectedClaimDocumentTypeRows.map((row) => (
                        <span
                          key={row.value}
                          className={`rounded-full px-2.5 py-1 text-[11px] font-medium ${
                            row.ok ? 'bg-emerald-100 text-emerald-900' : 'bg-slate-200/80 text-slate-600'
                          }`}
                        >
                          {row.ok ? '✓' : '·'} {row.label}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              {sharedDocuments.length === 0 ? (
                <div className="rounded border border-dashed border-slate-300 bg-slate-50 px-4 py-10 text-center text-sm text-slate-500">
                  첨부된 청구서류가 없습니다.
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6">
                  {sharedDocuments.map((doc, idx) => {
                    const preview = doc.fileId ? sharedDocumentPreviewUrls[String(doc.fileId)] : null
                    const previewKind = preview?.kind || filePreviewKind(doc)
                    const previewUrl = preview?.url || ''
                    return (
                      <div key={`${doc.fileId}-${idx}`} className="overflow-hidden rounded border border-slate-200 bg-white shadow-sm">
                        <div className="border-b border-slate-100 px-3 py-2">
                          <div className="truncate text-sm font-semibold text-slate-900" title={doc.label || doc.fileId}>
                            {doc.label || doc.fileId || `첨부 ${idx + 1}`}
                          </div>
                          <div className="mt-0.5 text-[11px] font-medium text-slate-500">{claimDocumentPageCount(doc)}장</div>
                        </div>
                        <button
                          type="button"
                          className="flex h-36 w-full items-center justify-center bg-slate-100 md:h-40"
                          onClick={() => openSharedDocumentPreview(idx)}
                          title="미리보기"
                        >
                          {previewKind === 'image' && previewUrl ? (
                            <img src={previewUrl} alt="" className="h-full w-full object-contain" loading="lazy" />
                          ) : previewKind === 'pdf' && previewUrl ? (
                            <iframe src={previewUrl} title={`${doc.label || doc.fileId || '첨부'} 미리보기`} className="pointer-events-none h-full w-full bg-white" />
                          ) : (
                            <div className="flex h-full w-full flex-col items-center justify-center gap-2 bg-slate-50 px-2 text-center text-xs font-semibold text-slate-400">
                              <span className="flex h-14 w-14 items-center justify-center rounded-xl border border-slate-200 bg-white">FILE</span>
                              <span className="line-clamp-2 break-all">{doc.label || doc.fileId}</span>
                            </div>
                          )}
                        </button>
                        <div className="space-y-2 p-2">
                          <div>
                            <label className="block text-[11px] font-semibold text-slate-600">유형</label>
                            <select
                              className="mt-1 w-full rounded border border-slate-300 bg-white px-2 py-1.5 text-xs"
                              value={doc.documentType != null && doc.documentType !== '' ? doc.documentType : ''}
                              onChange={(e) => {
                                const v = e.target.value
                                setSharedDocuments((prev) =>
                                  prev.map((it, i) => (i === idx ? { ...it, documentType: v || null } : it)),
                                )
                              }}
                            >
                              {CLAIM_DOCUMENT_TYPE_OPTIONS.map((o) => (
                                <option key={o.value || '_empty'} value={o.value}>
                                  {o.label}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="grid grid-cols-3 gap-1">
                            <button type="button" className="rounded border px-1.5 py-1 text-[11px] hover:bg-slate-50" onClick={() => openSharedDocumentPreview(idx)}>
                              미리보기
                            </button>
                            <button type="button" className="rounded border px-1.5 py-1 text-[11px] hover:bg-slate-50" onClick={() => downloadFile(doc.fileId)}>
                              다운로드
                            </button>
                            <button
                              type="button"
                              className="rounded border border-rose-200 px-1.5 py-1 text-[11px] text-rose-700 hover:bg-rose-50"
                              onClick={() => {
                                setSharedDocuments((prev) =>
                                  prev
                                    .filter((_, i) => i !== idx)
                                    .map((item, order) => ({ ...item, sortOrder: order })),
                                )
                              }}
                            >
                              삭제
                            </button>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </ClaimStepSection>
          )}

          {openStep === '5' && (
          <ClaimStepSection step="5" title="고객서명" headerRight={claimFlowNextStepButton}>
            {selectedInsurers.length === 0 ? (
              <EmptyState
                title="청구할 보험사가 선택되지 않았습니다"
                description="STEP 1에서 보험사를 선택하면 서명 상태와 발송 준비 상태를 보험사별로 확인할 수 있습니다."
                className="rounded border border-dashed bg-white"
              />
            ) : (
              <div className="space-y-4">
                <div className="rounded border border-sky-200 bg-sky-50/70 p-4">
                  <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
                    <div>
                      <div className="font-semibold text-slate-900">공통 서명</div>
                      <div className="mt-1 text-sm text-slate-600">
                        {commonBeneficiarySignatureRequired
                          ? (commonContractHolderSignatureRequired
                              ? '피보험자, 계약자, 수익자가 달라 필요한 서명을 각각 받습니다.'
                              : '수익자가 피보험자/계약자와 달라 피보험자 서명과 수익자 서명을 각각 받습니다.')
                          : (commonContractHolderSignatureRequired
                              ? '피보험자와 계약자가 달라 피보험자 서명과 계약자 서명을 각각 받습니다.'
                              : '피보험자와 계약자가 동일하므로 피보험자/계약자 공통 서명 한 번만 받습니다.')}
                      </div>
                    </div>
                    <span className={`w-fit rounded px-2 py-1 text-xs font-medium ${commonSignatureCompleted ? 'bg-emerald-100 text-emerald-800' : 'bg-white text-slate-700'}`}>
                      공통 서명 {commonSignatureCompleted ? '완료' : '필요'}
                    </span>
                  </div>
                  {signableClaimsForSelectedInsurers.length === 0 ? (
                    <div className="mt-3 rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                      아직 서명을 반영할 청구서가 없습니다. STEP 2에서 입력을 완료해 청구서를 생성해 주세요.
                    </div>
                  ) : (
                    <div className={`mt-4 grid grid-cols-1 gap-3 ${commonContractHolderSignatureRequired || commonBeneficiarySignatureRequired ? 'md:grid-cols-2 xl:grid-cols-3' : ''}`}>
                      <div className="rounded border bg-white p-4">
                        <div className="flex items-center justify-between gap-2">
                          <div className="font-medium text-slate-900">
                            {commonContractHolderSignatureRequired ? '피보험자 서명' : '피보험자/계약자 서명'}
                          </div>
                          <span className={`rounded px-2 py-0.5 text-xs ${commonInsuredSignatureCompleted ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-600'}`}>
                            {commonInsuredSignatureCompleted ? '완료' : '대기'}
                          </span>
                        </div>
                        <div className="mt-3 flex flex-wrap gap-2">
                          <button type="button" className="rounded border px-3 py-2 text-sm hover:bg-slate-50" onClick={() => openCommonSignatureModal('INSURED', 'IN_PERSON')}>
                            직접서명
                          </button>
                          <button type="button" className="rounded border px-3 py-2 text-sm hover:bg-slate-50" onClick={() => openCommonSignatureModal('INSURED', 'REMOTE')}>
                            서명링크 전송
                          </button>
                          {commonInsuredSignatureId && commonInsuredMissingApplicationIds.length > 0 && (
                            <button
                              type="button"
                              className="rounded border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm text-emerald-900 hover:bg-emerald-100"
                              title={`완료된 공통서명을 미완료 청구서 ${commonInsuredMissingApplicationIds.length}건에 반영합니다.`}
                              onClick={() => { void applyCommonSignatureToUnsignedApplications('INSURED') }}
                            >
                              공통서명 적용하기
                            </button>
                          )}
                          {pendingCommonInsuredSignatureId && !commonInsuredSignatureCompleted && (
                            <button
                              type="button"
                              className="rounded border border-sky-300 bg-sky-50 px-3 py-2 text-sm text-sky-900 hover:bg-sky-100"
                              onClick={() => syncRemoteSignatureToApplication(commonInsuredApplicationIds, pendingCommonInsuredSignatureId, 'insured')}
                            >
                              서명 완료 확인
                            </button>
                          )}
                        </div>
                        {commonContractHolderSignatureRequired ? (
                          <div className="mt-3">
                            {renderSignaturePreview(commonInsuredSignatureId, '피보험자 공통 서명', '피보험자 서명 썸네일')}
                          </div>
                        ) : (
                          <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                            {renderSignaturePreview(commonInsuredSignatureId, '계약자 공통 서명', '계약자 서명 썸네일')}
                            {renderSignaturePreview(commonInsuredSignatureId, '피보험자 공통 서명', '피보험자 서명 썸네일')}
                          </div>
                        )}
                      </div>
                      {commonContractHolderSignatureRequired && (
                        <div className="rounded border bg-white p-4">
                          <div className="flex items-center justify-between gap-2">
                            <div className="font-medium text-slate-900">계약자 서명</div>
                            <span className={`rounded px-2 py-0.5 text-xs ${commonContractHolderSignatureCompleted ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-600'}`}>
                              {commonContractHolderSignatureCompleted ? '완료' : '대기'}
                            </span>
                          </div>
                          <div className="mt-3 flex flex-wrap gap-2">
                            <button type="button" className="rounded border px-3 py-2 text-sm hover:bg-slate-50" onClick={() => openCommonSignatureModal('CONTRACT_HOLDER', 'IN_PERSON')}>
                              직접서명
                            </button>
                            <button type="button" className="rounded border px-3 py-2 text-sm hover:bg-slate-50" onClick={() => openCommonSignatureModal('CONTRACT_HOLDER', 'REMOTE')}>
                              서명링크 전송
                            </button>
                            {commonContractHolderSignatureId && commonContractHolderMissingApplicationIds.length > 0 && (
                              <button
                                type="button"
                                className="rounded border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm text-emerald-900 hover:bg-emerald-100"
                                title={`완료된 공통서명을 미완료 청구서 ${commonContractHolderMissingApplicationIds.length}건에 반영합니다.`}
                                onClick={() => { void applyCommonSignatureToUnsignedApplications('CONTRACT_HOLDER') }}
                              >
                                공통서명 적용하기
                              </button>
                            )}
                            {pendingCommonContractHolderSignatureId && !commonContractHolderSignatureCompleted && (
                              <button
                                type="button"
                                className="rounded border border-sky-300 bg-sky-50 px-3 py-2 text-sm text-sky-900 hover:bg-sky-100"
                                onClick={() => syncRemoteSignatureToApplication(commonContractHolderApplicationIds, pendingCommonContractHolderSignatureId, 'contractHolder')}
                              >
                                서명 완료 확인
                              </button>
                            )}
                          </div>
                          <div className="mt-3">
                            {renderSignaturePreview(commonContractHolderSignatureId, '계약자 공통 서명', '계약자 서명 썸네일')}
                          </div>
                        </div>
                      )}
                      {commonBeneficiarySignatureRequired && (
                        <div className="rounded border bg-white p-4">
                          <div className="flex items-center justify-between gap-2">
                            <div className="font-medium text-slate-900">수익자 서명</div>
                            <span className={`rounded px-2 py-0.5 text-xs ${commonBeneficiarySignatureCompleted ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-600'}`}>
                              {commonBeneficiarySignatureCompleted ? '완료' : '대기'}
                            </span>
                          </div>
                          <div className="mt-3 flex flex-wrap gap-2">
                            <button type="button" className="rounded border px-3 py-2 text-sm hover:bg-slate-50" onClick={() => openCommonSignatureModal('BENEFICIARY', 'IN_PERSON')}>
                              직접서명
                            </button>
                            <button type="button" className="rounded border px-3 py-2 text-sm hover:bg-slate-50" onClick={() => openCommonSignatureModal('BENEFICIARY', 'REMOTE')}>
                              서명링크 전송
                            </button>
                            {commonBeneficiarySignatureId && commonBeneficiaryMissingApplicationIds.length > 0 && (
                              <button
                                type="button"
                                className="rounded border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm text-emerald-900 hover:bg-emerald-100"
                                title={`완료된 공통서명을 미완료 청구서 ${commonBeneficiaryMissingApplicationIds.length}건에 반영합니다.`}
                                onClick={() => { void applyCommonSignatureToUnsignedApplications('BENEFICIARY') }}
                              >
                                공통서명 적용하기
                              </button>
                            )}
                            {pendingCommonBeneficiarySignatureId && !commonBeneficiarySignatureCompleted && (
                              <button
                                type="button"
                                className="rounded border border-sky-300 bg-sky-50 px-3 py-2 text-sm text-sky-900 hover:bg-sky-100"
                                onClick={() => syncRemoteSignatureToApplication(commonBeneficiaryApplicationIds, pendingCommonBeneficiarySignatureId, 'beneficiary')}
                              >
                                서명 완료 확인
                              </button>
                            )}
                          </div>
                          <div className="mt-3">
                            {renderSignaturePreview(commonBeneficiarySignatureId, '수익자 공통 서명', '수익자 서명 썸네일')}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="rounded border bg-white">
                  <div className="border-b px-4 py-3">
                    <div className="font-semibold text-slate-900">선택된 보험사 목록</div>
                  </div>
                  <div className="divide-y">
                    {createdClaimsForSelectedInsurers.map(({ insurer, application }) => {
                      const needCH = application ? needsContractHolderSignatureFromApplication(application) : false
                      const needBeneficiary = application ? needsBeneficiarySignatureFromApplication(application) : false
                      const beneficiarySameLabel = application ? beneficiarySignatureSameLabelFromApplication(application) : ''
                      const appKey = application?.id != null ? String(application.id) : ''
                      const pend = appKey ? pendingRemoteSigsByAppId[appKey] : null
                      const pendInsured = pend?.insured
                      const pendCh = pend?.contractHolder
                      const pendBeneficiary = pend?.beneficiary
                      const insuredSigOk = application && claimSignatureCompleted(application.signatureStatus)
                      const chSigOk = !needCH || (application && claimSignatureCompleted(application.contractHolderSignatureStatus))
                      const beneficiarySigOk = !needBeneficiary || (application && claimSignatureCompleted(application.beneficiarySignatureStatus))
                      const beneficiaryPreviewSignatureId = needBeneficiary
                        ? application?.beneficiarySignatureId
                        : beneficiarySameLabel === '계약자와 동일'
                          ? (needCH ? application?.contractHolderSignatureId : application?.signatureId)
                          : application?.signatureId
                      return (
                        <div key={insurer.id} className="p-4">
                          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                            <div className="min-w-[180px]">
                              <div className="font-medium text-slate-900">{insurer.name}</div>
                              {!application ? (
                                <div className="mt-1 text-sm text-amber-700">아직 저장된 청구서가 없습니다.</div>
                              ) : (
                                <div className="mt-1 text-sm text-slate-500">청구서 #{application.id}</div>
                              )}
                            </div>
                            <div className="grid min-w-0 flex-1 grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                              <div className="rounded border border-slate-100 bg-slate-50/70 p-3">
                                <div className="flex items-center justify-between gap-2">
                                  <div className="text-sm font-medium text-slate-900">피보험자 서명</div>
                                  <span className={`rounded px-2 py-0.5 text-xs ${insuredSigOk ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-600'}`}>
                                    {insuredSigOk ? '완료' : '대기'}
                                  </span>
                                </div>
                                <div className="mt-3 flex flex-wrap gap-2">
                                  <button
                                    type="button"
                                    className="rounded border bg-white px-3 py-1.5 text-xs hover:bg-slate-50 disabled:opacity-50"
                                    disabled={!application}
                                    onClick={() => openIndividualSignatureModal(application, insurer, 'INSURED', 'IN_PERSON')}
                                  >
                                    별도 직접서명
                                  </button>
                                  <button
                                    type="button"
                                    className="rounded border bg-white px-3 py-1.5 text-xs hover:bg-slate-50 disabled:opacity-50"
                                    disabled={!application}
                                    onClick={() => openIndividualSignatureModal(application, insurer, 'INSURED', 'REMOTE')}
                                  >
                                    별도 링크
                                  </button>
                                  {pendInsured && !insuredSigOk && (
                                    <button
                                      type="button"
                                      className="rounded border border-sky-300 bg-sky-50 px-3 py-1.5 text-xs text-sky-900 hover:bg-sky-100"
                                      onClick={() => syncRemoteSignatureToApplication(application.id, pendInsured, 'insured')}
                                    >
                                      완료 확인
                                    </button>
                                  )}
                                </div>
                                <div className="mt-3">
                                  {renderSignaturePreview(application?.signatureId, `${insurer.name} 피보험자 서명`, '피보험자 서명 썸네일')}
                                </div>
                              </div>
                              <div className="rounded border border-slate-100 bg-slate-50/70 p-3">
                                <div className="flex items-center justify-between gap-2">
                                  <div className="text-sm font-medium text-slate-900">계약자 서명</div>
                                  <span className={`rounded px-2 py-0.5 text-xs ${chSigOk ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-600'}`}>
                                    {needCH ? (chSigOk ? '완료' : '대기') : '피보험자와 동일'}
                                  </span>
                                </div>
                                {needCH && (
                                  <div className="mt-3 flex flex-wrap gap-2">
                                    <button
                                      type="button"
                                      className="rounded border bg-white px-3 py-1.5 text-xs hover:bg-slate-50 disabled:opacity-50"
                                      disabled={!application}
                                      onClick={() => openIndividualSignatureModal(application, insurer, 'CONTRACT_HOLDER', 'IN_PERSON')}
                                    >
                                      별도 직접서명
                                    </button>
                                    <button
                                      type="button"
                                      className="rounded border bg-white px-3 py-1.5 text-xs hover:bg-slate-50 disabled:opacity-50"
                                      disabled={!application}
                                      onClick={() => openIndividualSignatureModal(application, insurer, 'CONTRACT_HOLDER', 'REMOTE')}
                                    >
                                      별도 링크
                                    </button>
                                    {pendCh && !chSigOk && (
                                      <button
                                        type="button"
                                        className="rounded border border-sky-300 bg-sky-50 px-3 py-1.5 text-xs text-sky-900 hover:bg-sky-100"
                                        onClick={() => syncRemoteSignatureToApplication(application.id, pendCh, 'contractHolder')}
                                      >
                                        완료 확인
                                      </button>
                                    )}
                                  </div>
                                )}
                                <div className="mt-3">
                                  {renderSignaturePreview(
                                    needCH ? application?.contractHolderSignatureId : application?.signatureId,
                                    `${insurer.name} 계약자 서명`,
                                    '계약자 서명 썸네일',
                                  )}
                                  {!needCH && !application?.signatureId && (
                                    <div className="rounded border border-dashed bg-white p-3 text-xs text-slate-500">
                                      STEP 2에서 계약자가 피보험자와 동일하여 별도 계약자 서명이 필요하지 않습니다.
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="rounded border border-slate-100 bg-slate-50/70 p-3">
                                <div className="flex items-center justify-between gap-2">
                                  <div className="text-sm font-medium text-slate-900">수익자 서명</div>
                                  <span className={`rounded px-2 py-0.5 text-xs ${beneficiarySigOk ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-600'}`}>
                                    {needBeneficiary ? (beneficiarySigOk ? '완료' : '대기') : (beneficiarySameLabel || '별도 서명 없음')}
                                  </span>
                                </div>
                                {needBeneficiary && (
                                  <div className="mt-3 flex flex-wrap gap-2">
                                    <button
                                      type="button"
                                      className="rounded border bg-white px-3 py-1.5 text-xs hover:bg-slate-50 disabled:opacity-50"
                                      disabled={!application}
                                      onClick={() => openIndividualSignatureModal(application, insurer, 'BENEFICIARY', 'IN_PERSON')}
                                    >
                                      별도 직접서명
                                    </button>
                                    <button
                                      type="button"
                                      className="rounded border bg-white px-3 py-1.5 text-xs hover:bg-slate-50 disabled:opacity-50"
                                      disabled={!application}
                                      onClick={() => openIndividualSignatureModal(application, insurer, 'BENEFICIARY', 'REMOTE')}
                                    >
                                      별도 링크
                                    </button>
                                    {pendBeneficiary && !beneficiarySigOk && (
                                      <button
                                        type="button"
                                        className="rounded border border-sky-300 bg-sky-50 px-3 py-1.5 text-xs text-sky-900 hover:bg-sky-100"
                                        onClick={() => syncRemoteSignatureToApplication(application.id, pendBeneficiary, 'beneficiary')}
                                      >
                                        완료 확인
                                      </button>
                                    )}
                                  </div>
                                )}
                                <div className="mt-3">
                                  {renderSignaturePreview(
                                    beneficiaryPreviewSignatureId,
                                    `${insurer.name} 수익자 서명`,
                                    '수익자 서명 썸네일',
                                  )}
                                  {!needBeneficiary && !beneficiaryPreviewSignatureId && (
                                    <div className="rounded border border-dashed bg-white p-3 text-xs text-slate-500">
                                      STEP 2에서 수익자가 피보험자 또는 계약자와 동일하여 별도 수익자 서명이 필요하지 않습니다.
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              </div>
            )}
          </ClaimStepSection>
          )}

          {openStep === '6' && (
          <ClaimStepSection step="6" title="발송 전 동의 확인" headerRight={claimFlowNextStepButton}>
            <div className="rounded border border-slate-200 bg-white p-4">
              <div className="text-base font-bold text-slate-950">보험금청구 필수 동의</div>
              <div className="mt-1 text-sm text-slate-600">
                보험금 청구서류를 보험사로 발송하기 전에 고객 동의와 설계사 확인을 완료해 주세요.
              </div>
              <label className="mt-4 flex items-center gap-2 rounded border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-900">
                <input
                  type="checkbox"
                  checked={claimConsentAllChecked}
                  onChange={(e) => {
                    const checked = e.target.checked
                    setClaimConsentChecks(Object.fromEntries(claimConsentDocs.map((doc) => [doc.docCode, checked])))
                    setClaimAgentConfirmed(checked)
                  }}
                  disabled={claimConsentLoading || !!claimConsentError || claimConsentDocs.length === 0}
                />
                <span>전체동의</span>
              </label>
              <div className="mt-4 space-y-3 rounded border border-slate-200 bg-slate-50 p-3">
                <div className="text-sm font-semibold text-slate-900">고객 동의</div>
                {claimConsentLoading ? (
                  <div className="text-sm text-slate-600">동의 문서를 불러오는 중입니다.</div>
                ) : claimConsentError ? (
                  <div className="rounded border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{claimConsentError}</div>
                ) : (
                  <div className="space-y-2">
                    {claimConsentDocs.map((doc) => (
                      <div key={doc.docCode} className="rounded border border-slate-200 bg-white p-3">
                        <label className="flex items-start gap-2 text-sm font-semibold text-slate-800">
                          <input
                            type="checkbox"
                            className="mt-1"
                            checked={!!claimConsentChecks?.[doc.docCode]}
                            onChange={(e) => setClaimConsentChecks((prev) => ({ ...prev, [doc.docCode]: e.target.checked }))}
                          />
                          <span>
                            {doc.title || doc.fallbackLabel}
                            <span className="ml-1 text-xs font-normal text-slate-500">v{doc.versionLabel || '-'}</span>
                          </span>
                        </label>
                        {doc.bodyHtml ? (
                          <details className="mt-2 text-xs text-slate-600">
                            <summary className="cursor-pointer font-medium text-slate-700">내용 보기</summary>
                            <div
                              className="mt-2 space-y-2 leading-relaxed [&_h3]:font-semibold [&_ul]:list-disc [&_ul]:pl-5"
                              dangerouslySetInnerHTML={{ __html: doc.bodyHtml }}
                            />
                          </details>
                        ) : null}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="mt-3 rounded border border-slate-200 bg-white p-3">
                <label className="flex items-start gap-2 text-sm font-semibold text-slate-800">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={claimAgentConfirmed}
                    onChange={(e) => setClaimAgentConfirmed(e.target.checked)}
                  />
                  <span>고객 본인 또는 법정대리인으로부터 보험금 청구 지원에 필요한 동의를 확보했습니다.</span>
                </label>
              </div>
              <div className="mt-4 flex justify-end">
                <Button
                  type="button"
                  variant="sgez"
                  size="sm"
                  disabled={claimConsentLoading || !!claimConsentError || !claimConsentReady}
                  onClick={() => setOpenStep('7')}
                >
                  동의 확인 완료
                </Button>
              </div>
            </div>
          </ClaimStepSection>
          )}

          {openStep === '7' && (
          <ClaimStepSection step="7" title="보험금 청구" headerRight={claimFlowNextStepButton}>
            <div className="rounded border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
              팩스 전송이 접수되면 이용 횟수가 먼저 잡히고, 보험사 최종 수신이 확인되면 확정 차감됩니다. 전송이 시작되기 전까지 발송 취소할 수 있습니다.
            </div>
            {selectedInsurers.length === 0 ? (
              <div className="rounded border border-dashed p-4 text-sm text-slate-500">
                먼저 STEP 1에서 보험사를 선택해주세요.
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex flex-col gap-2 rounded border border-slate-200 bg-white p-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="text-sm text-slate-700">
                    <span className="font-medium text-slate-900">전체 청구</span>
                    <span className="ml-2 text-slate-600">
                      지금 발송 설정 가능한 보험사 <span className="tabular-nums font-semibold text-slate-900">{bulkClaimEligibleRows.length}</span>
                      곳 — 비가상팩스는 바로 예약하고, 가상팩스 필요 보험사만 수신번호 입력 창이 열립니다.
                    </span>
                  </div>
                  <Button
                    type="button"
                    variant="sgez"
                    size="sm"
                    className="shrink-0"
                    onClick={openBulkClaimScheduling}
                    disabled={
                      faxScheduling
                      || !!claimAdditionalFieldRequiredError
                      || bulkClaimEligibleRows.length === 0
                      || bulkClaimEligibleRows.some(({ application }) => !claimConsentSatisfiedForApplication(application))
                    }
                    title={
                      claimAdditionalFieldRequiredError
                        ? 'STEP 3에서 보험사별 필수 입력을 먼저 완료해 주세요.'
                        : bulkClaimEligibleRows.some(({ application }) => !claimConsentSatisfiedForApplication(application))
                        ? 'STEP 6에서 보험금청구 필수 동의를 먼저 확인해 주세요.'
                        : bulkClaimEligibleRows.length === 0
                        ? 'STEP 2~5가 완료되고, 발송 대기·이전 팩스 확정 대기가 아닌 보험사가 있을 때 사용할 수 있습니다.'
                        : undefined
                    }
                  >
                    전체 청구하기
                  </Button>
                </div>
                {createdClaimsForSelectedInsurers.map(({ insurer, application }) => {
                  const isDraft = application?.status === 'DRAFT'
                  const hasPendingFax = application?.faxQuotaSettlementStatus === 'PENDING'
                  const isScheduled = application?.faxReservationStatus === 'SCHEDULED'
      || application?.faxReservationStatus === 'SENDING'
                  const faxCanReschedule =
                    application?.faxReservationStatus === 'FAILED' || application?.faxReservationStatus === 'CANCELLED'
                  const pendingBlocksSchedule = hasPendingFax && !faxCanReschedule
                  const sigOk = applicationHasCompletedSignature(application)
                  const docsOk = applicationHasAttachedDocuments(application)
                  const consentOk = claimConsentSatisfiedForApplication(application)
                  const faxReadiness = getClaimApplicationFaxReadiness(application)
                  return (
                    <div key={insurer.id} className="rounded border bg-white p-4">
                      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                        <div>
                          <div className="font-semibold text-slate-900">{insurer.name}</div>
                          {!application ? (
                            <div className="mt-1 text-sm text-amber-700">아직 저장된 청구서가 없습니다. STEP 2에서 입력을 완료해주세요.</div>
                          ) : (
                            <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-slate-600">
                              <span>청구서 #{application.id}</span>
                              <span className="rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-700">
                                상태 {CLAIM_APPLICATION_STATUS_LABELS[application.status] || application.status || '-'}
                              </span>
                              {!sigOk && (
                                <span className="rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-900">서명 미완료</span>
                              )}
                              {application.faxReservationStatus && (
                                <span
                                  className={
                                    faxCanReschedule
                                      ? 'rounded bg-rose-100 px-2 py-0.5 text-xs text-rose-900'
                                      : 'rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-800'
                                  }
                                >
                                  팩스 {FAX_RESERVATION_UI_LABELS[application.faxReservationStatus] || application.faxReservationStatus}
                                </span>
                              )}
                              {application.faxQuotaSettlementStatus && application.faxQuotaSettlementStatus !== 'NONE' && (
                                <span className="rounded bg-sky-100 px-2 py-0.5 text-xs text-sky-800" title="Popbill 전송·이용 확정 상태">
                                  {FAX_TRANSMISSION_STATUS_LABELS[application.faxQuotaSettlementStatus] || application.faxQuotaSettlementStatus}
                                </span>
                              )}
                            </div>
                          )}
                          {pendingBlocksSchedule && (
                            <div className="mt-2 text-xs text-amber-700">이전 팩스 발송 결과가 아직 확정되지 않아 새로 발송할 수 없습니다. 잠시 후 다시 시도해 주세요.</div>
                          )}
                          {faxCanReschedule && hasPendingFax && (
                            <div className="mt-2 text-xs text-slate-600">
                              이전 건이 실패·취소된 상태입니다. 발송 재시도 시 서버에서 팩스 결과를 먼저 동기화한 뒤 진행합니다.
                            </div>
                          )}
                          {faxCanReschedule && application.faxLastError && (
                            <div className="mt-2 rounded border border-rose-200 bg-rose-50 p-2 text-xs text-rose-800">
                              직전 실패 사유: {application.faxLastError}
                            </div>
                          )}
                          {application && !docsOk && (
                            <div className="mt-2 text-xs text-amber-800">STEP 4에서 청구서류 첨부를 완료한 뒤 보험금 청구를 진행할 수 있습니다.</div>
                          )}
                          {!sigOk && application && docsOk && (
                            <div className="mt-2 text-xs text-amber-800">STEP 5에서 고객 서명을 완료한 뒤 보험금 청구를 진행할 수 있습니다.</div>
                          )}
                          {isDraft && faxReadiness.ready && (
                            <div className="mt-2 text-xs text-slate-500">보험금 청구를 진행하면 접수 상태로 전환됩니다.</div>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {application && (
                            <Button
                              type="button"
                              variant="sgezOutline"
                              size="sm"
                              onClick={() => previewClaimApplicationPdf(application.id)}
                            >
                              발송 서류 미리보기
                            </Button>
                          )}
                          <Button
                            type="button"
                            variant="sgez"
                            size="sm"
                            onClick={() => openFaxDisclaimer(application, insurer)}
                            disabled={!!claimAdditionalFieldRequiredError || !consentOk || !faxReadiness.ready}
                            title={
                              claimAdditionalFieldRequiredError
                                ? 'STEP 3에서 보험사별 필수 입력을 먼저 완료해 주세요.'
                                : !consentOk
                                ? 'STEP 6에서 보험금청구 필수 동의를 먼저 확인해 주세요.'
                                : faxReadiness.message || undefined
                            }
                          >
                            {isScheduled ? '발송 대기 중' : faxCanReschedule ? '발송 재시도' : '보험금 청구하기'}
                          </Button>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </ClaimStepSection>
          )}

          {activeModal === 'STEP2_SUMMARY' && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
              <div className="max-h-[90vh] w-full max-w-2xl overflow-auto rounded border bg-white p-4 shadow-xl">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-lg font-semibold text-slate-950">공통 청구정보 확인</div>
                    <div className="mt-1 text-sm text-slate-500">입력한 내용을 확인한 뒤 다음 단계로 진행하세요.</div>
                  </div>
                  <button type="button" className="rounded border px-3 py-1 text-sm hover:bg-slate-50" onClick={reopenStep2DetailsForEdit}>
                    닫기
                  </button>
                </div>
                <div className="mt-4 overflow-hidden rounded border border-slate-200">
                  {step2SummaryRows.map((row) => (
                    <div key={row.label} className="grid grid-cols-[7.5rem_1fr] border-b border-slate-100 last:border-b-0 text-sm">
                      <div className="bg-slate-50 px-3 py-2 font-medium text-slate-600">{row.label}</div>
                      <div className="min-w-0 whitespace-pre-wrap px-3 py-2 text-slate-900">{row.value}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-4 flex justify-end gap-2">
                  <button
                    type="button"
                    className="rounded border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-800 hover:bg-slate-50"
                    onClick={reopenStep2DetailsForEdit}
                  >
                    수정하기
                  </button>
                  <button
                    type="button"
                    className="rounded bg-brand-navy px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
                    onClick={proceedFromStep2Summary}
                  >
                    진행하기
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeModal === 'GUIDE' && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
              <div className="max-h-[90vh] w-full max-w-6xl overflow-auto rounded border bg-white p-4">
                <div className="flex items-center justify-between">
                  <div className="text-lg font-semibold">청구 가이드</div>
                  <button type="button" className="rounded border px-3 py-1 text-sm hover:bg-slate-50" onClick={() => setActiveModal(null)}>
                    닫기
                  </button>
                </div>
                <div className="mt-4 rounded border bg-white p-4">
                  <div className="font-semibold">가이드 상세</div>
                  {(guidesLoading || guideDetailLoading) && <div className="mt-2 text-sm text-slate-600">불러오는 중...</div>}
                  {guidesError && (
                    <ErrorState
                      description={guidesError}
                      className="mt-2 rounded border border-rose-100 bg-white py-6"
                    />
                  )}
                  {!supportedFlowCategory && (
                    <EmptyState
                      title="지원 준비 중인 청구 유형입니다"
                      description="선택한 청구 유형에 연결된 가이드가 등록되면 이곳에서 확인할 수 있습니다."
                      className="mt-2 rounded border border-dashed py-6"
                    />
                  )}
                  {!guidesLoading && !guidesError && supportedFlowCategory && mergedGuides.length === 0 && (
                    <EmptyState
                      title="등록된 청구 가이드가 없습니다"
                      description="선택한 보험 유형에 연결된 가이드가 등록되면 이곳에서 안내 내용을 확인할 수 있습니다."
                      className="mt-2 rounded border border-dashed py-6"
                    />
                  )}
                  {!guidesLoading && !guideDetailLoading && selectedGuide?.error && (
                    <ErrorState
                      description="가이드 상세를 불러오지 못했습니다. 잠시 후 다시 열어 주세요."
                      className="mt-2 rounded border border-rose-100 bg-white py-6"
                    />
                  )}
                  {!guidesLoading && !guideDetailLoading && selectedGuide && !selectedGuide?.error && (
                    <div className="mt-3 rounded border bg-slate-50 p-3">
                      <div className="text-sm text-slate-600">항목</div>
                      <div className="font-semibold text-slate-900">{selectedGuide.claimSection} / {selectedGuide.claimItem}</div>
                      {selectedGuide.bodyMarkdown ? (
                        <>
                          <div className="mt-3 text-sm text-slate-600">설명</div>
                          <div className="mt-1 whitespace-pre-wrap text-sm text-slate-900">
                            {selectedGuide.bodyMarkdown}
                          </div>
                        </>
                      ) : (
                        <EmptyState
                          title="등록된 설명이 없습니다"
                          description="이 가이드의 상세 설명이 등록되면 이곳에 표시됩니다."
                          className="mt-3 rounded border border-dashed py-6"
                        />
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeModal === 'FORMS' && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
              <div className="max-h-[90vh] w-full max-w-4xl overflow-auto rounded border bg-white p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-lg font-semibold">청구 서식</div>
                  </div>
                  <button type="button" className="rounded border px-3 py-1 text-sm hover:bg-slate-50" onClick={() => setActiveModal(null)}>
                    닫기
                  </button>
                </div>
                <div className="mt-4">
                  <label htmlFor="claim-document-section" className="mb-1.5 block text-xs font-medium text-slate-700">청구 유형</label>
                  <select
                    id="claim-document-section"
                    className="w-full rounded border border-slate-200 bg-white px-3 py-2 text-sm"
                    value={claimDocumentSectionTitle}
                    onChange={(e) => setClaimDocumentSectionTitle(e.target.value)}
                  >
                    {CLAIM_DOCUMENT_SECTIONS_ALL.map((section) => (
                      <option key={section.sectionTitle} value={section.sectionTitle}>{section.sectionTitle}</option>
                    ))}
                  </select>
                </div>
                <div className="mt-4 rounded border border-slate-200 bg-slate-50 p-4">
                  <ClaimRequiredDocumentsPanel section={selectedClaimDocumentSection} />
                </div>
              </div>
            </div>
          )}

          {activeModal === 'SIGN' && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
              <div className="max-h-[90vh] w-full max-w-2xl overflow-auto rounded border bg-white p-4">
                <div className="flex items-center justify-between">
                  <div className="text-lg font-semibold">
                    {sigLinkTarget?.signerRole === 'CONTRACT_HOLDER'
                      ? '계약자 비대면 서명 링크'
                      : sigLinkTarget?.signerRole === 'BENEFICIARY'
                        ? '수익자 비대면 서명 링크'
                        : (commonContractHolderSignatureRequired ? '피보험자 비대면 서명 링크' : '피보험자/계약자 비대면 서명 링크')}
                  </div>
                  <button type="button" className="rounded border px-3 py-1 text-sm hover:bg-slate-50" onClick={closeSignModal}>
                    닫기
                  </button>
                </div>
                <div className="mt-1 text-sm text-slate-600">
                  {sigLinkTarget?.signerRole === 'CONTRACT_HOLDER'
                    ? '계약자 휴대폰으로 서명 링크를 보내면, 본인 확인 후 직접 서명할 수 있습니다. 링크 발송만으로 보험사 청구가 진행되지는 않습니다.'
                    : sigLinkTarget?.signerRole === 'BENEFICIARY'
                      ? '수익자 휴대폰으로 서명 링크를 보내면, 본인 확인 후 직접 서명할 수 있습니다. 링크 발송만으로 보험사 청구가 진행되지는 않습니다.'
                      : '피보험자(고객)에게 서명 링크를 보내면, 고객이 직접 서명한 뒤 다음 단계에서 확인할 수 있습니다. 링크 발송만으로 보험사 청구가 진행되지는 않습니다.'}
                  {sigLinkTarget?.applicationIds?.length > 1 ? (
                    <span className="mt-1 block text-sky-800">
                      완료 확인 시 선택된 {sigLinkTarget.applicationIds.length}건의 청구서에 같은 서명이 공통 반영됩니다.
                    </span>
                  ) : null}
                </div>
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <div className="text-sm text-slate-600">서명자명(선택)</div>
                    <input
                      className="mt-1 w-full rounded border px-3 py-2 text-sm"
                      value={sigCustomerName}
                      onChange={(e) => setSigCustomerName(e.target.value)}
                      placeholder="홍길동"
                    />
                  </div>
                  <div>
                    <div className="text-sm text-slate-600">휴대폰 번호</div>
                    <input
                      className="mt-1 w-full rounded border px-3 py-2 text-sm"
                      value={sigCustomerPhone}
                      onChange={(e) => setSigCustomerPhone(e.target.value)}
                      placeholder="01012345678"
                    />
                  </div>
                </div>
                <div className="mt-3">
                  <div className="text-sm text-slate-600">발송 채널</div>
                  <div className="mt-2 inline-flex rounded border overflow-hidden text-sm">
                    <button type="button" className={`px-3 py-1.5 ${sigChannel === 'KAKAO' ? 'bg-sky-50 text-brand-navy ring-1 ring-inset ring-brand-navy/20' : 'bg-white'}`} onClick={() => setSigChannel('KAKAO')}>
                      카카오
                    </button>
                    <button type="button" className={`px-3 py-1.5 ${sigChannel === 'SMS' ? 'bg-sky-50 text-brand-navy ring-1 ring-inset ring-brand-navy/20' : 'bg-white'}`} onClick={() => setSigChannel('SMS')}>
                      문자(SMS)
                    </button>
                  </div>
                </div>
                {sigError && (
                  <div className="mt-3 rounded border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
                    {sigError}
                  </div>
                )}
                <div className="mt-4 flex items-center justify-end gap-2">
                  <button
                    type="button"
                    className="rounded bg-brand-navy px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
                    onClick={sendSignatureLink}
                    disabled={sigSending || !sigCustomerPhone}
                  >
                    {sigSending ? '발송 중...' : '링크 발송'}
                  </button>
                </div>
                {sigResult?.url && (
                  <div className="mt-4 rounded border bg-slate-50 p-3">
                    <div className="text-sm text-slate-600">발송 결과</div>
                    <div className="mt-1 text-sm text-slate-900">
                      채널: <span className="font-semibold">{sigResult.channel || '-'}</span>
                      {sigResult.strategy && <span className="text-slate-500"> ({sigResult.strategy})</span>}
                    </div>
                    <div className="mt-2 text-sm text-slate-600">링크</div>
                    <div className="mt-1 flex items-center gap-2">
                      <input className="flex-1 rounded border bg-white px-3 py-2 text-sm" readOnly value={sigResult.url} />
                      <button type="button" className="rounded border px-3 py-2 text-sm hover:bg-white" onClick={async () => { try { await navigator.clipboard.writeText(sigResult.url) } catch { } }}>
                        복사
                      </button>
                      <button type="button" className="rounded border px-3 py-2 text-sm hover:bg-white" onClick={() => window.open(sigResult.url, '_blank', 'noopener,noreferrer')}>
                        열기
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeModal === 'INTAKE' && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
              <div className="max-h-[90vh] w-full max-w-3xl overflow-auto rounded border bg-white p-4">
                <div className="flex items-center justify-between">
                  <div className="text-lg font-semibold">고객 청구링크 발송</div>
                  <button type="button" className="rounded border px-3 py-1 text-sm hover:bg-slate-50" onClick={() => setActiveModal(null)}>
                    닫기
                  </button>
                </div>
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="md:col-span-2">
                    <div className="text-sm text-slate-600">보험사</div>
                    <div className="mt-1 grid max-h-48 grid-cols-1 gap-2 overflow-auto rounded border bg-slate-50 p-2 md:grid-cols-2">
                      {selectedInsurerOptionsForActions.map(o => {
                        const key = String(o.id)
                        const checked = intakeInsurerOrgIds.map(String).includes(key)
                        return (
                          <label key={o.id} className="flex cursor-pointer items-center gap-2 rounded border bg-white px-3 py-2 text-sm hover:bg-slate-50">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={(e) => {
                                setIntakeInsurerOrgIds((prev) => {
                                  const prevKeys = prev.map(String)
                                  if (e.target.checked) {
                                    return prevKeys.includes(key) ? prev : [...prevKeys, key]
                                  }
                                  return prevKeys.filter((id) => id !== key)
                                })
                              }}
                            />
                            <span className="min-w-0 flex-1 truncate">{o.name || '보험사'}</span>
                            <span className="shrink-0 text-xs text-slate-500">{getFlowCategoryLabelForInsurer(o)}</span>
                          </label>
                        )
                      })}
                      {selectedInsurerOptionsForActions.length === 0 && (
                        <div className="rounded bg-white px-3 py-2 text-sm text-slate-500">선택 가능한 보험사가 없습니다.</div>
                      )}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-slate-600">고객명(선택)</div>
                    <input
                      className="mt-1 w-full rounded border px-3 py-2 text-sm"
                      value={intakeCustomerName}
                      onChange={(e) => setIntakeCustomerName(e.target.value)}
                      placeholder="홍길동"
                    />
                  </div>
                  <div>
                    <div className="text-sm text-slate-600">휴대폰 번호</div>
                    <input
                      className="mt-1 w-full rounded border px-3 py-2 text-sm"
                      value={intakeCustomerPhone}
                      onChange={(e) => setIntakeCustomerPhone(e.target.value)}
                      placeholder="01012345678"
                    />
                  </div>
                </div>
                <div className="mt-3">
                  <div className="text-sm text-slate-600">발송 채널</div>
                  <div className="mt-2 inline-flex rounded border overflow-hidden text-sm">
                    <button type="button" className={`px-3 py-1.5 ${intakeChannel === 'KAKAO' ? 'bg-sky-50 text-brand-navy ring-1 ring-inset ring-brand-navy/20' : 'bg-white'}`} onClick={() => setIntakeChannel('KAKAO')}>
                      카카오
                    </button>
                    <button type="button" className={`px-3 py-1.5 ${intakeChannel === 'SMS' ? 'bg-sky-50 text-brand-navy ring-1 ring-inset ring-brand-navy/20' : 'bg-white'}`} onClick={() => setIntakeChannel('SMS')}>
                      문자(SMS)
                    </button>
                  </div>
                </div>
                {intakeError && (
                  <div className="mt-3 rounded border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
                    {intakeError}
                  </div>
                )}
                <div className="mt-4 flex items-center justify-end">
                  <button
                    type="button"
                    className="rounded bg-brand-navy px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
                    onClick={sendIntakeLink}
                    disabled={intakeSending || !intakeCustomerPhone || intakeSelectedInsurerOptions.length === 0}
                  >
                    {intakeSending ? '발송 중...' : '링크 발송'}
                  </button>
                </div>
                {intakeResult?.results?.length > 0 ? (
                  <div className="mt-4 rounded border bg-slate-50 p-3">
                    <div className="text-sm text-slate-600">발송 결과</div>
                    <div className="mt-1 text-sm text-slate-900">발송: <span className="font-semibold">{intakeResult.results.length}건</span></div>
                    <div className="mt-3 space-y-2">
                      {intakeResult.results.map((row) => (
                        <div key={row.sessionId} className="rounded border bg-white p-2">
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <div className="text-sm font-semibold text-slate-900">{row.insurerName || '보험사'}</div>
                            <div className="text-xs text-slate-500">{row.channel || '-'}</div>
                          </div>
                          {row.url ? (
                            <div className="mt-2 flex items-center gap-2">
                              <input className="min-w-0 flex-1 rounded border bg-white px-3 py-2 text-sm" readOnly value={row.url} />
                              <button type="button" className="rounded border px-3 py-2 text-sm hover:bg-slate-50" onClick={async () => { try { await navigator.clipboard.writeText(row.url) } catch { } }}>
                                복사
                              </button>
                              <button type="button" className="rounded border px-3 py-2 text-sm hover:bg-slate-50" onClick={() => window.open(row.url, '_blank', 'noopener,noreferrer')}>
                                열기
                              </button>
                            </div>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          )}

          {inPersonSigModal.open && inPersonSigModal.application && inPersonSigModal.insurer && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
              <div className="w-full max-w-lg rounded border bg-white p-5 shadow-xl">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-lg font-semibold text-slate-900">
                      {inPersonSigModal.signerRole === 'CONTRACT_HOLDER'
                        ? '대면 계약자 서명'
                        : inPersonSigModal.signerRole === 'BENEFICIARY'
                          ? '대면 수익자 서명'
                          : (commonContractHolderSignatureRequired ? '대면 피보험자 서명' : '대면 피보험자/계약자 서명')}
                    </div>
                    <div className="mt-1 text-sm text-slate-600">
                      {inPersonSigModal.applications?.length > 1
                        ? `선택된 ${inPersonSigModal.applications.length}건의 청구서에 공통 반영됩니다.`
                        : `${inPersonSigModal.insurer.name} 청구서에 반영됩니다.`}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="shrink-0 rounded border px-3 py-1 text-sm hover:bg-slate-50"
                    onClick={() => {
                      setInPersonSigModal({ open: false, application: null, insurer: null, applications: [], insurers: [], signerRole: 'INSURED' })
                      clearInPersonCanvas()
                    }}
                  >
                    닫기
                  </button>
                </div>
                <div className="mt-4 rounded border bg-slate-50 p-2">
                  <canvas
                    ref={inPersonCanvasRef}
                    width={560}
                    height={200}
                    className="w-full touch-none rounded border bg-white"
                    style={{
                      cursor: 'url("data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%2724%27 height=%2724%27 viewBox=%270 0 24 24%27 fill=%27none%27 stroke=%27%23111827%27 stroke-width=%272%27 stroke-linecap=%27round%27 stroke-linejoin=%27round%27%3E%3Cpath d=%27M12 20h9%27/%3E%3Cpath d=%27M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z%27/%3E%3C/svg%3E") 2 22, pointer',
                    }}
                    onMouseDown={(e) => {
                      const p = getInPersonCanvasPoint(e)
                      if (p) startInPersonDraw(p.x, p.y)
                    }}
                    onMouseMove={(e) => {
                      const p = getInPersonCanvasPoint(e)
                      if (p) moveInPersonDraw(p.x, p.y)
                    }}
                    onMouseUp={endInPersonDraw}
                    onMouseLeave={endInPersonDraw}
                    onTouchStart={(e) => {
                      e.preventDefault()
                      const p = getInPersonCanvasPoint(e)
                      if (p) startInPersonDraw(p.x, p.y)
                    }}
                    onTouchMove={(e) => {
                      e.preventDefault()
                      const p = getInPersonCanvasPoint(e)
                      if (p) moveInPersonDraw(p.x, p.y)
                    }}
                    onTouchEnd={(e) => {
                      e.preventDefault()
                      endInPersonDraw()
                    }}
                  />
                </div>
                <div className="mt-3 flex flex-wrap items-center justify-end gap-2">
                  <button
                    type="button"
                    className="rounded border px-4 py-2 text-sm hover:bg-slate-50"
                    onClick={clearInPersonCanvas}
                  >
                    지우기
                  </button>
                  <button
                    type="button"
                    className="rounded bg-brand-navy px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
                    onClick={submitInPersonSignature}
                    disabled={inPersonSigSubmitting}
                  >
                    {inPersonSigSubmitting ? '저장 중...' : '서명 저장'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {sharedDocumentPreviewIndex != null && sharedDocuments[sharedDocumentPreviewIndex] && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
              <div className="flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded border bg-white shadow-xl">
                <div className="flex items-center justify-between gap-3 border-b px-4 py-3">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-semibold text-slate-900">
                      {sharedDocuments[sharedDocumentPreviewIndex].label || sharedDocuments[sharedDocumentPreviewIndex].fileId || '첨부파일 미리보기'}
                    </div>
                    <div className="mt-0.5 text-xs text-slate-500">
                      {sharedDocumentPreviewIndex + 1} / {sharedDocuments.length}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="shrink-0 rounded border px-3 py-1.5 text-sm hover:bg-slate-50"
                    onClick={() => setSharedDocumentPreviewIndex(null)}
                  >
                    닫기
                  </button>
                </div>
                <div className="grid min-h-0 flex-1 grid-cols-[auto_1fr_auto] items-center gap-2 bg-slate-950 p-3">
                  <button
                    type="button"
                    className="rounded bg-white/90 px-3 py-2 text-sm font-semibold text-slate-900 hover:bg-white disabled:opacity-40"
                    onClick={() => moveSharedDocumentPreview(-1)}
                    disabled={sharedDocuments.length <= 1}
                  >
                    이전
                  </button>
                  <div className="flex min-h-[60vh] items-center justify-center overflow-auto rounded bg-slate-900">
                    {(() => {
                      const doc = sharedDocuments[sharedDocumentPreviewIndex]
                      const preview = doc.fileId ? sharedDocumentPreviewUrls[String(doc.fileId)] : null
                      const kind = preview?.kind || filePreviewKind(doc)
                      const url = preview?.url || ''
                      if (kind === 'image' && url) {
                        return <img src={url} alt={doc.label || doc.fileId} className="max-h-[72vh] max-w-full object-contain" />
                      }
                      if (kind === 'pdf' && url) {
                        return <iframe src={url} title={doc.label || doc.fileId || '첨부 PDF'} className="h-[72vh] w-full min-w-[320px] rounded bg-white" />
                      }
                      return (
                        <div className="rounded bg-white px-6 py-5 text-center text-sm text-slate-700">
                          이 파일 형식은 브라우저 미리보기가 제한될 수 있습니다.
                          <button
                            type="button"
                            className="mt-3 block w-full rounded border px-3 py-2 text-sm font-medium hover:bg-slate-50"
                            onClick={() => downloadFile(doc.fileId)}
                          >
                            다운로드
                          </button>
                        </div>
                      )
                    })()}
                  </div>
                  <button
                    type="button"
                    className="rounded bg-white/90 px-3 py-2 text-sm font-semibold text-slate-900 hover:bg-white disabled:opacity-40"
                    onClick={() => moveSharedDocumentPreview(1)}
                    disabled={sharedDocuments.length <= 1}
                  >
                    다음
                  </button>
                </div>
              </div>
            </div>
          )}

          {faxScheduleModal.open && faxScheduleModal.application && faxScheduleModal.insurer && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
              <div className="w-full max-w-md rounded border bg-white p-5 shadow-xl">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-lg font-semibold">보험사로 팩스 발송</div>
                    <div className="mt-1 text-sm text-slate-600">{faxScheduleModal.insurer.name} 청구 서류를 보험사 팩스로 보냅니다.</div>
                  </div>
                  <button
                    type="button"
                    className="rounded border px-3 py-1 text-sm hover:bg-slate-50"
                    onClick={() => {
                      bulkClaimRemainderRef.current = null
                      setFaxScheduleModal({ open: false, application: null, insurer: null })
                      setFaxScheduleForm({ receiveNum: '', receiveName: '', title: '' })
                    }}
                  >
                    닫기
                  </button>
                </div>
                <div className="mt-4 space-y-3">
                  {faxScheduleModalCfg?.virtualFaxRequired && (
                    <div className="rounded border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                      이 보험사는 <span className="font-semibold">가상팩스번호</span>가 필요합니다. 발급받은 번호를 수신번호로 입력해 주세요.
                      {faxScheduleListedFaxDisplay
                        ? (
                          <span className="block mt-1 text-amber-800">
                            안내용 번호(이 청구 사고유형 기준): {faxScheduleListedFaxDisplay} (이 번호와 동일하면 발송할 수 없습니다)
                          </span>
                          )
                        : null}
                    </div>
                  )}
                  <div>
                    <div className="text-sm font-medium text-slate-700 mb-1">수신번호 *</div>
                    <input
                      className="w-full rounded border px-3 py-2 text-sm"
                      value={faxScheduleForm.receiveNum}
                      onChange={(e) => setFaxScheduleForm((prev) => ({ ...prev, receiveNum: e.target.value }))}
                      placeholder="예: 02-1234-5678"
                    />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-slate-700 mb-1">수신자명 *</div>
                    <input
                      className="w-full rounded border px-3 py-2 text-sm"
                      value={faxScheduleForm.receiveName}
                      onChange={(e) => setFaxScheduleForm((prev) => ({ ...prev, receiveName: e.target.value }))}
                      placeholder="예: 삼성화재"
                    />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-slate-700 mb-1">제목</div>
                    <input
                      className="w-full rounded border px-3 py-2 text-sm"
                      value={faxScheduleForm.title}
                      onChange={(e) => setFaxScheduleForm((prev) => ({ ...prev, title: e.target.value }))}
                      placeholder="보험금청구서"
                    />
                  </div>
                  <div className="rounded border bg-slate-50 p-3 text-xs text-slate-600">
                    청구서 PDF와 공통·개별 첨부가 함께 발송됩니다. 약 1분 후 전송이 시작되며, 시작 전까지 발송 취소할 수 있습니다.
                  </div>
                  <div className="flex items-center justify-end gap-2">
                    <button
                      type="button"
                      className="rounded border px-4 py-2 text-sm hover:bg-slate-50"
                      onClick={() => {
                        bulkClaimRemainderRef.current = null
                        setFaxScheduleModal({ open: false, application: null, insurer: null })
                        setFaxScheduleForm({ receiveNum: '', receiveName: '', title: '' })
                      }}
                    >
                      취소
                    </button>
                    <button
                      type="button"
                      className="rounded bg-brand-navy px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
                      onClick={handleScheduleClaimFax}
                      disabled={faxScheduling || !faxScheduleForm.receiveNum || !faxScheduleForm.receiveName}
                    >
                      {faxScheduling
                        ? '처리 중...'
                        : (faxScheduleModal.application?.faxReservationStatus === 'FAILED'
                          || faxScheduleModal.application?.faxReservationStatus === 'CANCELLED'
                          ? '발송 재시도'
                          : '발송하기')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
